/**
 * Throws an exception with given message. If you need to handle all errors in one place, redefined this method to your needed handler.
 * @param error
 */
const error = function (error) {
  if (isString(error)) {
    throw new Error(error);
  } else {
    throw error;
  }
};
const warn = function (error) {
  // eslint-disable-next-line no-console
  console.warn(error);
};
/**
 * Calls the 'func' callback in 'interval' ms after a series of subsequent function calls.
 * @param func - callback function
 * @param interval - interval in ms
 */

const debounce = function (func, interval) {
  let timer = null;
  return function () {
    if (timer) clearTimeout(timer);
    let args = arguments;
    timer = window.setTimeout(function () {
      timer = null;
      func.apply(this, args);
    }, interval);
  };
};
/**
 * Throttle function calls
 * @param func - callback function
 * @param interval - interval
 */

const throttle = function (func, interval) {
  let isThrottled = false;
  let savedArgs;
  let savedThis;

  function wrapper() {
    if (isThrottled) {
      savedArgs = arguments;
      savedThis = this;
      return;
    }

    func.apply(this, arguments);
    isThrottled = true;
    setTimeout(function () {
      isThrottled = false;

      if (savedArgs) {
        wrapper.apply(savedThis, savedArgs);
        savedArgs = savedThis = null;
      }
    }, interval);
  }

  return wrapper;
};
/**
 * Returns true if n is a finite number, otherwise false
 * @param n
 */

const isNumber = function (n) {
  return typeof n === 'number' && isFinite(n);
};
/**
 * Returns true if n is an integer number, otherwise false
 * @param n
 */

const isInteger = function (n) {
  return isNumber(n) && Math.round(n) === n;
};
/**
 * Returns true if s is a string, otherwise false
 * @param s
 */

const isString = function (s) {
  return typeof s === 'string';
};
/**
 * Returns a random GUID
 */

const getGuid = function () {
  //noinspection SpellCheckingInspection
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    let r = Math.random() * 16 | 0;
    let v = c == 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};
/**
 * Returns true if at least one element of arr1 also exists in arr2
 * @param arr1
 * @param arr2
 */

const arrayIntersect = function (arr1, arr2) {
  for (let i = 0; i < arr1.length; i++) {
    if (arr2.indexOf(arr1[i]) !== -1) {
      return true;
    }
  }

  return false;
};
/**
 * Makes a deep copy af the array
 * @param arr
 */

const copyArray = function (arr) {
  let copy = [];

  for (let i = 0, l = arr.length; i < l; i++) {
    if (Array.isArray(arr[i])) {
      copy[i] = copyArray(arr[i]);
    } else {
      copy[i] = arr[i];
    }
  }

  return copy;
};
/**
 * Makes a deep copy of an object
 * @param obj
 * TODO: this will not copy the inner arrays properly
 */

const copyObject = function (obj) {
  if (!(obj instanceof Function) && obj instanceof Object) {
    let copy = Array.isArray(obj) ? [] : {};
    let keys = Object.keys(obj);

    for (let i = 0; i < keys.length; i++) {
      copy[keys[i]] = copyObject(obj[keys[i]]);
    }

    return copy;
  } else {
    return obj;
  }
};
/**
 * Creates a new <style> node and adds it to the document head section.
 * @param desc
 */

const setCssClasses = function (desc) {
  let classes = Object.keys(desc).map(key => {
    return getCssText(key, desc[key]);
  });
  setStyleNode(classes.join('\n'));
};

const getCssText = function (className, styles) {
  return '.' + className + '{' + styles + '}';
};
/**
 * Creates a new <style> node with the given code and adds it to the document head.
 * @param text
 */


const setStyleNode = function (text) {
  let node = document.createElement('style');
  node.type = 'text/css';

  if (node.styleSheet) {
    node.styleSheet.cssText = text;
  } else {
    node.appendChild(document.createTextNode(text));
  }

  document.head.appendChild(node);
};
/**
 * Contains the name of the current browser.
 */

const browser = /*#__PURE__*/function () {
  let ua = navigator.userAgent,
      tem,
      M = /*#__PURE__*/ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];

  if (/trident/i.test(M[1])) {
    tem = /*#__PURE__*/ /\brv[ :]+(\d+)/g.exec(ua) || [];
    return 'IE ' + (tem[1] || '');
  }

  if (M[1] === 'Chrome') {
    tem = /*#__PURE__*/ua.match(/\bOPR\/(\d+)/);
    if (tem != null) return 'Opera ' + tem[1];
  }

  M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
  if ((tem = /*#__PURE__*/ua.match(/version\/(\d+)/i)) != null) M.splice(1, 1, tem[1]);
  return M.join(' ');
}();
/**
 * Contains 'true' if the current browser is Internet Explorer of any version.
 */

const isIE = /*#__PURE__*/browser.search('IE') !== -1;
/**
 * Contains 'true' if the current browser is Chrome of any version.
 */

const isChrome = /*#__PURE__*/browser.search('Chrome') !== -1;
/**
 * Contains 'true' if the current browser is Opera of any version.
 */

const isOpera = /*#__PURE__*/browser.search('Opera') !== -1;
/**
 * Contains 'true' if the current browser is run on a touch device.
 */

const isTouch = ('ontouchstart' in document.documentElement);
/* Simple polyfil for tests */

if (typeof window.requestAnimationFrame === 'undefined') {
  window.requestAnimationFrame = callback => {
    setTimeout(callback, 1000 / 30);
    return 0;
  };
}
/**
 * async loadImage function
 * @param image
 * @param src
 * @param signal
 */


function loadImage(image, src, signal) {
  if (signal && !src.includes(";base64")) {
    return fetch(src, {
      signal
    }).then(res => res.blob()).then(data => {
      const url = URL.createObjectURL(data);
      image.src = url;
    });
  }

  image.src = src;
  return new Promise((res, rej) => {
    if (image.complete) {
      return res();
    }

    image.onload = () => res();

    image.onerror = error => rej(error);
  });
}
const getParameterValue = value => {
  if (value === null || value === void 0 ? void 0 : value.defaultValue) {
    return value.defaultValue;
  }

  return value;
};

var utilsModule = /*#__PURE__*/Object.freeze({
  __proto__: null,
  error: error,
  warn: warn,
  debounce: debounce,
  throttle: throttle,
  isNumber: isNumber,
  isInteger: isInteger,
  isString: isString,
  getGuid: getGuid,
  arrayIntersect: arrayIntersect,
  copyArray: copyArray,
  copyObject: copyObject,
  setCssClasses: setCssClasses,
  setStyleNode: setStyleNode,
  browser: browser,
  isIE: isIE,
  isChrome: isChrome,
  isOpera: isOpera,
  isTouch: isTouch,
  loadImage: loadImage,
  getParameterValue: getParameterValue
});

var MouseEventFlags;

(function (MouseEventFlags) {
  MouseEventFlags[MouseEventFlags["None"] = 0] = "None";
  MouseEventFlags[MouseEventFlags["MouseDown"] = 2] = "MouseDown";
  MouseEventFlags[MouseEventFlags["MouseUp"] = 4] = "MouseUp";
  MouseEventFlags[MouseEventFlags["MouseClick"] = 8] = "MouseClick";
  MouseEventFlags[MouseEventFlags["MouseMove"] = 16] = "MouseMove";
  MouseEventFlags[MouseEventFlags["MouseOver"] = 32] = "MouseOver";
  MouseEventFlags[MouseEventFlags["MouseOut"] = 64] = "MouseOut";
  MouseEventFlags[MouseEventFlags["DoubleClick"] = 128] = "DoubleClick";
  MouseEventFlags[MouseEventFlags["DragStart"] = 256] = "DragStart";
  MouseEventFlags[MouseEventFlags["Drag"] = 512] = "Drag";
  MouseEventFlags[MouseEventFlags["DragEnd"] = 1024] = "DragEnd";
})(MouseEventFlags || (MouseEventFlags = {}));

const mouseEvents = {
  click: {
    type: 'click',
    flag: MouseEventFlags.MouseClick
  },
  doubleClick: {
    type: 'dblclick',
    flag: MouseEventFlags.DoubleClick
  },
  mouseDown: {
    type: 'mousedown',
    flag: MouseEventFlags.MouseDown
  },
  mouseUp: {
    type: 'mouseup',
    flag: MouseEventFlags.MouseUp
  },
  mouseOver: {
    type: 'mouseover',
    flag: MouseEventFlags.MouseOver
  },
  mouseOut: {
    type: 'mouseout',
    flag: MouseEventFlags.MouseOut
  },
  mouseMove: {
    type: 'mousemove',
    flag: MouseEventFlags.MouseMove
  },
  dragStart: {
    type: 'dragStart',
    flag: MouseEventFlags.DragStart
  },
  drag: {
    type: 'drag',
    flag: MouseEventFlags.Drag
  },
  dragEnd: {
    type: 'dragEnd',
    flag: MouseEventFlags.DragEnd
  }
};
const eventTypeFlags = {};
Object.keys(mouseEvents).forEach(eventName => {
  eventTypeFlags[mouseEvents[eventName].type] = mouseEvents[eventName].flag;
});
/**
 * Base of all sGis library events
 */

class sGisEvent {
  /**
   * @param type - name of the event
   */
  constructor(type) {
    this._cancelPropagation = false;
    this.type = type;
  }
  /**
   * Prevents any further event handlers to be called for this event.
   */


  stopPropagation() {
    this._cancelPropagation = true;
  }
  /**
   * Whether the .stopPropagation() method has been called for this event.
   */


  get isCanceled() {
    return this._cancelPropagation;
  }

}
/**
 * Provides methods for handling events.
 * @alias sGis.EventHandler
 */

class EventHandler {
  constructor() {
    // This initialization makes the properties not enumerable and guaranties a valid value is there at all times
    Object.defineProperty(this, '_eventHandlers', {
      value: {}
    });
    Object.defineProperty(this, '_prohibitedEvents', {
      value: []
    });
    Object.defineProperty(this, 'eventFlags', {
      value: MouseEventFlags.None,
      writable: true
    });
  }
  /**
   * Triggers event with the given parameters. It is supposed to be used to transfer event from one object to another (for example, from layer to a feature).
   * @param event - event object of the original event
   */


  forwardEvent(event) {
    if (this._prohibitedEvents && this._prohibitedEvents.indexOf(event.type) !== -1) return;
    let eventType = event.type;

    if (this._eventHandlers && this._eventHandlers[eventType]) {
      let handlerList = copyArray(this._eventHandlers[eventType]); //This is needed in case one of the handlers is deleted in the process of handling

      for (let i = 0; i < handlerList.length; i++) {
        if (handlerList[i].oneTime) {
          let currentIndex = this._eventHandlers[eventType].indexOf(handlerList[i]);

          this._eventHandlers[eventType].splice(currentIndex, 1);
        }

        handlerList[i].handler.call(this, event);
        if (event.isCanceled) break;
      }
    }
  }
  /**
   * Triggers the event of the given type. Each handler will be triggered one by one in the order they were added. Returns
   * the event object, or null in case the event was not triggered (if it is prohibited).
   * @param event - event object or exact name of the event to be triggered.
   * @param parameters - [JS ONLY] parameters to be transferred to the event object. Applied only if the first argument is string.
   */


  fire(event, parameters) {
    if (typeof event === 'string') {
      event = new sGisEvent(event);
      if (parameters) Object.assign(event, parameters);
      event.sourceObject = this;
    }

    if (this._prohibitedEvents.indexOf(event.type) !== -1) return null;
    this.forwardEvent(event);
    return event;
  }
  /**
   * Sets a listener for the given event type.
   * @param description - description of the event. Can contain any number of type names and namespaces (namespaces start with .), but must have at least one of either..
   * @param handler - handler to be executed. The handler is called in the event source object context.
   * @param oneTime - if set to true, the event will be triggered only once.
   */


  addListener(description, handler, oneTime = false) {
    let types = getTypes(description);
    let namespaces = getNamespaces(description);
    if (types.length === 0) error('No event types are specified.');
    if (!handler) error('No handler is given.');

    for (let i = 0; i < types.length; i++) {
      if (!this._eventHandlers[types[i]]) this._eventHandlers[types[i]] = [];

      this._eventHandlers[types[i]].push({
        handler: handler,
        namespaces: namespaces,
        oneTime
      });

      if (eventTypeFlags[types[i]]) this.eventFlags = this.eventFlags | eventTypeFlags[types[i]];
    }
  }
  /**
   * Sets a one time handler for the given event. This handler is removed from the list of handlers just before it is called.
   * @param description - description of the event. Can contain <u>ONLY ONE EVENT TYPE</u> and any number of namespaces (namespaces start with .).
   * @param handler - handler to be executed. The handler is called in the event source object context.
   */


  once(description, handler) {
    this.addListener(description, handler, true);
  }
  /**
   * Removes the given handlers from the event listener list.
   * @param description - description of the event. Can contain any number of type names and namespaces, but must have at least one of either.
   * @param handler - handler to be removed. If no handler is specified, all handlers from the given namespaces will be removed. If no handler and namespace are specified, error will be thrown.
   */


  removeListener(description, handler) {
    let types = getTypes(description);
    let namespaces = getNamespaces(description);
    if (types.length === 0) types = Object.keys(this._eventHandlers);

    for (let i = 0; i < types.length; i++) {
      if (this._eventHandlers[types[i]]) {
        for (let j = this._eventHandlers[types[i]].length - 1; j >= 0; j--) {
          if ((namespaces === null || namespaces.length === 0 || arrayIntersect(this._eventHandlers[types[i]][j].namespaces, namespaces)) && (!handler || this._eventHandlers[types[i]][j].handler === handler)) {
            this._eventHandlers[types[i]].splice(j, 1);
          }
        }

        if (this._eventHandlers[types[i]].length === 0 && eventTypeFlags[types[i]]) {
          this.eventFlags = this.eventFlags & ~eventTypeFlags[types[i]];
        }
      }
    }
  }
  /**
   * Prohibits triggering of the event. The prohibitions are stacked - if the same event is prohibited N times, you need to allow it N times to make it work.
   * @param type - name of the event to be prohibited.
   */


  prohibitEvent(type) {
    this._prohibitedEvents.push(type);
  }
  /**
   * Allows a previously prohibited event. The prohibitions are stacked - if the same event is prohibited N times, you need to allow it N times to make it work. If no prohibitions were set for the event, the operation is ignored.
   * @param type - name of the event to be allowed.
   */


  allowEvent(type) {
    let index = this._prohibitedEvents.indexOf(type);

    if (index !== -1) this._prohibitedEvents.splice(index, 1);
  }
  /**
   * Checks if the object has the handler for the given event type.
   * @param type - name of the event.
   * @param handler - handler to be checked
   */


  hasListener(type, handler) {
    if (this._eventHandlers[type]) {
      for (let i = 0; i < this._eventHandlers[type].length; i++) {
        if (this._eventHandlers[type][i].handler === handler) return true;
      }
    }

    return false;
  }
  /**
   * Checks if the object has any handlers corresponding to the following description.
   * @param description - description of the event. Can contain any number of type names and namespaces (namespaces start with .), but must have at least one of either.
   * @returns true if the object has at least one handler of the given types with the given namespaces. If no event type is given, checks if there are any handlers in the given namespaces exist. If no namespace is given, the namespace check is ignored.
   */


  hasListeners(description) {
    let types = getTypes(description);
    let namespaces = getNamespaces(description);
    if (types.length === 0) types = Object.keys(this._eventHandlers);

    for (let i = 0; i < types.length; i++) {
      if (this._eventHandlers[types[i]] && this._eventHandlers[types[i]].length > 0) {
        if (namespaces.length > 0) {
          for (let j = 0; j < this._eventHandlers[types[i]].length; j++) {
            if (arrayIntersect(this._eventHandlers[types[i]][j].namespaces, namespaces)) {
              return true;
            }
          }
        } else {
          return true;
        }
      }
    }

    return false;
  }
  /**
   * Removes all event listeners from the object.
   */


  removeAllListeners() {
    Object.keys(this._eventHandlers).forEach(key => delete this._eventHandlers[key]);
  }
  /**
   * @see EventHandler#addListener
   */


  on(description, handler, oneTime = false) {
    this.addListener.apply(this, arguments);
  }
  /**
   * @see EventHandler#removeListener
   */


  off(description, handler) {
    this.removeListener.apply(this, arguments);
  }

}

function getTypes(string) {
  return string.replace(/\.[A-Za-z0-9_-]+/g, '').match(/[A-Za-z0-9_-]+/g) || [];
}

function getNamespaces(string) {
  return string.match(/\.[A-Za-z0-9_-]+/g) || [];
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

/**
 * Some property of the layer has been changed
 * @event PropertyChangeEvent
 */

class PropertyChangeEvent extends sGisEvent {
  /**
   * @param propertyName - ame of the property that has been changed
   */
  constructor(propertyName) {
    super(PropertyChangeEvent.type);
    this.property = propertyName;
  }

}
PropertyChangeEvent.type = 'propertyChange';
/**
 * Visibility property of the layer has been changed.
 * @event VisibilityChangeEvent
 */

class VisibilityChangeEvent extends sGisEvent {
  constructor() {
    super(VisibilityChangeEvent.type);
  }

}
VisibilityChangeEvent.type = 'visibilityChange';
/**
 * Layer unable to be loaded.
 * @event LayerErrorEvent
 */

class LayerErrorEvent extends sGisEvent {
  constructor(error) {
    super(LayerErrorEvent.type);
    this.error = error;
  }

}
LayerErrorEvent.type = 'layerError';
/**
 * Base class for all map layers. A layer is a container for features, that is responsible for filter out (or create)
 * features for requested bbox and resolution.
 * @alias sGis.Layer
 */

class Layer extends EventHandler {
  /**
   * @param __namedParameters - properties to be set to the corresponding fields
   * @param extensions - [JS ONLY]additional properties to be copied to the created instance
   */
  constructor({
    delayedUpdate = false,
    resolutionLimits = [-1, -1],
    opacity = 1,
    isDisplayed = true
  } = {}, extensions) {
    super();
    this.delayedUpdate = delayedUpdate;
    this._resolutionLimits = resolutionLimits;
    this._opacity = opacity;
    this._isDisplayed = isDisplayed;
    if (extensions) Object.assign(this, extensions);
  }
  /**
   * Whether the layer is drawn to map
   * @fires [[VisibilityChangeEvent]]
   */


  get isDisplayed() {
    return this._isDisplayed;
  }

  set isDisplayed(bool) {
    this._isDisplayed = bool;
    this.fire(new VisibilityChangeEvent());
  }
  /**
   * Return true if the layer is displayed and the resolution is inside the limits
   * @param resolution
   */


  checkVisibility(resolution) {
    return this._isDisplayed && (this.resolutionLimits[0] < 0 || resolution >= this.resolutionLimits[0]) && (this.resolutionLimits[1] < 0 || resolution <= this.resolutionLimits[1]);
  }
  /**
   * Makes the layer visible
   * @fires [[PropertyChangeEvent]]
   */


  show() {
    this.isDisplayed = true;
  }
  /**
   * Makes the layer invisible
   * @fires [[PropertyChangeEvent]]
   */


  hide() {
    this.isDisplayed = false;
  }
  /**
   * Opacity of the layer. It sets the opacity of all features in this layer. Valid values: [0..1].
   * @fires [[PropertyChangeEvent]]
   */


  get opacity() {
    return this.getOpacity();
  }

  set opacity(opacity) {
    this.setOpacity(opacity);
  }

  getOpacity() {
    return this._opacity;
  }

  setOpacity(opacity) {
    opacity = opacity < 0 ? 0 : opacity > 1 ? 1 : opacity;
    this._opacity = opacity;
    this.fire(new PropertyChangeEvent('opacity'));
  }
  /**
   * Min and max resolution between which the layer will be displayed. Negative values are treated as no limit.
   * @fires [[PropertyChangeEvent]]
   */


  get resolutionLimits() {
    return this._resolutionLimits;
  }

  set resolutionLimits(limits) {
    this._resolutionLimits = limits;
    this.fire(new PropertyChangeEvent('resolutionLimits'));
  }
  /**
   * Forces redrawing of the layer
   * @fires [[PropertyChangeEvent]]
   */


  redraw() {
    this.fire(new PropertyChangeEvent('content'));
  }

}

class Render {
  constructor() {
    this._listensFor = MouseEventFlags.None;
    this._eventHandler = null;
    this.contourIndex = -1;
    this.pointIndex = -1;
    this.updateNeeded = true;
  }

  get listensFor() {
    return this._listensFor;
  }

  triggerEvent(event) {
    if (this._eventHandler) this._eventHandler(event);
  }

  listenFor(eventFlags, handler) {
    this._eventHandler = handler;
    this._listensFor = eventFlags;
  }

}
class StaticRender extends Render {}
class VectorRender extends StaticRender {
  drawOnCanvas(ctx, map) {
    ctx.save();
    this.setupRender(ctx, map);
    this.renderObject(ctx, map);
    ctx.restore();
  }

}
class DynamicRender extends Render {
  constructor({
    node,
    update,
    redraw,
    onRender
  }) {
    super();
    this.node = node;
    this.update = update;
    this.onRender = onRender;
    this.redraw = redraw;
  }

  contains(position) {
    return false;
  }

}

class StaticImageRender extends StaticRender {
  constructor({
    src,
    width = 0,
    height = 0,
    onLoad = null,
    onLoadStart = null,
    onLoadEnd = null,
    onError = null,
    opacity = 1,
    offset = [0, 0]
  }) {
    super();
    this.offset = offset;
    this.onLoad = onLoad;
    this.onLoadStart = onLoadStart;
    this.onLoadEnd = onLoadEnd;
    this.onError = onError;
    this._opacity = opacity;
    this._width = width;
    this._height = height;
    this._src = src;
    this._aborted = false;
    this._complete = false;

    this._createNode();
  }

  _createNode() {
    this._node = new Image();
    if (this._width > 0) this._node.width = this._width;
    if (this._height > 0) this._node.height = this._height;
    this._node.style.opacity = this._opacity.toString();
    const controller = new AbortController();
    const signal = controller.signal;
    this._controller = controller;
    this.onLoadStart && this.onLoadStart();
    this.readyPromise = loadImage(this._node, this._src, signal);
    this.readyPromise.then(() => {
      this._complete = true;
      this.onLoad && this.onLoad();
    }).catch(error => {
      if (this._aborted) {
        return;
      }

      this.onError && this.onError(error);
    }).finally(() => {
      this.onLoadEnd && this.onLoadEnd();
    });
  }

  get node() {
    if (this._node) return this._node;

    this._createNode();

    return this._node;
  }

  get width() {
    return this._width;
  }

  get height() {
    return this._height;
  } // working only for base64 src


  get isReady() {
    return this._node && this._node.complete;
  }

  get error() {
    return this._node && (this._node.complete && this._node.src || this.isImageLoaded && this._node.parentElement) && this._node.naturalWidth === 0;
  }

  get opacity() {
    return parseFloat(this.node.style.opacity);
  }

  set opacity(value) {
    this._opacity = value;
    if (this.node) this.node.style.opacity = value.toString();
  }

  get isImageLoaded() {
    return this._complete;
  }

  deleteNode() {
    this._aborted = true;

    this._controller.abort();

    if (this._node) {
      this._node.src = "";
    }
  }

}

/**
 * New features are added to the feature layer
 * @event FeaturesAddEvent
 */

class FeaturesAddEvent extends sGisEvent {
  constructor(features) {
    super(FeaturesAddEvent.type);
    this.features = features;
  }

}
FeaturesAddEvent.type = 'featuresAdd';
/**
 * Some features were removed from the feature layer
 * @event FeaturesRemoveEvent
 */

class FeaturesRemoveEvent extends sGisEvent {
  constructor(features) {
    super(FeaturesRemoveEvent.type);
    this.features = features;
  }

}
FeaturesRemoveEvent.type = 'featuresRemove';
/**
 * A layer that contains arbitrary set of features.
 * @alias sGis.FeatureLayer
 */

class FeatureLayer extends Layer {
  /**
   * @param __namedParameters - properties to be set to the corresponding fields.
   * @param extensions - [JS ONLY]additional properties to be copied to the created instance.
   */
  constructor(_a = {}, extensions) {
    var {
      delayedUpdate = true,
      features = []
    } = _a,
        layerParams = __rest(_a, ["delayedUpdate", "features"]);

    super(Object.assign({
      delayedUpdate
    }, layerParams), extensions);
    this._features = features;
  }

  getRenders(bbox, resolution) {
    let renders = [];
    this.getFeatures(bbox, resolution).forEach(feature => {
      renders = renders.concat(feature.render(resolution, bbox.crs));
      renders.forEach(render => {
        if (render instanceof StaticImageRender) {
          render.onLoad = () => {
            this.redraw();
          };
        }
      });
    });
    return renders;
  }

  getFeatures(bbox, resolution) {
    if (!this.checkVisibility(resolution)) return [];
    return this._features.filter(feature => feature.crs.canProjectTo(bbox.crs) && (feature.persistOnMap || feature.bbox.intersects(bbox)));
  }
  /**
   * Adds a feature or an array of features to the layer.
   * @param features - features to add.
   * @throws if one of the features is already in the layer.
   * @fires FeaturesAddEvent
   */


  add(features) {
    const toAdd = Array.isArray(features) ? features : [features];
    if (toAdd.length === 0) return;
    toAdd.forEach(f => {
      if (this._features.indexOf(f) !== -1) error(new Error(`Feature ${f} is already in the layer`));
    });
    this._features = this._features.concat(toAdd);
    this.fire(new FeaturesAddEvent(toAdd));
    this.redraw();
  }
  /**
   * Removes a feature or an array of features from the layer.
   * @param features - feature or features to be removed.
   * @throws if the one of the features is not in the layer.
   * @fires [[FeaturesRemoveEvent]]
   */


  remove(features) {
    const toRemove = Array.isArray(features) ? features : [features];
    if (toRemove.length === 0) return;
    toRemove.forEach(f => {
      let index = this._features.indexOf(f);

      if (index === -1) error(new Error(`Feature ${f} is not in the layer`));

      this._features.splice(index, 1);
    });
    this.fire(new FeaturesRemoveEvent(toRemove));
    this.redraw();
  }
  /**
   * Returns true if the given feature is in the layer.
   * @param feature
   */


  has(feature) {
    return this._features.indexOf(feature) !== -1;
  }
  /**
   * Moves the given feature to the top of the layer (end of the list). If the feature is not in the layer, the command is ignored.
   * @param feature
   */


  moveToTop(feature) {
    let index = this._features.indexOf(feature);

    if (index !== -1) {
      this._features.splice(index, 1);

      this._features.push(feature);

      this.redraw();
    }
  }
  /**
   * List of features in the layer. If assigned, it removes all features and add new ones, firing all the respective events.
   * @fires [[FeaturesAddEvent]]
   * @fires [[FeaturesRemoveEvent]]
   */


  get features() {
    return this._features;
  }

  set features(features) {
    const currFeatures = this._features;
    this._features = [];
    this.fire(new FeaturesRemoveEvent(currFeatures));
    this.add(features);
    this.redraw();
  }

}

/**
 * Converts degrees to radians
 */
const degToRad = d => d / 180 * Math.PI;
/**
 * Converts radians to degrees
 */

const radToDeg = r => r / Math.PI * 180;
const TOLERANCE$1 = 0.000001;
/**
 * Returns true if a and b differ less then tolerance
 */

const softEquals = function softEquals(a, b, tolerance = TOLERANCE$1) {
  return Math.abs(a - b) <= tolerance;
};
/**
 * Prepares the set of coordinates for matrix operations
 * @param coord
 * @param center - the center of the operation
 */

const extendCoordinates = function (coord, center) {
  coord.forEach(point => {
    point[0] = point[0] - center[0];
    point[1] = point[1] - center[1]; // FIX_ME:
    // @ts-ignore

    point[2] = 1;
  });
};
/**
 * Takes extended coordinates and make them plain again
 * @param coord
 * @param center - the center of the operation
 */

const collapseCoordinates = function (coord, center) {
  coord.forEach(point => {
    point[0] = point[0] + center[0];
    point[1] = point[1] + center[1];
    point.splice(2, 1);
  });
};
/**
 * Returns a array with render coordinates of Polygon with simplification
 * @param feature
 * @param resolution
 * @param crs
 * @param tolerance - the tolerance of simplification. Points that are overflow other points or lines with given tolerance will be excluded from the result
 */

const getRenderedPolygonCoordinates = (feature, resolution, crs, tolerance = 1) => {
  let projected = feature.crs.equals(crs) ? feature.rings : feature.projectTo(crs).rings;
  return simplifyCoordinates(projected.map(ring => {
    return ring.map(point => {
      return [Math.round(point[0] / resolution), Math.round(point[1] / -resolution)];
    });
  }), tolerance);
};
/**
 * Returns a new array with simplified coordinates
 * @param rings - array of coordinate contours
 * @param tolerance - the tolerance of simplification. Points that are overflow other points or lines with given tolerance will be excluded from the result
 */

const simplifyCoordinates = function (rings, tolerance) {
  let result = [];

  for (let ring = 0, l = rings.length; ring < l; ring++) {
    let simplified = [rings[ring][0]];

    for (let i = 1, len = rings[ring].length - 1; i < len; i++) {
      if (!softEquals(rings[ring][i][0], simplified[simplified.length - 1][0], tolerance) || !softEquals(rings[ring][i][1], simplified[simplified.length - 1][1], tolerance)) {
        simplified.push(rings[ring][i]);
      }
    }

    if (simplified[simplified.length - 1] !== rings[ring][rings[ring].length - 1]) simplified.push(rings[ring][rings[ring].length - 1]);
    result[ring] = simplified;
  }

  return result;
};
/**
 * Multiplies matrix a by matrix b
 */

const multiplyMatrix = function (a, b) {
  let c = [];

  for (let i = 0, m = a.length; i < m; i++) {
    c[i] = [];

    for (let j = 0, q = b[0].length; j < q; j++) {
      c[i][j] = 0;

      for (let r = 0, n = b.length; r < n; r++) {
        c[i][j] += a[i][r] * b[r][j];
      }
    }
  }

  return c;
};
const squareDistance = function (a, b) {
  return (a[0] - b[0]) * (a[0] - b[0]) + (a[1] - b[1]) * (a[1] - b[1]);
};
/**
 * counter-clockwise - positive angle (@radians param)
 * clockwise - negative angle (@radians param)
 */

function rotate$1(cx, cy, x, y, radians) {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return [cos * (x - cx) + sin * (y - cy) + cx, cos * (y - cy) - sin * (x - cx) + cy];
}

var mathModule = /*#__PURE__*/Object.freeze({
  __proto__: null,
  degToRad: degToRad,
  radToDeg: radToDeg,
  softEquals: softEquals,
  extendCoordinates: extendCoordinates,
  collapseCoordinates: collapseCoordinates,
  getRenderedPolygonCoordinates: getRenderedPolygonCoordinates,
  simplifyCoordinates: simplifyCoordinates,
  multiplyMatrix: multiplyMatrix,
  squareDistance: squareDistance,
  rotate: rotate$1
});

let identityProjection = ([x, y]) => [x, y];
/**
 * @alias sGis.Crs
 */


class Crs$1 {
  /**
   * @constructor
   * @param description - properties describing crs. If given, the values will be set to the corresponding fields of the crs instance.
   * @param projectionMap - set of projections to different coordinate systems. If not specified, .setProjectionTo() method
   *                        must be used in order to enable projecting to a needed crs.
   */
  constructor(description, projectionMap) {
    this._projections = new Map();
    this._discoveryMode = false;
    if (description) Object.assign(this, description);
    if (projectionMap) this._projections = projectionMap;
  }
  /**
   * Returns string definition of the crs.<br>
   *     If .wkid is set, returning value will be wkid string;<br>
   *     Else if .wkt is set, returning value will be wkt;<br>
   *     Otherwise .details property will be returned.
   */


  toString() {
    if (this.wkid) return this.wkid.toString();
    if (this.wkt) return this.wkt;
    return this.details || 'Unknown crs';
  }
  /**
   * Returns true if given crs represents the same spatial reference system. The objects are compared by wkid or by wkt text.
   * In case neither is specified, true will be returned only if both this and 'crs' represent same JS object.
   */


  equals(crs) {
    if (this === crs) return true;
    if (this.wkid && this.wkid === crs.wkid) return true;
    return !!this.wkt && this.wkt === crs.wkt;
  }
  /**
   * Returns projection function from the current coordinate system to the specified one. If it is possible to
   * project current crs to the target through another crs, returning function will apply this route.<br>
   * E.g. if trying to project from WebMercator to EllipticalMercator, the returned Projection function will
   * first project from WebMercator to Lat/Lon, and then project the result to EllipticalMercator.
   */


  projectionTo(crs) {
    return this._projections.get(crs) || this._discoverProjectionTo(crs);
  }
  /**
   * Returns true if the current coordinate system can be projected to the given crs. Projections through other coordinate
   * systems are also considered if discovered.
   */


  canProjectTo(crs) {
    return this.projectionTo(crs) !== null;
  }
  /**
   * Sets the projection function from current crs to the given one.
   */


  setProjectionTo(crs, projection) {
    this._projections.set(crs, projection);
  }

  _discoverProjectionTo(crs) {
    if (this._discoveryMode) return null;
    if (this.equals(crs)) return identityProjection;
    this._discoveryMode = true;

    for (let [ownCrs, func] of this._projections) {
      if (ownCrs.equals(crs)) {
        this._projections.set(crs, func);

        break;
      }

      const innerProjection = ownCrs._discoverProjectionTo(crs);

      if (innerProjection) {
        let result = function ([x, y]) {
          return innerProjection(func([x, y]));
        };

        this._projections.set(crs, result);

        break;
      }
    }

    this._discoveryMode = false;
    return this._projections.get(crs) || null;
  }

}
/**
 * Plain euclidean coordinate system. This projection cannot be projected to any other projection.
 * @alias sGis.CRS.plain
 */

const plain = /*#__PURE__*/new Crs$1({
  details: 'Plain crs without any projection functions'
});
const OCG84_WKT = 'GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137,298.257223563]],' + 'PRIMEM["Greenwich",0],UNIT["Degree",0.017453292519943295]]';
/**
 * Geographic coordinate system on WGS84 datum, which has longitude set as first coordinate, and latitude as second coordinate.<br>
 * <b>NOTE:</b> A lot of GIS software incorrectly implement EPSG:4326 as this longitude/latitude coordinate system. If you
 * are used to using such systems as OpenLayers or PostGIS, you want to use this crs instead of 4326 in those system.
 * @alias sGis.CRS.wgs84
 */

const wgs84 = /*#__PURE__*/new Crs$1({
  wkid: 84,
  authority: 'OCG',
  wkt: OCG84_WKT
});
const EPSG4326_WKT = `GEODCRS["WGS 84",
  DATUM["World Geodetic System 1984",
    ELLIPSOID["WGS 84",6378137,298.257223563,LENGTHUNIT["metre",1.0]]],
  CS[ellipsoidal,2],
    AXIS["latitude",north,ORDER[1]],
    AXIS["longitude",east,ORDER[2]],
    ANGLEUNIT["degree",0.01745329252],
  ID["EPSG",4326]]`;
/**
 * Geographic coordinate system on WGS84 datum with latitude being first coordinate, and longitude as second coordinate.<br>
 * <b>NOTE:</b> This coordinate system is registered by EPSG as EPSG:4326, but a lot of GIS software implements 4326 as
 * longitude/latitude. If you are used to that coordinate order use wgs84 crs instead of this one.
 * @alias sGis.CRS.geo
 */

const geo = /*#__PURE__*/new Crs$1({
  wkid: 4326,
  authority: 'EPSG',
  wkt: EPSG4326_WKT
});
geo.setProjectionTo(wgs84, ([x, y]) => [y, x]);
wgs84.setProjectionTo(geo, ([x, y]) => [y, x]);
const EPSG3857_WKT = 'PROJCS["WGS 84 / Pseudo-Mercator",GEOGCS["GCS_WGS_1984",' + 'DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137,298.257223563]],PRIMEM["Greenwich",0],' + 'UNIT["Degree",0.017453292519943295]],PROJECTION["Mercator"],PARAMETER["central_meridian",0],' + 'PARAMETER["scale_factor",1],PARAMETER["false_easting",0],PARAMETER["false_northing",0],UNIT["Meter",1]]';
/**
 * Coordinate system used by many gis web applications. The coordinates are given in meters, as projected by Mercator projection
 * using WGS84 datum assuming that the Earth is a sphere. Has high distortions near the poles.
 * @alias sGis.CRS.webMercator
 */

const webMercator = /*#__PURE__*/new Crs$1({
  wkid: 3857,
  authority: 'EPSG',
  wkt: EPSG3857_WKT
});
{
  const a = 6378137;
  webMercator.setProjectionTo(wgs84, ([x, y]) => {
    let rLat = Math.PI / 2 - 2 * Math.atan(Math.exp(-y / a));
    let rLong = x / a;
    let lon = radToDeg(rLong);
    let lat = radToDeg(rLat);
    return [lon, lat];
  });
  wgs84.setProjectionTo(webMercator, ([x, y]) => {
    let rLon = degToRad(x);
    let rLat = degToRad(y);
    let X = a * rLon;
    let Y = a * Math.log(Math.tan(Math.PI / 4 + rLat / 2));
    return [X, Y];
  });
}
const EPSG3395_WKT = 'PROJCS["WGS 84 / World Mercator",GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",' + 'SPHEROID["WGS_1984",6378137,298.257223563]],PRIMEM["Greenwich",0],UNIT["Degree",0.017453292519943295]],' + 'PROJECTION["Mercator"],PARAMETER["central_meridian",0],PARAMETER["scale_factor",1],' + 'PARAMETER["false_easting",0],PARAMETER["false_northing",0],UNIT["Meter",1]]';
/**
 * Mercator projection from WGS84 datum. This crs considers the fact that the Earth is not a sphere. Coordinates are in meters.
 * @alias sGis.CRS.ellipticalMercator
 */

const ellipticalMercator = /*#__PURE__*/new Crs$1({
  wkid: 3395,
  authority: 'EPSG',
  wkt: EPSG3395_WKT
});
{
  const a = 6378137;
  const b = 6356752.3142451793;
  const e = /*#__PURE__*/Math.sqrt(1 - b * b / a / a);
  const eh = e / 2;
  const pih = Math.PI / 2;
  ellipticalMercator.setProjectionTo(wgs84, ([x, y]) => {
    let ts = Math.exp(-y / a);
    let phi = pih - 2 * Math.atan(ts);
    let i = 0;
    let dphi = 1;

    while (Math.abs(dphi) > 0.000000001 && i++ < 15) {
      let con = e * Math.sin(phi);
      dphi = pih - 2 * Math.atan(ts * Math.pow((1 - con) / (1 + con), eh)) - phi;
      phi += dphi;
    }

    let rLong = x / a,
        rLat = phi,
        lon = radToDeg(rLong),
        lat = radToDeg(rLat);
    return [lon, lat];
  });
  wgs84.setProjectionTo(ellipticalMercator, ([x, y]) => {
    let rLat = degToRad(y);
    let rLon = degToRad(x);
    let X = a * rLon;
    let Y = a * Math.log(Math.tan(Math.PI / 4 + rLat / 2) * Math.pow((1 - e * Math.sin(rLat)) / (1 + e * Math.sin(rLat)), e / 2));
    return [X, Y];
  });
} //http://mathworld.wolfram.com/AlbersEqual-AreaConicProjection.html

/**
 * Class constructor of <a href="https://en.wikipedia.org/wiki/Albers_projection">Alber's equal area projections</a> from wgs84 datum.
 * @alias sGis.CRS.AlbersEqualArea
 */

class AlbersEqualArea extends Crs$1 {
  /**
   * @param lat0 - latitude of origin
   * @param lon0 - longitude of origin
   * @param stLat1 - first standard parallel
   * @param stLat2 - second standard parallel
   */
  constructor(lat0, lon0, stLat1, stLat2) {
    super({
      details: 'Albers Equal-Area Conic Projection: ' + lat0 + ',' + lon0 + ',' + stLat1 + ',' + stLat2
    });
    this.R = 6372795;

    let _lat0 = degToRad(lat0);

    let _lon0 = degToRad(lon0);

    let _stLat1 = degToRad(stLat1);

    let _stLat2 = degToRad(stLat2);

    let _n = (Math.sin(_stLat1) + Math.sin(_stLat2)) / 2;

    let _c = Math.pow(Math.cos(_stLat1), 2) + 2 * _n * Math.sin(_stLat1);

    let _ro0 = Math.sqrt(_c - 2 * _n * Math.sin(_lat0)) / _n;

    this.setProjectionTo(wgs84, ([x, y]) => {
      let xRad = x / this.R;
      let yRad = y / this.R;
      let th = Math.atan(xRad / (_ro0 - yRad));
      let ro = xRad / Math.sin(th);
      let rLat = Math.asin((_c - ro * ro * _n * _n) / 2 / _n);
      let rLon = _lon0 + th / _n;
      let lat = radToDeg(rLat);
      let lon = radToDeg(rLon);
      return [lon, lat];
    });
    wgs84.setProjectionTo(this, ([lon, lat]) => {
      let rLon = degToRad(lon),
          rLat = degToRad(lat),
          th = _n * (rLon - _lon0),
          ro = Math.sqrt(_c - 2 * _n * Math.sin(rLat)) / _n,
          x = ro * Math.sin(th) * this.R,
          y = (_ro0 - ro * Math.cos(th)) * this.R;

      return [x, y];
    });
  }

}
/**
 * Alber's equal area projection with parameters 0, 180, 60, 50. Used internally for precise geographic area calculations.
 * @alias sGis.CRS.conicEqualArea
 */

const conicEqualArea = /*#__PURE__*/new AlbersEqualArea(0, 180, 60, 50);

var CrsModule = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Crs: Crs$1,
  plain: plain,
  wgs84: wgs84,
  geo: geo,
  webMercator: webMercator,
  ellipticalMercator: ellipticalMercator,
  AlbersEqualArea: AlbersEqualArea,
  conicEqualArea: conicEqualArea
});

/**
 * Feature is an abstract representation of a geographical object.
 * @alias sGis.Feature
 */

class Feature extends EventHandler {
  constructor({
    crs = geo,
    symbol,
    persistOnMap = false
  } = {}) {
    super();
    this._hidden = false;
    this._symbolContainer = new FeatureSymbolContainer(this, symbol);
    this._crs = crs;
    this.persistOnMap = persistOnMap;
  }
  /**
   * Sets default coordinate system for all features.<br><br>
   *     <strong>
   *     NOTE: This method affects all already created features that do not have explicitly specified crs.
   *     You should use this function only when initializing the library.
   *     </strong>
   * @param crs
   */


  static setDefaultCrs(crs) {
    Feature.prototype._crs = crs;
  }
  /**
   * Calls the render method of the feature's symbol (or temp symbol if it is set) to render the feature. The render result
   * is cached inside the feature class.
   * @param resolution
   * @param crs
   */


  render(resolution, crs) {
    return this._symbolContainer.render(resolution, crs);
  }
  /**
   * Resets the rendered cache of the feature, making it to redraw in the next redraw cycle.
   */


  redraw() {
    this._symbolContainer.reset();
  }
  /**
   * Prevents feature from rendering.
   */


  hide() {
    this._hidden = true;
  }
  /**
   * Allows feature to render after it was hidden.
   */


  show() {
    this._hidden = false;
  }
  /**
   * Sets a temporary symbol for the feature. This symbol is used instead of the original symbol until cleared.
   * @param symbol
   */


  setTempSymbol(symbol) {
    this._symbolContainer.setTempSymbol(symbol);
  }
  /**
   * Clears the previously set temporary symbol, restoring the original symbol.
   */


  clearTempSymbol() {
    this._symbolContainer.clearTempSymbol();
  }
  /**
   * Returns true, if a temporary symbol is currently set for this feature.
   */


  get isTempSymbolSet() {
    return this._symbolContainer.isTempSymbolSet;
  }
  /**
   * Returns the original symbol of the feature. If temporary symbol is not set, the returned value will be same as value of the .symbol property.
   */


  get originalSymbol() {
    return this._symbolContainer.originalSymbol;
  }
  /**
   * Coordinate system of the feature.
   */


  get crs() {
    return this._crs;
  }
  /**
   * Current symbol of the feature. If temporary symbol is set, the value will be the temporary symbol.
   */


  get symbol() {
    return this._symbolContainer.symbol;
  }

  set symbol(symbol) {
    this._symbolContainer.symbol = symbol;
  }
  /**
   * Specifies weather the feature is hidden by .hide() method.
   */


  get hidden() {
    return this._hidden;
  }

}

class FeatureSymbolContainer {
  constructor(feature, symbol) {
    this._feature = feature;
    this._symbol = symbol;
  }

  get symbol() {
    return this._tempSymbol || this._symbol;
  }

  set symbol(symbol) {
    this._symbol = symbol;
    this.reset();
  }
  /**
   * Renders the feature with the given parameters.
   * @param resolution
   * @param crs
   */


  render(resolution, crs) {
    if (this._feature.hidden || !this.symbol) return [];
    if (this._cached && !this._needToRender(resolution, crs)) return this._cached.renders;
    this._cached = {
      resolution: resolution,
      crs: crs,
      renders: this.symbol.renderFunction(this._feature, resolution, crs)
    };
    if (this._feature.eventFlags !== MouseEventFlags.None) this._cached.renders.forEach(render => {
      render.listenFor(this._feature.eventFlags, event => {
        this._feature.fire(event);
      });
    });
    return this._cached.renders;
  }

  _needToRender(resolution, crs) {
    return !this._cached || this._cached.resolution !== resolution || this._cached.crs !== crs || this._cached.renders.length === 0;
  }

  reset() {
    this._cached = undefined;
    let dynamicFeature = this._feature;

    if (dynamicFeature.__dynamicSymbolRender) {
      dynamicFeature.__dynamicSymbolRender.update();

      dynamicFeature.__dynamicSymbolRender.redraw(dynamicFeature);
    }
  }

  setTempSymbol(symbol) {
    this._tempSymbol = symbol;
    this.reset();
  }

  clearTempSymbol() {
    this._tempSymbol = undefined;
    this.reset();
  }

  get isTempSymbolSet() {
    return !!this._tempSymbol;
  }

  get originalSymbol() {
    return this._symbol;
  }

}

/**
 * Simple geographical point
 * @alias sGis.Point
 */

class Point$1 {
  /**
   * @param position - point position
   * @param crs - point coordinate system
   */
  constructor(position, crs = geo) {
    this.crs = crs;
    this.position = position;
  }
  /**
   * Returns a new point with same position in new crs
   * @param newCrs - target coordinate system
   * @throws Cannot project to specified crs.
   */


  projectTo(newCrs) {
    const projection = this.crs.projectionTo(newCrs);

    if (projection) {
      return new Point$1(projection(this.position), newCrs);
    } else {
      return error(new Error("Cannot project point to crs: " + newCrs.toString()));
    }
  }
  /**
   * Returns a copy of the point
   */


  clone() {
    return new Point$1(this.position, this.crs);
  }
  /**
   * Getter returns clone of this point. Setter set point position.
   */


  get point() {
    return this.clone();
  }

  set point(point) {
    this.position = point.projectTo(this.crs).position;
  }

  get x() {
    return this.position[0];
  }

  set x(x) {
    this.position[0] = x;
  }

  get y() {
    return this.position[1];
  }

  set y(y) {
    this.position[1] = y;
  }
  /**
   * Returns true if the target point has the same position and crs as the current one
   * @param point - target point for comparison
   */


  equals(point) {
    return softEquals(point.x, this.x) && softEquals(point.y, this.y) && point.crs.equals(this.crs);
  }
  /**
   * Coordinates / position of this point
   */


  get coordinates() {
    return [this.position[0], this.position[1]];
  }

  set coordinates(position) {
    this.position = [position[0], position[1]];
  }

}

/**
 * Object representing a rectangular area on a map between two points.
 * @alias sGis.Bbox
 */

class Bbox {
  /**
   * Resulting rectangle will be the minimum rectangle that includes both points. The order and relative position of
   * points can be arbitrary.
   * @param point1 - first corner point of rectangle
   * @param point2 - second corner point of rectangle
   * @param crs - coordinate system of the point coordinates
   */
  constructor(point1, point2, crs = geo) {
    this._crs = crs;
    this._p = [Math.min(point1[0], point2[0]), Math.min(point1[1], point2[1]), Math.max(point1[0], point2[0]), Math.max(point1[1], point2[1])];
  }
  /**
   * Returns a new Bbox in the specified coordinate system
   * @param crs - target coordinate system
   * @throws If the instance coordinates cannot be projected into the target crs
   */


  projectTo(crs) {
    let projected1 = new Point$1([this._p[0], this._p[1]], this._crs).projectTo(crs).position;
    let projected2 = new Point$1([this._p[2], this._p[3]], this._crs).projectTo(crs).position;
    return new Bbox(projected1, projected2, crs);
  }
  /**
   * Center point of the bounding box
   */


  get center() {
    return [(this.xMax + this.xMin) / 2, (this.yMax + this.yMin) / 2];
  }
  /**
   * Returns a copy of the bbox
   */


  clone() {
    return this.projectTo(this._crs);
  }
  /**
   * Returns true if the given bbox is equal (geographically) to the target bbox. It will return false if the target
   * bbox is set in different coordinate system or if any of the 4 coordinates are different by more then 0.0001%.
   * @param bbox - target bbox
   */


  equals(bbox) {
    let target = bbox.coordinates;

    for (let i = 0; i < 4; i++) if (!softEquals(this._p[i], target[i])) return false;

    return this._crs.equals(bbox.crs);
  }
  /**
   * Returns combination of the current bbox with the given one. It will include be a minimum rectangle that includes
   * both bboxes.
   * @param bbox
   */


  intersect(bbox) {
    bbox = bbox.crs === this.crs ? bbox : bbox.projectTo(this.crs);
    return new Bbox([Math.min(this.xMin, bbox.xMin), Math.min(this.yMin, bbox.yMin)], [Math.max(this.xMax, bbox.xMax), Math.max(this.yMax, bbox.yMax)], this.crs);
  }
  /**
   * Returns true if at list one point of the given bbox lies inside the target bbox.
   * <br><bNOTE</b> that this method will return true if on of the bboxes is completely inside the other.
   * It will return false if bboxes are adjustened, e.g. a side of one bbox touches a side of another one.
   * @param bbox - target bbox
   */


  intersects(bbox) {
    let projected = bbox.projectTo(this._crs);
    return this.xMax > projected.xMin && this.xMin < projected.xMax && this.yMax > projected.yMin && this.yMin < projected.yMax;
  }
  /**
   * Returns true, if the target point is inside the bbox or lies on one of its sides
   * @param point
   */


  contains(point) {
    let projected = point.projectTo(this.crs);
    return this.xMin <= projected.x && this.xMax >= projected.x && this.yMin <= projected.y && this.yMax >= projected.y;
  }
  /**
   * Coordinate system of the bbox.
   */


  get crs() {
    return this._crs;
  }
  /**
   * Coordinate of the right border of the bbox. Cannot be assigned value less then xMin.
   */


  get xMax() {
    return this._p[2];
  }

  set xMax(value) {
    if (value < this.xMin) error('Max value cannot be lower than the min value');
    this._p[2] = value;
  }
  /**
   * Coordinate of the top border of the bbox. Cannot be assigned value less then yMin.
   */


  get yMax() {
    return this._p[3];
  }

  set yMax(value) {
    if (value < this.yMin) error('Max value cannot be lower than the min value');
    this._p[3] = value;
  }
  /**
   * Coordinate of the left border of the bbox. Cannot be assigned value larger then xMax.
   */


  get xMin() {
    return this._p[0];
  }

  set xMin(value) {
    if (value > this.xMax) error('Min value cannot be higher than the max value');
    this._p[0] = value;
  }
  /**
   * Coordinate of the bottom border of the bbox. Cannot be assigned value larger then yMax.
   */


  get yMin() {
    return this._p[1];
  }

  set yMin(value) {
    if (value > this.yMax) error('Min value cannot be higher than the max value');
    this._p[1] = value;
  }
  /**
   * Width of the bbox.
   */


  get width() {
    return this.xMax - this.xMin;
  }
  /**
   * Height of the bbox.
   */


  get height() {
    return this.yMax - this.yMin;
  }
  /**
   * Coordinates of the bbox in the form [xMin, yMin, xMax, yMax].
   */


  get coordinates() {
    return [this._p[0], this._p[1], this._p[2], this._p[3]];
  }
  /**
   * Creates a bbox from a point and offset. The created bbox will be a square area around the point min and max
   * coordinates of which are 'offset' away from the point. Bbox will have point's crs.
   * @param point - center point of created bbox.
   * @param offset - distance from center to the borders of bbox.
   */


  static fromPoint(point, offset) {
    return new Bbox([point.x - offset, point.y - offset], [point.x + offset, point.y + offset], point.crs);
  }

  offset(offset) {
    return new Bbox([this.xMin - offset[0], this.yMin - offset[1]], [this.xMax + offset[0], this.yMax + offset[1]], this.crs);
  }

}

/**
 * Utility class for working with different representations of colors in browsers
 * @alias sGis.utils.Color
 */

class Color {
  /**
   * @param string - any valid css color string
   */
  constructor(string) {
    this._original = string;
    if (!isString(string)) string = string.toString();
    this._color = string && string.trim() || 'transparent';

    this._setChannels();
  }

  _setChannels() {
    let format = this.format;

    if (format && formats[format]) {
      this._channels = formats[format](this._color);
    } else {
      this._channels = {
        r: NaN,
        g: NaN,
        b: NaN,
        a: NaN
      };
    }
  }

  get _min() {
    return Math.min(this._channels.r, this._channels.g, this._channels.b);
  }

  get _max() {
    return Math.max(this._channels.r, this._channels.g, this._channels.b);
  }

  get _delta() {
    return this._max - this._min;
  }
  /**
   * Returns the color as a string in the requested format
   * @param format - target format. Available values: "hex" - #AARRGGBB, "rgb" - "rgb(r, g, b)", "rgba" - "rgba(r, g, b, a)"
   */


  toString(format) {
    if (format === 'hex') {
      return '#' + decToHex(this.a) + decToHex(this.r) + decToHex(this.g) + decToHex(this.b);
    } else if (format === 'rgb') {
      return 'rgb(' + this.r + ',' + this.g + ',' + this.b + ')';
    } else {
      return 'rgba(' + this.r + ',' + this.g + ',' + this.b + ',' + (this.a / 255).toFixed(2).replace(/\.*0+$/, '') + ')';
    }
  }
  /**
   * Returns the string given to the constructor of the instance
   * @type {String}
   */


  get original() {
    return this._original;
  }
  /**
   * Returns true if the instance represents a valid color, i.e. that after parsing of the string given to the constructor,
   * the format is recognized and values of all channels are resolved to valid numbers.
   * @returns {Boolean}
   */


  get isValid() {
    return isNumber(this._channels.a) && isNumber(this._channels.r) && isNumber(this._channels.g) && isNumber(this._channels.b);
  }
  /**
   * Converts HSV color value into RGB
   * @param h - hue value
   * @param s - saturation value
   * @param v - 'value' value
   */


  setHsv(h, s, v) {
    let rgbColors = [];
    h /= 60;
    s /= 100;
    v /= 100;
    let floor = Math.floor(h);
    let mod = floor % 6;
    let f = h - floor;
    let p = Math.round(255 * v * (1 - s));
    let q = Math.round(255 * v * (1 - s * f));
    let t = Math.round(255 * v * (1 - s * (1 - f)));
    v = Math.round(255 * v);

    switch (mod) {
      case 0:
        rgbColors = [v, t, p];
        break;

      case 1:
        rgbColors = [q, v, p];
        break;

      case 2:
        rgbColors = [p, v, t];
        break;

      case 3:
        rgbColors = [p, q, v];
        break;

      case 4:
        rgbColors = [t, p, v];
        break;

      case 5:
        rgbColors = [v, p, q];
        break;
    }

    this._channels.r = rgbColors[0];
    this._channels.g = rgbColors[1];
    this._channels.b = rgbColors[2];
    return this;
  }
  /**
   * Returns the format of the input color sting. Possible values: hex3, hex6, hex4, hex8, rgb, rgba, name.
   * (name is the named css color values like "white")
   */


  get format() {
    if (this._color.substr(0, 1) === '#' && this._color.search(/[^#0-9a-fA-F]/) === -1) {
      if (this._color.length === 4) {
        return 'hex3';
      } else if (this._color.length === 7) {
        return 'hex6';
      } else if (this._color.length === 5) {
        return 'hex4';
      } else if (this._color.length === 9) {
        return 'hex8';
      }
    } else if (this._color.substr(0, 4) === 'rgb(') {
      return 'rgb';
    } else if (this._color.substr(0, 5) === 'rgba(') {
      return 'rgba';
    } else if (this._color.substr(0, 4) === 'hsv(') {
      return 'hsv';
    } else if (this._color in Color.names) {
      return 'name';
    }
  }
  /**
   * Returns red channel value as integer from 0 to 255.
   */


  get r() {
    return this._channels.r;
  }

  set r(v) {
    this._channels.r = v;
  }
  /**
   * Returns green channel value as integer from 0 to 255.
   */


  get g() {
    return this._channels.g;
  }

  set g(v) {
    this._channels.g = v;
  }
  /**
   * Returns blue channel value as integer from 0 to 255.
   */


  get b() {
    return this._channels.b;
  }

  set b(v) {
    this._channels.b = v;
  }
  /**
   * Returns opacity channel value as integer from 0 to 255.
   */


  get a() {
    return this._channels.a;
  }

  set a(v) {
    this._channels.a = v;
  }
  /**
   * Returns hue channel value as integer from 0 to 360.
   */


  get h() {
    let max = this._max;
    let min = this._min;
    let delta = this._delta;
    let h;
    let {
      r,
      g,
      b
    } = this._channels;
    if (max === min) h = 0;else if (r === max) h = (g - b) / delta;else if (g === max) h = 2 + (b - r) / delta;else if (b === max) h = 4 + (r - g) / delta;
    h = Math.min(h * 60, 360);
    if (h < 0) h += 360;
    h = Math.round(h);
    return h;
  }
  /**
   * Returns saturation channel value as integer from 0 to 100.
   */


  get s() {
    let s;
    let max = this._max;
    if (max === 0) s = 0;else s = this._delta / max * 1000 / 10;
    s = Math.round(s);
    return s;
  }
  /**
   * Returns 'value' channel of hsv space as integer from 0 to 100.
   */


  get v() {
    return Math.round(this._max / 255 * 1000 / 10);
  }
  /**
   * Returns values of the channels as integers from 0 to 255. Format is { r: r, g: g, b: b, a: a }.
   */


  get channels() {
    return Object.assign({}, this._channels);
  }

} // Big List of Colors
// ------------------
// <http://www.w3.org/TR/css3-color/#svg-color>
//noinspection SpellCheckingInspection

Color.names = {
  aliceblue: "f0f8ff",
  antiquewhite: "faebd7",
  aqua: "0ff",
  aquamarine: "7fffd4",
  azure: "f0ffff",
  beige: "f5f5dc",
  bisque: "ffe4c4",
  black: "000",
  blanchedalmond: "ffebcd",
  blue: "00f",
  blueviolet: "8a2be2",
  brown: "a52a2a",
  burlywood: "deb887",
  burntsienna: "ea7e5d",
  cadetblue: "5f9ea0",
  chartreuse: "7fff00",
  chocolate: "d2691e",
  coral: "ff7f50",
  cornflowerblue: "6495ed",
  cornsilk: "fff8dc",
  crimson: "dc143c",
  cyan: "0ff",
  darkblue: "00008b",
  darkcyan: "008b8b",
  darkgoldenrod: "b8860b",
  darkgray: "a9a9a9",
  darkgreen: "006400",
  darkgrey: "a9a9a9",
  darkkhaki: "bdb76b",
  darkmagenta: "8b008b",
  darkolivegreen: "556b2f",
  darkorange: "ff8c00",
  darkorchid: "9932cc",
  darkred: "8b0000",
  darksalmon: "e9967a",
  darkseagreen: "8fbc8f",
  darkslateblue: "483d8b",
  darkslategray: "2f4f4f",
  darkslategrey: "2f4f4f",
  darkturquoise: "00ced1",
  darkviolet: "9400d3",
  deeppink: "ff1493",
  deepskyblue: "00bfff",
  dimgray: "696969",
  dimgrey: "696969",
  dodgerblue: "1e90ff",
  firebrick: "b22222",
  floralwhite: "fffaf0",
  forestgreen: "228b22",
  fuchsia: "f0f",
  gainsboro: "dcdcdc",
  ghostwhite: "f8f8ff",
  gold: "ffd700",
  goldenrod: "daa520",
  gray: "808080",
  green: "008000",
  greenyellow: "adff2f",
  grey: "808080",
  honeydew: "f0fff0",
  hotpink: "ff69b4",
  indianred: "cd5c5c",
  indigo: "4b0082",
  ivory: "fffff0",
  khaki: "f0e68c",
  lavender: "e6e6fa",
  lavenderblush: "fff0f5",
  lawngreen: "7cfc00",
  lemonchiffon: "fffacd",
  lightblue: "add8e6",
  lightcoral: "f08080",
  lightcyan: "e0ffff",
  lightgoldenrodyellow: "fafad2",
  lightgray: "d3d3d3",
  lightgreen: "90ee90",
  lightgrey: "d3d3d3",
  lightpink: "ffb6c1",
  lightsalmon: "ffa07a",
  lightseagreen: "20b2aa",
  lightskyblue: "87cefa",
  lightslategray: "789",
  lightslategrey: "789",
  lightsteelblue: "b0c4de",
  lightyellow: "ffffe0",
  lime: "0f0",
  limegreen: "32cd32",
  linen: "faf0e6",
  magenta: "f0f",
  maroon: "800000",
  mediumaquamarine: "66cdaa",
  mediumblue: "0000cd",
  mediumorchid: "ba55d3",
  mediumpurple: "9370db",
  mediumseagreen: "3cb371",
  mediumslateblue: "7b68ee",
  mediumspringgreen: "00fa9a",
  mediumturquoise: "48d1cc",
  mediumvioletred: "c71585",
  midnightblue: "191970",
  mintcream: "f5fffa",
  mistyrose: "ffe4e1",
  moccasin: "ffe4b5",
  navajowhite: "ffdead",
  navy: "000080",
  oldlace: "fdf5e6",
  olive: "808000",
  olivedrab: "6b8e23",
  orange: "ffa500",
  orangered: "ff4500",
  orchid: "da70d6",
  palegoldenrod: "eee8aa",
  palegreen: "98fb98",
  paleturquoise: "afeeee",
  palevioletred: "db7093",
  papayawhip: "ffefd5",
  peachpuff: "ffdab9",
  peru: "cd853f",
  pink: "ffc0cb",
  plum: "dda0dd",
  powderblue: "b0e0e6",
  purple: "800080",
  rebeccapurple: "663399",
  red: "f00",
  rosybrown: "bc8f8f",
  royalblue: "4169e1",
  saddlebrown: "8b4513",
  salmon: "fa8072",
  sandybrown: "f4a460",
  seagreen: "2e8b57",
  seashell: "fff5ee",
  sienna: "a0522d",
  silver: "c0c0c0",
  skyblue: "87ceeb",
  slateblue: "6a5acd",
  slategray: "708090",
  slategrey: "708090",
  snow: "fffafa",
  springgreen: "00ff7f",
  steelblue: "4682b4",
  tan: "d2b48c",
  teal: "008080",
  thistle: "d8bfd8",
  tomato: "ff6347",
  turquoise: "40e0d0",
  violet: "ee82ee",
  wheat: "f5deb3",
  white: "fff",
  whitesmoke: "f5f5f5",
  yellow: "ff0",
  yellowgreen: "9acd32",
  transparent: '0000'
};

function decToHex(dec) {
  let hex = Math.floor(dec).toString(16);
  return hex.length === 1 ? '0' + hex : hex;
}

let formats = {
  hex3: function (string) {
    return {
      r: parseInt(string.substr(1, 1) + string.substr(1, 1), 16),
      g: parseInt(string.substr(2, 1) + string.substr(2, 1), 16),
      b: parseInt(string.substr(3, 1) + string.substr(3, 1), 16),
      a: 255
    };
  },
  hex6: function (string) {
    return {
      r: parseInt(string.substr(1, 2), 16),
      g: parseInt(string.substr(3, 2), 16),
      b: parseInt(string.substr(5, 2), 16),
      a: 255
    };
  },
  hex4: function (string) {
    return {
      r: parseInt(string.substr(2, 1) + string.substr(2, 1), 16),
      g: parseInt(string.substr(3, 1) + string.substr(3, 1), 16),
      b: parseInt(string.substr(4, 1) + string.substr(4, 1), 16),
      a: parseInt(string.substr(1, 1) + string.substr(1, 1), 16)
    };
  },
  hex8: function (string) {
    return {
      r: parseInt(string.substr(3, 2), 16),
      g: parseInt(string.substr(5, 2), 16),
      b: parseInt(string.substr(7, 2), 16),
      a: parseInt(string.substr(1, 2), 16)
    };
  },
  rgb: function (string) {
    let percents = string.match(/%/g);

    if (!percents || percents.length === 3) {
      var channels = string.substring(string.search(/\(/) + 1, string.length - 1).split(',');

      for (var i = 0; i < 3; i++) {
        if (channels[i]) {
          channels[i] = channels[i].trim();
          var percent = channels[i].match(/[.\d\-]+%/);

          if (percent) {
            var points = channels[i].match(/\./g);
            channels[i] = channels[i].search(/[^\d.\-%]/) === -1 && (!points || points.length < 2) ? parseFloat(percent[0]) : NaN;

            if (channels[i] < 0) {
              channels[i] = 0;
            } else if (channels[i] > 100) {
              channels[i] = 100;
            }

            channels[i] = Math.floor(channels[i] * 255 / 100);
          } else {
            channels[i] = channels[i] && channels[i].match(/[^ \-0-9]/) === null && channels[i].match(/[0-9]+/g).length === 1 ? parseInt(channels[i]) : NaN;

            if (channels[i] < 0) {
              channels[i] = 0;
            } else if (channels[i] > 255) {
              channels[i] = 255;
            }
          }
        }
      }
    } else {
      channels = [];
    }

    return {
      r: channels[0],
      g: channels[1],
      b: channels[2],
      a: 255
    };
  },
  rgba: function (string) {
    let channels = formats.rgb(string);
    channels.a = undefined;
    let match = string.match(/[\-0-9\.]+/g);

    if (match && match[3]) {
      let points = match[3].match(/\./g);

      if (!points || points.length === 1) {
        channels.a = parseFloat(match[3]);

        if (channels.a < 0) {
          channels.a = 0;
        } else if (channels.a > 1) {
          channels.a = 1;
        }

        channels.a = Math.round(channels.a * 255);
      }
    }

    return channels;
  },
  name: function (string) {
    let color = new Color('#' + Color.names[string]);
    return color.channels;
  }
};

let symbolDescriptions = {};
/**
 * Registers symbol class for serialization. After registration, the symbol can be serialized and deserialized with this
 * serialier.
 * @param constructor - constructor (class) of the symbol.
 * @param name - unique name of the symbol type. It is used to find correct constructor on deserialization.
 * @param properties - list of property names that should be serialized.
 */

const registerSymbol = (constructor, name, properties) => {
  symbolDescriptions[name] = {
    Constructor: constructor,
    properties: properties
  };
};
/**
 * Serializes symbol to a key-value JSON object.
 * @param symbol - symbol to be serialized.
 * @param colorsFormat - color format to be used during serialization. If not set, the value from the symbol property
 *                       will be used without change.
 */

const serialize = (symbol, colorsFormat = null) => {
  let keys = Object.keys(symbolDescriptions);

  for (let i = 0; i < keys.length; i++) {
    let desc = symbolDescriptions[keys[i]];

    if (symbol.constructor === desc.Constructor) {
      let serialized = {
        symbolName: keys[i]
      };
      desc.properties.forEach(prop => {
        let value = symbol[prop];

        if (colorsFormat) {
          let color = new Color(value);
          if (color.isValid) value = color.toString(colorsFormat);
        }

        if (value instanceof Object) {
          value = copyObject(value);
        }

        serialized[prop] = value;
      });
      return serialized;
    }
  }

  error(new Error('Unknown type of symbol.'));
};
/**
 * Deserializes symbol.
 * @param desc - serialized symbol as JSON object.
 * @param colorsFormat - format of the color properties to be used after deserialization. If not set, the values will be
 *                       set without change.
 */

const deserialize = (desc, colorsFormat = null) => {
  if (!symbolDescriptions[desc.symbolName]) error(new Error('Unknown type of symbol.'));
  let symbol = new symbolDescriptions[desc.symbolName].Constructor();
  symbolDescriptions[desc.symbolName].properties.forEach(prop => {
    let val = desc[prop];

    if (colorsFormat) {
      let color = new Color(val.toString());
      if (color.isValid && color.format === colorsFormat) val = color.toString('rgba');
    }

    if (val instanceof Object) {
      val = copyObject(val);
    }

    symbol[prop] = val;
  });
  return symbol;
};

var symbolSerializer = /*#__PURE__*/Object.freeze({
  __proto__: null,
  registerSymbol: registerSymbol,
  serialize: serialize,
  deserialize: deserialize
});

let id = 0;
/**
 * Cross browser DOM event attachment
 * @param {HTMLElement} element - target element
 * @param {String} type - name of the event
 * @param {Function} handler - event handler
 * @returns {Function} - attached handler
 */

const listenDomEvent = function (element, type, handler) {
  if (type === 'wheel') type = getWheelEventType();
  if (!handler.guid) handler.guid = ++id;

  if (!element.events) {
    element.events = {};

    element.handle = event => {
      return commonHandle.call(element, event);
    };
  }

  if (!element.events[type]) {
    element.events[type] = {};

    if (element.addEventListener) {
      element.addEventListener(type, element.handle, false);
    } else if (element.attachEvent) {
      element.attachEvent("on" + type, element.handle);
    }
  }

  element.events[type][handler.guid] = handler;
  return handler;
};
/**
 * Removes an event handler.
 * @param {HTMLElement} element - target element
 * @param {String} type - event name
 * @param {Function} [handler] - handler to be removed. If not specified all handlers will be removed.
 */

const removeDomEventListener = function (element, type, handler) {
  var handlers = element.events && element.events[type];
  if (!handlers) return;

  if (!handler) {
    Object.keys(handlers).forEach(key => {
      delete handlers[key];
    });
  } else {
    delete handlers[handler.guid];
  }

  if (Object.keys(handlers).length > 0) return;

  if (element.removeEventListener) {
    element.removeEventListener(type, element.handle, false);
  } else if (element.detachEvent) {
    element.detachEvent("on" + type, element.handle);
  }

  delete element.events[type];
  if (Object.keys(element.events).length > 0) return;

  try {
    delete element.handle;
    delete element.events;
  } catch (e) {
    // IE
    element.removeAttribute("handle");
    element.removeAttribute("events");
  }
};

function fixEvent(event) {
  event = event || window.event;

  if (event.isFixed) {
    return event;
  }

  event.isFixed = true;

  event.preventDefault = event.preventDefault || function () {
    this.returnValue = false;
  };

  event.stopPropagation = event.stopPropagation || function () {
    this.cancelBubble = true;
  };

  if (!event.target) {
    event.target = event.srcElement;
  }

  if (!event.currentTarget) {
    event.currentTarget = event.srcElement;
  }

  if (event.relatedTarget === undefined && event.fromElement) {
    event.relatedTarget = event.fromElement === event.target ? event.toElement : event.fromElement;
  }

  if (event.pageX == null && event.clientX != null) {
    var html = document.documentElement,
        body = document.body;
    event.pageX = event.clientX + (html && html.scrollLeft || body && body.scrollLeft || 0) - (html.clientLeft || 0);
    event.pageY = event.clientY + (html && html.scrollTop || body && body.scrollTop || 0) - (html.clientTop || 0);
  }

  if (!event.which && event.button) {
    // noinspection JSBitwiseOperatorUsage
    event.which = event.button & 1 ? 1 : event.button & 2 ? 3 : event.button & 4 ? 2 : 0;
  }

  return event;
}

function commonHandle(event) {
  event = fixEvent(event);
  var handlers = this.events[event.type];
  let keys = Object.keys(handlers);

  for (let i = 0; i < keys.length; i++) {
    let ret = handlers[keys[i]].call(this, event);

    if (ret === false) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  }
}

function getWheelEventType() {
  if (document.addEventListener) {
    if ('onwheel' in document) {
      return 'wheel';
    } else if ('onmousewheel' in document) {
      return 'mousewheel';
    } else {
      return 'MozMousePixelScroll';
    }
  }
}
/**
 * Returns cross-browser consistent value of wheel rolling direction (-1 or 1).
 * @param {Event} e
 * @returns {number}
 */


const getWheelDirection = function (e) {
  var wheelData = (e.detail ? e.detail * -1 : e.wheelDelta / 40) || e.deltaY * -1;

  if (wheelData > 0) {
    wheelData = 1;
  } else if (wheelData < 0) {
    wheelData = -1;
  }

  return wheelData;
};
/**
 * Returns offset of a mouse event relative to the target element
 * @param {HTMLElement} target
 * @param {MouseEvent} e
 * @returns {{x: Number, y: Number}}
 */

const getMouseOffset = function (target, e) {
  let docPos = target === document || target === window ? {
    x: 0,
    y: 0
  } : getPosition(target);
  return {
    x: e.pageX - docPos.x,
    y: e.pageY - docPos.y
  };
};
/**
 * Returns offset of an element relative to the viewport
 * @param {HTMLElement} e
 * @returns {{x: Number, y: Number}}
 */

const getPosition = function (e) {
  var clientRect = e.getBoundingClientRect(),
      x = window.pageXOffset !== undefined ? window.pageXOffset : (document.documentElement || document.body.parentNode || document.body).scrollLeft,
      y = window.pageYOffset !== undefined ? window.pageYOffset : (document.documentElement || document.body.parentNode || document.body).scrollTop;
  return {
    x: clientRect.left + x,
    y: clientRect.top + y
  };
};

var eventModule = /*#__PURE__*/Object.freeze({
  __proto__: null,
  listenDomEvent: listenDomEvent,
  removeDomEventListener: removeDomEventListener,
  getWheelDirection: getWheelDirection,
  getMouseOffset: getMouseOffset,
  getPosition: getPosition
});

/**
 * Symbol that renders a feature to the screen coordinate system. All symbols take as input a feature, target resolution
 * and target crs, and must return a set of renders (rendered primitives) that then can be used to draw the feature.
 * @alias sGis.Symbol
 */

class Symbol {
  /**
   * This async function will be called every time the feature has to be drawn. It returns an array of renders that will actually be displayed on the map.
   * If the symbol cannot render provided feature, empty array is returned.
   * @param feature - feature to be drawn.
   * @param resolution - resolution of the render.
   * @param crs - target coordinate system of the render.
   */
  renderFunctionAsync(feature, resolution, crs) {
    return __awaiter(this, void 0, void 0, function* () {
      return this.renderFunction(feature, resolution, crs);
    });
  }
  /**
   * Returns a copy of the symbol. Only essential properties are copied.
   */


  clone() {
    let desc = serialize(this);
    return deserialize(desc);
  }

}
class DynamicPointSymbol extends Symbol {
  constructor({
    offset = [0, 0],
    onRender
  } = {}) {
    super();
    this.offset = offset;
    this.onRender = onRender;
  }

  renderFunction(feature, resolution, crs) {
    let dynamicFeature = feature;
    if (dynamicFeature.__dynamicSymbolRender) return [dynamicFeature.__dynamicSymbolRender];

    let node = this._getFeatureNode(feature);

    node.style.position = 'absolute';
    dynamicFeature.__dynamicSymbolRender = new DynamicRender({
      node: node,
      onRender: this.onRender,
      update: (bbox = null, resolution = null) => {
        if (bbox === null) bbox = dynamicFeature.__lastBbox;
        if (resolution === null) resolution = dynamicFeature.__lastResolution;
        if (!bbox || !resolution || !dynamicFeature.crs.canProjectTo(bbox.crs)) return;
        let point = dynamicFeature.projectTo(bbox.crs);
        let dx = Math.round((point.x - bbox.xMin) / resolution + this.offset[0]);
        let dy = Math.round((bbox.yMax - point.y) / resolution + this.offset[1]);
        node.style.left = `${dx.toString()}px`;
        node.style.top = `${dy.toString()}px`;
        dynamicFeature.__lastBbox = bbox;
        dynamicFeature.__lastResolution = resolution;
      },
      redraw: () => {
        this._updateFeatureNode(dynamicFeature);
      }
    });

    this._setEventListeners(dynamicFeature);

    return [dynamicFeature.__dynamicSymbolRender];
  }

  getNode(feature) {
    let [render] = this.renderFunction(feature, 1, null);
    return render.node;
  }

  _setEventListeners(dynamicFeature) {
    if (dynamicFeature.eventFlags === MouseEventFlags.None) return;
    Object.keys(mouseEvents).forEach(eventName => {
      if (dynamicFeature.eventFlags & mouseEvents[eventName].flag) {
        listenDomEvent(dynamicFeature.__dynamicSymbolRender.node, mouseEvents[eventName].type, event => {
          dynamicFeature.fire(mouseEvents[eventName].type, {
            node: dynamicFeature.__dynamicSymbolRender.node,
            browserEvent: event
          });
        });
      }
    });
  }

  _updateFeatureNode(feature) {// do nothing
  }

  renderFunctionAsync(feature, resolution, crs) {
    return __awaiter(this, void 0, void 0, function* () {
      return this.renderFunction(feature, resolution, crs);
    });
  }

}

const BEZIER_ARC_C = 0.5522847498307933984022516322796;
/**
 * Rendered arc (circle) on a map.
 * @alias sGis.render.Arc
 */

class Arc extends VectorRender {
  /**
   * @param center - the center of the arc, in the [x, y] format.
   * @param [options] - key-value options of any Arc parameters
   */
  constructor(center, options = {}) {
    super();
    /** The center of the arc in [x, y] format. */

    this.center = [0, 0];
    /** The radius of the arc. */

    this.radius = 5;
    /** The stroke color of the arc (outline color). The value can be any valid css color string. */

    this.strokeColor = 'black';
    /** The stroke width of the arc. */

    this.strokeWidth = 1;
    /** The fill color of the arc. The value can be any valid css color string. */

    this.fillColor = 'transparent';
    /** Specifies whether this render can catch mouse events. If true, this render will be transparent for any pointer events. */

    this.ignoreEvents = false;
    /** Start angle of the sector. */

    this.startAngle = 0;
    /** End angle of the sector. */

    this.endAngle = 2 * Math.PI;
    /** Shows whether the arc is a sector of a circle rather then simple arc. Set to false if you need to draw a circle, for sector has all its boundaries outlined. */

    this.isSector = false;
    /** Direction of the arc. */

    this.clockwise = true;
    /** @see [[PolyRender.lineDash]] */

    this.lineDash = [];
    /**
     * Property of the Canvas 2D API determines the shape used to draw the end points of lines.
     *
     * - `"butt"`
     *      The ends of lines are squared off at the endpoints.
     * - `"round"`
     *      The ends of lines are rounded.
     * - `"square"`
     *      The ends of lines are squared off by adding a box with an equal width and half the height of the line's thickness.
     */

    this.lineCap = "round";
    Object.assign(this, options);
    this.center = center;
  }

  contains(position) {
    let dx = position[0] - this.center[0];
    let dy = position[1] - this.center[1];
    let distance2 = dx * dx + dy * dy;
    return distance2 < (this.radius + 2) * (this.radius + 2);
  }

  get isVector() {
    return true;
  }

  setupRender(ctx, map) {
    ctx.lineWidth = this.strokeWidth;
    ctx.strokeStyle = this.strokeColor;
    ctx.fillStyle = this.fillColor;
    ctx.setLineDash(this.lineDash || []);
    ctx.lineCap = this.lineCap;
  }

  renderObject(ctx, map) {
    const isCanvas = ctx instanceof CanvasRenderingContext2D; // fix for antialias render effect on canvas on high resolution

    if (isCanvas && map && map.getScaleLevel() >= 17) {
      this.renderAsBezierCurves(ctx);
    } else {
      this.renderAsArc(ctx);
    }
  }

  renderAsArc(ctx) {
    const center = this.center;
    const isCanvas = ctx instanceof CanvasRenderingContext2D;

    if (isCanvas) {
      ctx.beginPath();
    }

    if (this.isSector) {
      ctx.moveTo(center[0], center[1]);
    } else if (ctx instanceof Path2D) {
      ctx.moveTo(center[0] + this.radius, center[1]);
    }

    ctx.arc(center[0], center[1], this.radius, this.startAngle, this.clockwise ? this.endAngle : -this.endAngle, !this.clockwise);

    if (this.isSector) {
      ctx.lineTo(center[0], center[1]);
    }

    if (isCanvas) {
      ctx.fill();
      ctx.stroke();
    }
  }

  renderAsBezierCurves(ctx) {
    const center = this.center;
    const r = this.radius;
    ctx.translate(center[0], center[1]);
    ctx.beginPath();
    ctx.moveTo(0, -r);
    ctx.bezierCurveTo(BEZIER_ARC_C * r, -r, r, -BEZIER_ARC_C * r, r, 0);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.bezierCurveTo(r, BEZIER_ARC_C * r, BEZIER_ARC_C * r, r, 0, r);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, r);
    ctx.bezierCurveTo(-BEZIER_ARC_C * r, r, -r, BEZIER_ARC_C * r, -r, 0);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-r, 0);
    ctx.bezierCurveTo(-r, -BEZIER_ARC_C * r, -BEZIER_ARC_C * r, -r, 0, -r);
    ctx.fill();
    ctx.stroke();

    if (this.isSector) {
      ctx.beginPath();
      ctx.lineTo(center[0], center[1]);
      ctx.fill();
      ctx.stroke();
    }
  }

}

/**
 * Symbol of point drawn as circle with outline.
 * @alias sGis.symbol.point.Point
 */

class PointSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Diameter of the circle. */

    this.size = 10;
    this._offset = [0, 0];
    /** Color of the inner part of the circle. Can be any valid css color string. */

    this.fillColor = 'black';
    /** Color of the outline of the circle. Can be any valid css color string. */

    this.strokeColor = 'transparent';
    /** Width of the outline. */

    this.strokeWidth = 1;
    Object.assign(this, options);
  }
  /** Offset of the point from the feature position. If set to [0, 0], center of the circle will be at the position of the feature. */


  get offset() {
    return this._offset;
  }

  set offset(value) {
    // TODO: remove deprecated part after 2018
    let deprecated = value;

    if (deprecated.x !== undefined && deprecated.y !== undefined) {
      warn('Using anchorPoint in {x, y} format is deprecated. Use [x, y] format instead.');
      this._offset = [deprecated.x, deprecated.y];
    } else {
      this._offset = value;
    }
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof PointFeature)) return [];
    let position = feature.projectTo(crs).position;
    let pxPosition = [position[0] / resolution + (this.offset[0] || 0), -position[1] / resolution + (this.offset[1] || 0)];
    return [new Arc(pxPosition, {
      fillColor: this.fillColor,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      radius: this.size / 2
    })];
  }

}
registerSymbol(PointSymbol, 'point.Point', ['size', 'offset', 'fillColor', 'strokeColor', 'strokeWidth']);

/**
 * Simple geographical point.
 * @alias sGis.feature.Point
 * @example symbols/Point_Symbols
 */

class PointFeature extends Feature {
  constructor(position, _a = {}) {
    var {
      symbol = new PointSymbol()
    } = _a,
        params = __rest(_a, ["symbol"]);

    super(Object.assign({
      symbol
    }, params));
    this._position = position;
  }

  projectTo(crs) {
    let projected = Point$1.prototype.projectTo.call(this, crs);
    return new PointFeature(projected.position, {
      crs: crs,
      symbol: this.symbol
    });
  }
  /**
   * Returns a copy of the feature.
   */


  clone() {
    return this.projectTo(this.crs);
  }

  get bbox() {
    return new Bbox(this._position, this._position, this.crs);
  }
  /**
   * Geographical coordinates of the point, given in the feature crs.
   */


  get position() {
    return [this._position[0], this._position[1]];
  }

  set position(position) {
    this._position = position;
    this.redraw();
  }
  /**
   * Point object corresponding to the feature position. This is the same as position property, but also
   * includes the information about coordinate system. If set, the point will first be projected to the feature
   * crs, and then the projected coordinates will be set to the feature.
   */


  get point() {
    return new Point$1(this.position, this.crs);
  }

  set point(point) {
    this.position = point.projectTo(this.crs).position;
  }

  get x() {
    return this._position[0];
  }

  set x(x) {
    this._position[0] = x;
    this.redraw();
  }

  get y() {
    return this._position[1];
  }

  set y(y) {
    this._position[1] = y;
    this.redraw();
  }
  /**
   * @deprecated
   */


  get coordinates() {
    return [this.position[0], this.position[1]];
  }

  set coordinates(position) {
    this.position = [position[0], position[1]];
  }

  get centroid() {
    return this.position;
  }

}

/**
 * Finds distance between two geographical points. If the coordinate system of the points can be projected to the
 * wgs84 crs, the distance will be calculated on a sphere with radius 6,371,009 meters (mean radius of the Earth).
 * @param {sGis.IPoint} a
 * @param {sGis.IPoint} b
 * @returns {Number}
 */

const distance$1 = function (a, b) {
  let l;

  if (a.crs.canProjectTo(wgs84)) {
    let p1 = a.projectTo(wgs84);
    let p2 = b.projectTo(wgs84);
    let lat1 = degToRad(p1.y);
    let lat2 = degToRad(p2.y);
    let dLat = lat2 - lat1;
    let dLon = degToRad(p2.x - p1.x);
    let d = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    let c = 2 * Math.atan2(Math.sqrt(d), Math.sqrt(1 - d));
    let R = 6371009;
    l = R * c;
  } else {
    l = Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2));
  }

  return l;
};
/**
 * Finds geographical length of the polyline or polygon. If the coordinates cannot be projected to wgs84 crs,
 * simple geometrical length will be returned.
 * @param {Position[][]} rings - the set of coordinates
 * @param {sGis.Crs} crs - coordinate system
 * @param {Boolean} [enclose=false] - if set to true, the geometry is treated as polygon, meaning that the result
 *                                    will also include the distance between first and last point of every contour
 * @returns {Number}
 */

const length = function (rings, crs, enclose = false) {
  let length = 0;
  let ringTemp;

  for (let ring = 0, l = rings.length; ring < l; ring++) {
    ringTemp = [].concat(rings[ring]);
    if (enclose) ringTemp.push(ringTemp[0]);

    for (let i = 0, m = ringTemp.length - 1; i < m; i++) {
      length += distance$1(new Point$1(ringTemp[i], crs), new Point$1(ringTemp[i + 1], crs));
    }
  }

  return length;
};
/**
 * Finds geographical area of the polygon. If the coordinates cannot be projected to wgs84 crs, simple geometrical
 * area will be returned.
 * @param {Position[][]} rings - coordinates of the polygon
 * @param {sGis.Crs} crs - coordinate system
 * @returns {Number}
 */

const area = function (rings, crs) {
  let projected;

  if (crs.canProjectTo(conicEqualArea)) {
    projected = projectRings(rings, crs, conicEqualArea);
  } else {
    projected = rings;
  }

  let area = 0;
  projected.forEach(ring => area += polygonArea(ring));
  return area;
};
/**
 * Projects the set of coordinates from one coordinate system to another.
 * If the coordinates cannot be projected, exception will be thrown.
 * @param {Position[][]} rings - coordinates
 * @param {sGis.Crs} fromCrs - source coordinate system
 * @param {sGis.Crs} toCrs - target coordinate system
 * @returns {Position[][]} - new array with projected coordinates
 */

const projectRings = function (rings, fromCrs, toCrs) {
  let projection = fromCrs.projectionTo(toCrs);
  let result = [];
  rings.forEach(ring => {
    let projectedRing = [];
    ring.forEach(position => {
      projectedRing.push(projection(position));
    });
    result.push(projectedRing);
  });
  return result;
};
const projectPoint = (coordinates, fromCrs, toCrs) => {
  const projection = fromCrs.projectionTo(toCrs);
  return projection(coordinates);
};
const projectPoints = function (ring, fromCrs, toCrs) {
  let projection = fromCrs.projectionTo(toCrs);
  let projectedRing = [];
  ring.forEach(position => {
    projectedRing.push(projection(position));
  });
  return projectedRing;
};

function polygonArea(coord) {
  coord = coord.concat([coord[0]]);
  let area = 0;

  for (let i = 0, l = coord.length - 1; i < l; i++) {
    area += (coord[i][0] + coord[i + 1][0]) * (coord[i][1] - coord[i + 1][1]);
  }

  return Math.abs(area / 2);
}
/**
 * Returns a point on the line, closest to the given point
 * @param {Position} point
 * @param {Position[]} line - line coordinates in the form [[x1, y1], [x2, y2]]
 * @returns {Position}
 */


const pointToLineProjection = function (point, line) {
  if (line[0][0] === line[1][0]) {
    return [line[0][0], point[1]];
  } else if (line[0][1] === line[1][1]) {
    return [point[0], line[0][1]];
  } else {
    let lx = line[1][0] - line[0][0];
    let ly = line[1][1] - line[0][1];
    let dx = line[0][0] - point[0];
    let dy = line[0][1] - point[1];
    let t = -(dx * lx + dy * ly) / (lx * lx + ly * ly);
    let x = line[0][0] + t * lx;
    let y = line[0][1] + t * ly;
    return [x, y];
  }
};
/**
 * Checks if a point is located inside a polygon.
 * @param {Number[]} polygon - coordinates of polygon in format [[[x11, y11], [x12, y12], ...], [x21, y21], [x22, y22], ...], ...]. If there is only one counter outer array can be omitted.
 * @param {number[]} point - coordinates of the point [x, y]
 * @param {Number} [tolerance=0] - the tolerance of check. If the point is out of the polygon, but is closer then tolerance, the returned result will be true.
 * @returns {boolean|Array} - true, if the point is inside of polygon, [ring, index] - index of vertex if the point is closer then 'tolerance' to one of the sides of polygon, false otherwise
 */

const contains = function (polygon, point, tolerance = 0) {
  let intersectionCount = 0;
  let adjusted = Array.isArray(polygon[0][0]) ? polygon : [polygon];

  for (let ring = 0, l = adjusted.length; ring < l; ring++) {
    let points = adjusted[ring].concat([adjusted[ring][0]]);
    let prevD = points[0][0] - point[0];
    let prevH = points[0][1] > point[1];

    for (let i = 1; i < points.length; i++) {
      if (pointToLineDistance(point, [points[i - 1], points[i]]) <= tolerance) {
        return [ring, i - 1];
      }

      let D = points[i][0] - point[0];
      let H = points[i][1] > point[1];

      if (!H !== !prevH //otherwise line does not intersect horizontal line
      && (D > 0 || prevD > 0) //line is to the left from the point, but we look to the right
      ) {
        if (!(point[1] === points[i][1] && point[1] === points[i - 1][1])) {
          //checks if line is horizontal and has same Y with point
          if (intersects([[points[i][0], points[i][1]], [points[i - 1][0], points[i - 1][1]]], [point, [Math.max(points[i][0], points[i - 1][0]), point[1]]])) {
            intersectionCount++;
          }
        }
      }

      prevD = D;
      prevH = H;
    }
  }

  return intersectionCount % 2 === 1;
};
/**
 * Returns the minimum distance between the given point and line.
 * @param point
 * @param line - line coordinates in the form [[x1, y1], [x2, y2]]
 */

const pointToLineDistance = function (point, line) {
  return Math.sqrt(pointToLineDistanceSquare(point, line));
};
/**
 * Returns the minimum square distance between the given point and line.
 * @param point
 * @param line
 */

const pointToLineDistanceSquare = function (point, line) {
  let lx = line[1][0] - line[0][0];
  let ly = line[1][1] - line[0][1];
  let dx = line[0][0] - point[0];
  let dy = line[0][1] - point[1];
  let t = 0 - (dx * lx + dy * ly) / (lx * lx + ly * ly);
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.pow(lx * t + dx, 2) + Math.pow(ly * t + dy, 2);
};
/**
 * Returns true if the given lines have at least one common point.
 * @param {Position[]} line1 - line coordinates in the form [[x1, y1], [x2, y2]]
 * @param {Position[]} line2 - line coordinates in the form [[x1, y1], [x2, y2]]
 * @returns {Boolean}
 */

const intersects = function (line1, line2) {
  if (line1[0][0] === line1[1][0]) {
    return line1[0][0] > line2[0][0];
  } else {
    let k = (line1[0][1] - line1[1][1]) / (line1[0][0] - line1[1][0]);
    let b = line1[0][1] - k * line1[0][0];
    let x = (line2[0][1] - b) / k;
    return x > line2[0][0];
  }
};
/**
 * Returns the angle of line relative to horizon in radians. The value can be from -PI to PI, first point is considered base point for rotation.
 * @param {Position[]} line - line coordinates in the form [[x1, y1], [x2, y2]]
 * @return {Number}
 */

const getLineAngle = function (line) {
  if (line[0][0] === line[1][0] && line[0][1] === line[1][1]) return NaN;
  let x = line[1][0] - line[0][0];
  let y = line[1][1] - line[0][1];
  let cos = x / Math.sqrt(x * x + y * y);
  return y >= 0 ? Math.acos(cos) : -Math.acos(cos);
};
/**
 * Returns a point at the specified distance and angle relative to horizon from origin point
 * @param {Position} point - origin point
 * @param {Number} angle - angle in radians
 * @param {Number} distance - distance
 * @returns {Position}
 */

const getPointFromAngleAndDistance = function (point, angle, distance) {
  return [point[0] + Math.cos(angle) * distance, point[1] + Math.sin(angle) * distance];
};
/**
 * Returns false if polygon has self-intersection, segments of zero length or contours with less then 3 points
 * @param {sGis.feature.Polygon|Position[][]} polygon  - polygon feature or coordinates
 * @param {boolean} tolerateSelfIntersection - skip check for self intersection
 * @returns {Boolean}
 */

const isPolygonValid = function (polygon, tolerateSelfIntersection) {
  let coordinates = polygon.rings ? polygon.rings : polygon;
  if (coordinates.length === 0) return false;

  for (let ring = 0; ring < coordinates.length; ring++) {
    if (coordinates[ring].length <= 2) return false;

    for (let i = 0; i < coordinates[ring].length; i++) {
      let p1 = coordinates[ring][i];
      let p2 = coordinates[ring][i + 1] || coordinates[ring][0];
      if (p1[0] == p2[0] && p1[1] === p2[1]) return false;
      if (!tolerateSelfIntersection && hasIntersection(coordinates, [p1, p2], [ring, i])) return false;
    }
  }

  return true;
};

function hasIntersection(coordinates, line, exc) {
  for (let ring = 0; ring < coordinates.length; ring++) {
    for (let i = 0; i < coordinates[ring].length; i++) {
      if (ring === exc[0] && (Math.abs(i - exc[1]) < 2 || exc[1] === 0 && i === coordinates[ring].length - 1 || i === 0 && exc[1] === coordinates[ring].length - 1)) continue;
      if (lineIntersects([coordinates[ring][i], coordinates[ring][i + 1] || coordinates[ring][0]], line)) return true;
    }
  }

  return false;
}

function lineIntersects(l1, l2) {
  let o1 = orient(l1[0], l1[1], l2[0]);
  let o2 = orient(l1[0], l1[1], l2[1]);
  let o3 = orient(l2[0], l2[1], l1[0]);
  let o4 = orient(l2[0], l2[1], l1[1]);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(l1[0], l2[0], l1[1])) return true;
  if (o2 === 0 && onSegment(l1[0], l2[1], l1[1])) return true;
  if (o3 === 0 && onSegment(l2[1], l1[0], l2[1])) return true;
  if (o4 === 0 && onSegment(l2[1], l1[1], l2[1])) return true;
  return false;
}

function orient(p, q, r) {
  let val = (q[1] - p[1]) * (r[0] - q[0]) - (q[0] - p[0]) * (r[1] - q[1]);
  if (Math.abs(val) < 0.000001) return 0;
  return val > 0 ? 1 : 2;
}

function onSegment(p, q, r) {
  return q[0] <= Math.max(p[0], r[0]) && q[0] >= Math.min(p[0], r[0]) && q[1] <= Math.max(p[1], r[1]) && q[1] >= Math.min(p[1], r[1]);
}
/**
 * Applies matrix transformation on each feature in the set
 * @param {sGis.Feature[]} features
 * @param {Number[][]} matrix - transformation matrix
 * @param {IPoint|Position} center - the center of transformation
 * @param {sGis.Crs} [mapCrs] - coordinate system of transformation. If not specified, transformation will be preformed
 * on the coordinates as is. This will result in errors with any non-cartesian coordinates (like lat/lon).
 */


const transform = function (features, matrix, center, mapCrs = null) {
  if (Array.isArray(features)) {
    features.forEach(feature => transformFeature(feature, matrix, center, mapCrs));
  } else {
    transformFeature(features, matrix, center, mapCrs);
  }
};
/**
 * Rotates the features around given point
 * @param {sGis.Feature[]} features
 * @param {Number} angle - rotation angle in radians. Positive values stand for counterclockwise rotation.
 * @param {IPoint|Position} center - rotation center
 * @param {sGis.Crs} [mapCrs] - coordinate system of transformation. If not specified, transformation will be preformed
 * on the coordinates as is. This will result in errors with any non-cartesian coordinates (like lat/lon).
 */

const rotate = function (features, angle, center, mapCrs = null) {
  let sin = Math.sin(angle);
  let cos = Math.cos(angle);
  transform(features, [[cos, sin, 0], [-sin, cos, 0], [0, 0, 1]], center, mapCrs);
};
/**
 * Scales the features
 * @param {sGis.Feature[]} features
 * @param {Number} scale - the magnitude of scaling. E.g. value of 2 means that the size of features will be increased 2 times.
 * @param {Position} center - center of scaling
 * @param {sGis.Crs} [mapCrs] - coordinate system of transformation. If not specified, transformation will be preformed
 * on the coordinates as is. This will result in errors with any non-cartesian coordinates (like lat/lon).
 */

const scale = function (features, scale, center, mapCrs = null) {
  transform(features, [[scale[0], 0, 0], [0, scale[1], 0], [0, 0, 1]], center, mapCrs);
};
/**
 * Moves the features
 * @param {sGis.Feature[]} features
 * @param {Number[]} translate - moving values in form [dx, dy]
 * @param {sGis.Crs} [mapCrs] - coordinate system of transformation. If not specified, transformation will be preformed
 * on the coordinates as is. This will result in errors with any non-cartesian coordinates (like lat/lon).
 */

const move = function (features, translate, mapCrs = null) {
  transform(features, [[1, 0, 0], [0, 1, 1], [translate[0], translate[1], 1]], [0, 0], mapCrs);
};

function transformFeature(feature, matrix, center, mapCrs = null) {
  let targetCrs = mapCrs || feature.crs;
  let base = center.crs ? center.projectTo(targetCrs).position : center;

  if (feature.rings) {
    let rings = feature.rings;

    if (targetCrs !== feature.crs) {
      rings = projectRings(rings, feature.crs, targetCrs);
    }

    transformRings(rings, matrix, base);

    if (targetCrs !== feature.crs) {
      rings = projectRings(rings, targetCrs, feature.crs);
    }

    feature.rings = rings;
  } else if (feature.points) {
    let points = feature.points;

    if (targetCrs !== feature.crs) {
      points = projectRings([points], feature.crs, targetCrs)[0];
    }

    points = transformRing(points, matrix, base);

    if (targetCrs !== feature.crs) {
      points = projectRings([points], targetCrs, feature.crs)[0];
    }

    feature.points = points;
  } else if (feature.position) {
    let points = [feature.position];

    if (targetCrs !== feature.crs) {
      points = projectRings([points], feature.crs, targetCrs)[0];
    }

    points = transformRing(points, matrix, base);

    if (targetCrs !== feature.crs) {
      points = projectRings([points], targetCrs, feature.crs)[0];
    }

    feature.position = points[0];
  }
}

function transformRings(rings, matrix, base) {
  rings.forEach((ring, index) => {
    rings[index] = transformRing(ring, matrix, base);
  });
}

function transformRing(ring, matrix, base) {
  extendCoordinates(ring, base);
  let transformed = multiplyMatrix(ring, matrix);
  collapseCoordinates(transformed, base);
  return transformed;
}

var geotoolsModule = /*#__PURE__*/Object.freeze({
  __proto__: null,
  distance: distance$1,
  length: length,
  area: area,
  projectRings: projectRings,
  projectPoint: projectPoint,
  projectPoints: projectPoints,
  pointToLineProjection: pointToLineProjection,
  contains: contains,
  pointToLineDistance: pointToLineDistance,
  pointToLineDistanceSquare: pointToLineDistanceSquare,
  intersects: intersects,
  getLineAngle: getLineAngle,
  getPointFromAngleAndDistance: getPointFromAngleAndDistance,
  isPolygonValid: isPolygonValid,
  transform: transform,
  rotate: rotate,
  scale: scale,
  move: move
});

var FillStyle;

(function (FillStyle) {
  FillStyle[FillStyle["None"] = 0] = "None";
  FillStyle[FillStyle["Color"] = 1] = "Color";
  FillStyle[FillStyle["Image"] = 2] = "Image";
})(FillStyle || (FillStyle = {}));
/**
 * Rendered polygon
 * @alias sGis.render.Poly
 */


class PolyRender extends VectorRender {
  /**
   * @param coordinates - the coordinates of the polygon.
   * @param options - properties to be assigned to the instance
   */
  constructor(coordinates, options = {}) {
    super();
    /** Whether the first and the last points should be connected. */

    this.enclosed = false;
    /** Fill style of the polygon. */

    this.fillStyle = FillStyle.Color;
    /** Stroke color of the polygon. Can be any valid css color string. */

    this.strokeColor = 'black';
    /** Fill color of the polygon. Can be any valid css color string. */

    this.fillColor = 'transparent';
    /** Stroke width of the polyline. */

    this.strokeWidth = 1;
    /** Specifies whether this render can catch mouse events. If true, this render will be transparent for any pointer events. */

    this.ignoreEvents = false;
    /** The distance (px) from the drawn line inside which the event is still considered to be inside the line. */

    this.lineContainsTolerance = 4;
    /** Dash pattern for the line as specified in HTML CanvasRenderingContext2D.setLineDash() specification. */

    this.lineDash = [];
    /** Fill image of the polygon */

    this.fillImage = null;
    /** Drop shadow of the polygon {offsetX, offsetY, blur, color}. */

    this.shadow = null;
    /**
     * Property of the Canvas 2D API determines the shape used to draw the end points of lines.
     *
     * - `"butt"`
     *      The ends of lines are squared off at the endpoints.
     * - `"round"`
     *      The ends of lines are rounded.
     * - `"square"`
     *      The ends of lines are squared off by adding a box with an equal width and half the height of the line's thickness.
     */

    this.lineCap = "round";
    /**
     * Property of the Canvas 2D API determines the shape used to join two line segments where they meet.
     *
     * - `"bevel"`
     *      Fills an additional triangular area between the common endpoint of connected segments, and the separate outside rectangular corners of each segment.
     * - `"round"`
     *      Rounds off the corners of a shape by filling an additional sector of disc centered at the common endpoint of connected segments. The radius for these rounded corners is equal to the line width.
     * - `"miter"`
     *      Connected segments are joined by extending their outside edges to connect at a single point, with the effect of filling an additional lozenge-shaped area. This setting is affected by the miterLimit property
     */

    this.lineJoin = "round";
    /**
     * Property of the Canvas 2D API sets the miter limit ratio.
     * @docs https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/miterLimit
     */

    this.miterLimit = 10;
    /** Drawn stroke outside. */

    this.isOutsideStroke = false;
    /** Drawn stroke inside. */

    this.isInsideStroke = false;
    Object.assign(this, options);
    this.coordinates = coordinates;
  }

  get isVector() {
    return true;
  }

  contains(position) {
    let polygonContains = contains(this.coordinates, position, this.strokeWidth / 2 + this.lineContainsTolerance);
    if (this.enclosed) return !!polygonContains;
    return Array.isArray(polygonContains) && polygonContains[1] !== this.coordinates[polygonContains[0]].length - 1;
  }

  setupRender(ctx, map) {
    ctx.lineCap = this.lineCap;
    ctx.lineJoin = this.lineJoin;
    ctx.miterLimit = this.miterLimit;
    ctx.lineWidth = this.strokeWidth;
    ctx.strokeStyle = this.strokeWidth <= 0 ? "transparent" : this.strokeColor;
    ctx.setLineDash(this.lineDash || []);
  }

  _drawLines(ctx) {
    const coordinates = this.coordinates;

    for (let i = 0, ringsCount = coordinates.length; i < ringsCount; i++) {
      const ring = coordinates[i];
      ctx.moveTo(ring[0][0], ring[0][1]);

      for (let j = 1, len = ring.length; j < len; j++) {
        ctx.lineTo(ring[j][0], ring[j][1]);
      }

      if (this.enclosed) {
        ctx.closePath();
      }
    }
  }

  _resetShadow(ctx) {
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'rgba(0, 0, 0, 0)';
  }

  _setShadow(ctx, reset) {
    if (reset || this.shadow === null) {
      this._resetShadow(ctx);
    } else {
      const {
        offsetX,
        offsetY,
        blur,
        color,
        isOuter
      } = this.shadow;
      ctx.shadowOffsetX = offsetX;
      ctx.shadowOffsetY = offsetY;
      ctx.shadowBlur = blur;
      ctx.shadowColor = color;

      if (isOuter) {
        ctx.fillStyle = color;
        ctx.fill();

        this._resetShadow(ctx);

        ctx.fillStyle = 'rgba(0, 0, 0, 1)';
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fill();
        ctx.fillStyle = this.fillColor;
        ctx.globalCompositeOperation = 'source-over';
      }
    }
  }

  renderObject(ctx, map) {
    const coordinates = this.coordinates;
    ctx.beginPath();

    this._drawLines(ctx);

    this._setShadow(ctx);

    if (this.isOutsideStroke) {
      ctx.lineWidth = this.strokeWidth * 2;
      ctx.stroke();
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "#000";
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
    }

    if (this.fillStyle === FillStyle.Color) {
      ctx.fillStyle = this.fillColor;
      ctx.fill();
    } else if (this.fillStyle === FillStyle.Image) {
      ctx.fillStyle = ctx.createPattern(this.fillImage, 'repeat');
      const patternOffsetX = coordinates[0][0][0] % this.fillImage.width,
            patternOffsetY = coordinates[0][0][1] % this.fillImage.height;
      ctx.translate(patternOffsetX, patternOffsetY);
      ctx.fill();
      ctx.translate(-patternOffsetX, -patternOffsetY);
    }

    if (this.enclosed) {
      this._setShadow(ctx, true);
    }

    if (this.isInsideStroke) {
      ctx.lineWidth = this.strokeWidth * 2;
      ctx.clip();
      ctx.stroke();
    }

    if (!this.isOutsideStroke && !this.isInsideStroke) {
      ctx.stroke();
    }
  }

}

class CrossPointSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Size of the square. */

    this.size = 11;
    /** Offset of the point from the feature position in {x: dx, y: dy} format. If set to {x:0, y:0}, center of the square will be at the position of the feature. */

    this.offset = [0, 0];
    /** Color of the outline of the square. Can be any valid css color string. */

    this.strokeColor = '#444';
    /** Width of the outline. */

    this.strokeWidth = 1;
    Object.assign(this, options);
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof PointFeature)) return [];
    let position = feature.projectTo(crs).position;
    let pxPosition = [position[0] / resolution, -position[1] / resolution];
    let halfSize = this.size / 2;
    let offset = this.offset;
    let coordinates = [[[pxPosition[0], pxPosition[1] - halfSize + offset[1]], [pxPosition[0], pxPosition[1] + halfSize + offset[1]]], [[pxPosition[0] - halfSize + offset[0], pxPosition[1]], [pxPosition[0] + halfSize + offset[0], pxPosition[1]]]];
    return [new PolyRender(coordinates, {
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      enclosed: false,
      fillStyle: FillStyle.None
    })];
  }

}

/**
 * Drawing of a new feature is started. When this event is fired, the control will have a new feature as its .activeFeature property.
 * @event DrawingBeginEvent
 */

class DrawingBeginEvent extends sGisEvent {
  constructor() {
    super(DrawingBeginEvent.type);
  }

}
DrawingBeginEvent.type = 'drawingBegin';
/**
 * Drawing of the current feature is finished.
 * @event DrawingFinishEvent
 */

class DrawingFinishEvent extends sGisEvent {
  constructor(feature, browserEvent) {
    super(DrawingFinishEvent.type);
    this.feature = feature;
    this.browserEvent = browserEvent;
  }

}
DrawingFinishEvent.type = 'drawingFinish';
/**
 * Control's active feature has been changed. This event is fired after change process is complete, e.g. drag is over or
 * double click is processed.
 * @event EditEvent
 */

class EditEvent extends sGisEvent {
  constructor() {
    super(EditEvent.type);
  }

}
EditEvent.type = 'edit';
/**
 * Control's active feature is being changed. This event is fired during the change, e.g. on every drag or click event.
 * @event ChangeEvent
 */

class ChangeEvent extends sGisEvent {
  constructor(ringIndex = null, pointIndex = null) {
    super(ChangeEvent.type);
    this.ringIndex = ringIndex;
    this.pointIndex = pointIndex;
  }

}
ChangeEvent.type = 'change';
/**
 * A new point was added to the control's active feature.
 * @event PointAddEvent
 */

class PointAddEvent extends sGisEvent {
  constructor() {
    super(PointAddEvent.type);
  }

}
PointAddEvent.type = 'pointAdd';
/**
 * Base class of all controls. Controls are objects that provide methods for setting interactions between user and map.
 * @alias sGis.Control
 */

class Control extends EventHandler {
  /**
   * @param map - map the control will work with.
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, {
    useTempLayer = false,
    snappingProvider,
    activeLayer,
    isActive = false,
    snappingSymbol = new CrossPointSymbol()
  } = {}) {
    super();
    this._isActive = false;
    /**
     * Temporary feature layer that is added to the map when the control is activated. It is used to show snapping point
     * and can be used as a temporary storage for features the control works with.
     */

    this._tempLayer = null;
    this._map = map;
    this.useTempLayer = useTempLayer;
    this.snappingProvider = snappingProvider;
    this.activeLayer = activeLayer;
    this._snappingSymbol = snappingSymbol;
    if (isActive) this.isActive = isActive;
  }
  /**
   * Makes the control active.
   */


  activate() {
    this.isActive = true;
  }
  /**
   * Makes the control inactive.
   */


  deactivate() {
    this.isActive = false;
  }
  /**
   * Active status of the control.
   */


  get isActive() {
    return this._isActive;
  }

  set isActive(bool) {
    if (!this._map || this._isActive === bool) return;
    this._isActive = bool;

    if (bool) {
      if (this.useTempLayer) {
        this._tempLayer = new FeatureLayer();

        this._map.addLayer(this._tempLayer);
      }

      this._activate();
    } else {
      this._deactivate();

      if (this._tempLayer && this._map.contains(this._tempLayer)) {
        this._map.removeLayer(this._tempLayer);
      }

      this._tempLayer = null;
    }
  }
  /**
   * Using the provider set in .snappingProvider property, this method searches for snapping point based on given
   * parameters. If such a point is found, it adds the snapping feature to the tempLayer (if temp layer is used).
   * Then it returns the position of the snapping point as a return value.
   * If no snapping point was found, it clears the snapping feature and removes the original coordinates.
   * @param point - base point that should be snapped.
   * @param isAltPressed - whether alt key is pressed (to cancel snapping).
   * @param activeContour - active contour of the feature. This is used for axis and orthogonal snapping methods.
   * @param activeIndex - index of the active point in the contour. This is used for axis and orthogonal snapping methods.
   * @param isPolygon - specifies whether the contour should be treated as enclosed (meaning, that last and first
   *                    points should be connected.
   * @param snappingProviderOverride - if specified, this provider will be used instead of the default one.
   */


  _snap(point, isAltPressed, activeContour, activeIndex, isPolygon, snappingProviderOverride) {
    let snappingPoint = null;
    const snappingProvider = snappingProviderOverride || this.snappingProvider;

    if (!isAltPressed && snappingProvider) {
      snappingPoint = snappingProvider.getSnappingPoint(point, activeContour, activeIndex, isPolygon);
    }

    Promise.resolve(snappingPoint).then(snappingPointResult => {
      this._snappingPointRender(point, snappingPointResult);
    });
    return snappingPoint || point;
  }

  _snappingPointRender(currentPosition, snappingPointResult) {
    if (this._tempLayer) {
      const snappingFeature = this._getSnappingFeature(snappingPointResult || currentPosition);

      if (snappingPointResult && !this._tempLayer.has(snappingFeature)) {
        this._tempLayer.add(snappingFeature);
      } else if (!snappingPointResult && this._tempLayer.has(snappingFeature)) {
        this._tempLayer.remove(snappingFeature);
      }

      this._tempLayer.redraw();
    }
  }
  /**
   * Removes snapping point from the temp layer.
   */


  _unsnap() {
    if (this._tempLayer && this._snappingFeature && this._tempLayer.has(this._snappingFeature)) {
      this._tempLayer.remove(this._snappingFeature);
    }
  }

  _getSnappingFeature(point) {
    if (!this._snappingFeature) {
      this._snappingFeature = new PointFeature([0, 0], {
        crs: this._map.crs,
        symbol: this._snappingSymbol
      });
    }

    if (point) {
      this._snappingFeature.position = point;
    }

    return this._snappingFeature;
  }
  /**
   * Map the control works with.
   */


  get map() {
    return this._map;
  }

}

class sGisMouseEvent extends sGisEvent {
  constructor({
    type,
    flag
  }, {
    point,
    browserEvent
  }) {
    super(type);
    this.point = point;
    this.browserEvent = browserEvent;
    this.eventFlag = flag;
  }

}
class sGisClickEvent extends sGisMouseEvent {
  constructor(params) {
    super(mouseEvents.click, params);
  }

}
sGisClickEvent.type = mouseEvents.click.type;
sGisClickEvent.eventFlag = mouseEvents.click.flag;
class sGisDoubleClickEvent extends sGisMouseEvent {
  constructor(params) {
    super(mouseEvents.doubleClick, params);
  }

}
sGisDoubleClickEvent.type = mouseEvents.doubleClick.type;
sGisDoubleClickEvent.eventFlag = mouseEvents.doubleClick.flag;
class sGisMouseMoveEvent extends sGisMouseEvent {
  constructor(params) {
    super(mouseEvents.mouseMove, params);
  }

}
sGisMouseMoveEvent.type = mouseEvents.mouseMove.type;
sGisMouseMoveEvent.eventFlag = mouseEvents.mouseMove.flag;
class sGisMouseOutEvent extends sGisMouseEvent {
  constructor(params) {
    super(mouseEvents.mouseOut, params);
  }

}
sGisMouseOutEvent.type = mouseEvents.mouseOut.type;
sGisMouseOutEvent.eventFlag = mouseEvents.mouseOut.flag;
class sGisMouseOverEvent extends sGisMouseEvent {
  constructor(params) {
    super(mouseEvents.mouseOver, params);
  }

}
sGisMouseOverEvent.type = mouseEvents.mouseOver.type;
sGisMouseOverEvent.eventFlag = mouseEvents.mouseOver.flag;
class DragStartEvent extends sGisMouseEvent {
  constructor(draggingObject, params) {
    super(mouseEvents.dragStart, params);
    this.draggingObject = draggingObject;
  }

}
DragStartEvent.type = mouseEvents.dragStart.type;
DragStartEvent.eventFlag = mouseEvents.dragStart.flag;
class DragEvent extends sGisMouseEvent {
  constructor({
    point,
    browserEvent,
    offset,
    pxOffset
  }) {
    super(mouseEvents.drag, {
      point,
      browserEvent
    });
    this.offset = offset;
    this.pxOffset = pxOffset;
  }

}
DragEvent.type = mouseEvents.drag.type;
DragEvent.eventFlag = mouseEvents.drag.flag;
class DragEndEvent extends sGisMouseEvent {
  constructor(params) {
    super(mouseEvents.dragEnd, params);
  }

}
DragEndEvent.type = mouseEvents.dragEnd.type;
DragEndEvent.eventFlag = mouseEvents.dragEnd.flag;

/**
 * Base class for polyline and polygon controls. When active, click on the map will start a new feature, then
 * every next click adds a new point to the feature. If ctrl is held during click, new point is added and then new
 * ring drawing starts. Feature is completed by double click.<br><br>
 *
 * When control is activated, a temporary feature layer is created and added to the map. Feature is drawn on that temp
 * layer. After drawing is finished, if .activeLayer is set, the created feature is removed from the temp layer and
 * added to the active layer.
 *
 * @alias sGis.controls.Poly
 */

class PolyControl extends Control {
  /**
   * @param map - map the control will work with
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, {
    snappingProvider,
    snappingSymbol,
    activeLayer,
    isActive = false,
    symbol,
    dblClickMinTime = 30
  } = {}) {
    super(map, {
      snappingProvider,
      snappingSymbol,
      activeLayer,
      useTempLayer: true
    });
    this._dblClickTime = 0;
    this._activeFeature = null;
    this._debouncedSnappingHandle = debounce(this._snappingHandle.bind(this), 50);
    this._handleClick = this._handleClick.bind(this);
    this._handleMousemove = this._handleMousemove.bind(this);
    this._handleDblclick = this._handleDblclick.bind(this);
    this.symbol = symbol;
    this.isActive = isActive;
    this.dblClickMinTime = dblClickMinTime;
  }

  _activate() {
    this.map.on(sGisClickEvent.type, this._handleClick);
    this.map.on(sGisMouseMoveEvent.type, this._handleMousemove);
    this.map.on(sGisDoubleClickEvent.type, this._handleDblclick);
  }

  _deactivate() {
    this.cancelDrawing();
    this.map.off(sGisClickEvent.type, this._handleClick);
    this.map.off(sGisMouseMoveEvent.type, this._handleMousemove);
    this.map.off(sGisDoubleClickEvent.type, this._handleDblclick);
  }

  _handleClick(event) {
    let clickEvent = event;
    setTimeout(() => {
      if (Date.now() - this._dblClickTime < this.dblClickMinTime) return;

      if (this._activeFeature) {
        if (clickEvent.browserEvent.ctrlKey) {
          this.startNewRing();
        } else {
          this._activeFeature.addPoint(clickEvent.point.position, this._activeFeature.rings.length - 1);

          return;
        }
      } else {
        this.startNewFeature(clickEvent.point);
        this.fire(new DrawingBeginEvent());
      }

      this.fire(new PointAddEvent());
      if (this._tempLayer) this._tempLayer.redraw();
    }, 10);
    event.stopPropagation();
  }
  /**
   * Starts a new feature with the first point at given position. If the control was not active, this method will set it active.
   * @param point
   */


  startNewFeature(point) {
    this.activate();
    this.cancelDrawing();
    this._activeFeature = this._getNewFeature(point.position);
    if (this._tempLayer) this._tempLayer.add(this._activeFeature);
  }

  _handleMousemove(event) {
    let mousemoveEvent = event;

    this._unsnap();

    this._debouncedSnappingHandle(mousemoveEvent);

    if (!this._activeFeature) return;
    let ringIndex = this._activeFeature.rings.length - 1;
    let pointIndex = this._activeFeature.rings[ringIndex].length - 1;
    this._activeFeature.rings[ringIndex][pointIndex] = mousemoveEvent.point.position;

    this._activeFeature.redraw();

    if (this._tempLayer) this._tempLayer.redraw();
    this.fire(new ChangeEvent(ringIndex, pointIndex));
  }

  _snappingHandle(event) {
    if (this._activeFeature) {
      let ringIndex = this._activeFeature.rings.length - 1;
      let pointIndex = this._activeFeature.rings[ringIndex].length - 1;

      const snappingResult = this._snap(event.point.position, event.browserEvent.altKey, this._activeFeature.rings[ringIndex], pointIndex, this._activeFeature.isEnclosed);

      Promise.resolve(snappingResult).then(point => {
        this._activeFeature.rings[ringIndex][pointIndex] = point || event.point.position;

        this._activeFeature.redraw();

        if (this._tempLayer) this._tempLayer.redraw();
        this.fire(new ChangeEvent(ringIndex, pointIndex));
      });
    } else {
      this._snap(event.point.position, event.browserEvent.altKey);
    }
  }

  _handleDblclick(event) {
    let feature = this._activeFeature;
    if (!feature) return;
    let dblclickEvent = event;
    this.finishDrawing();
    event.stopPropagation();
    this._dblClickTime = Date.now();
    this.fire(new DrawingFinishEvent(feature, dblclickEvent.browserEvent));
  }
  /**
   * Cancels drawing of the current feature, removes the feature and the temp layer. No events are fired.
   */


  cancelDrawing() {
    if (!this._activeFeature || !this._tempLayer) return;
    if (this._tempLayer.has(this._activeFeature)) this._tempLayer.remove(this._activeFeature);
    this._activeFeature = null;

    this._unsnap();
  }
  /**
   * Finishes drawing of the current feature and moves it to the active layer (if set). If the current ring has less
   * then two points, the ring is removed. If the feature has no rings, the feature is not added to the active layer.
   */


  finishDrawing() {
    let feature = this._activeFeature;
    if (!feature) return;
    let ringIndex = feature.rings.length - 1;
    this.cancelDrawing();
    if (ringIndex === 0 && feature.rings[ringIndex].length < 3) return;
    feature.removePoint(ringIndex, feature.rings[ringIndex].length - 1);
    if (this.activeLayer) this.activeLayer.add(feature);
  }
  /**
   * Start drawing of a new ring of the feature.
   */


  startNewRing() {
    if (!this._activeFeature) return;
    let rings = this._activeFeature.rings;
    let ringIndex = rings.length;
    let point = rings[ringIndex - 1][rings[ringIndex - 1].length - 1];

    this._activeFeature.setRing(ringIndex, [point]);
  }
  /**
   * The active drawing feature.
   */


  get activeFeature() {
    return this._activeFeature;
  }

  set activeFeature(feature) {
    if (!this._isActive) return;
    this.cancelDrawing();
    this._activeFeature = feature;
  }

}

const MAX_RESOLUTION = 22;
const TOLERANCE = 0.001;
const defaultLevels = [{
  resolution: 156543.03392800014,
  indexCount: 1,
  zIndex: 0
}];

for (let i = 1; i < MAX_RESOLUTION; i++) {
  defaultLevels[i] = {
    resolution: defaultLevels[i - 1].resolution / 2,
    indexCount: defaultLevels[i - 1].indexCount * 2,
    zIndex: i
  };
}
/**
 * Tile scheme used by tile layers to calculate indexes and coordinates of the tiles.
 * @alias sGis.TileScheme
 */


class TileScheme {
  constructor({
    origin,
    levels,
    limits = [-Infinity, -Infinity, Infinity, Infinity],
    reversedY = false,
    tileWidth = 256,
    tileHeight = 256
  }) {
    this._levels = levels.sort((a, b) => a.resolution - b.resolution);
    this._origin = origin;
    this.reversedY = reversedY;
    this.tileWidth = tileWidth;
    this.tileHeight = tileHeight;
    this.limits = limits;
  }
  /** @see [[TileSchemeConstructorParams.levels]] */


  get levels() {
    return this._levels;
  }
  /**
   * Returns resolution of the closest level in the tile scheme in the given direction. If no such level is found, returns smallest or largest possible resolution.
   * @param resolution - resolution that will be used as a base for search
   * @param direction - if false, will return resolution smaller then given, if true, will return resolution larger then given
   */


  getAdjustedResolution(resolution, direction = false) {
    return this.levels[this.getLevel(resolution, direction)].resolution;
  }
  /**
   * Returns closest level index in the tile scheme that has resolution in the given direction. If no such level is found, returns the last level index.
   * @param resolution - resolution that will be used as a base for search
   * @param direction - if false, resolution level with smaller resolution will be returned. If true, resolution level with larger resolution will be returned.
   */


  getLevel(resolution, direction = false) {
    if (!this.levels || this.levels.length === 0) error('Tile scheme levels are not set');
    let i;

    for (i = 0; i < this.levels.length; i++) {
      if (resolution <= this.levels[i].resolution + TOLERANCE) {
        if (direction) {
          return i === 0 ? i : i - 1;
        }

        return i;
      }
    }

    return i - 1;
  }
  /**
   * Maximum resolution in the scheme
   */


  get maxResolution() {
    return this.levels[this.levels.length - 1].resolution;
  }
  /**
   * Minimum resolution in the scheme
   */


  get minResolution() {
    return this.levels[0].resolution;
  }
  /** @see [[TileSchemeConstructorParams.origin]] */


  get origin() {
    return this._origin;
  }

}
/** Default tile scheme used for Mercator projections. */

TileScheme.default = /*#__PURE__*/new TileScheme({
  origin: [-20037508.342787, 20037508.342787],
  levels: defaultLevels,
  tileWidth: 256,
  tileHeight: 256,
  reversedY: false,
  limits: [-Infinity, -20037508.342787, Infinity, 20037508.342787]
});

var ContentsChangeType;

(function (ContentsChangeType) {
  ContentsChangeType[ContentsChangeType["Add"] = 0] = "Add";
  ContentsChangeType[ContentsChangeType["Remove"] = 1] = "Remove";
  ContentsChangeType[ContentsChangeType["Reorder"] = 2] = "Reorder";
  ContentsChangeType[ContentsChangeType["VisibilityChange"] = 3] = "VisibilityChange";
})(ContentsChangeType || (ContentsChangeType = {}));
/**
 * The contents, their order or visibility of the group is changed.
 * @event ContentsChangeEvent
 */


class ContentsChangeEvent extends sGisEvent {
  constructor(changeType, affectedLayers) {
    super(ContentsChangeEvent.type);
    this.changeType = changeType;
    this.affectedLayers = affectedLayers;
  }

}
ContentsChangeEvent.type = 'contentsChange';
/**
 * Ordered nested list of layers.
 * @alias sGis.LayerGroup
 */

class LayerGroup extends EventHandler {
  /**
   * @param layers - initial list of layers in the group
   */
  constructor(layers = []) {
    super();

    this._fireContentChange = (changeType, layers) => this.fire(new ContentsChangeEvent(changeType, layers));

    this._forwardEvent = ev => this.fire(ev);

    this._isDisplayed = true;
    this._layers = layers;
  }
  /**
   * Adds a layer to the end of the list.
   * @param layer - layer to add.
   * @fires [[ContentsChangeEvent]]
   * @throws If the layer is already in the group, or in any of the child groups.
   */


  addLayer(layer) {
    if (layer === this) error(new Error('Cannot add self to the group'));

    if (this.contains(layer)) {
      error(new Error('Cannot add layer to the group: the layer is already in the group'));
    }

    this._layers.push(layer);

    this._setChildListeners(layer);

    if (layer instanceof LayerGroup) {
      this._setForwardListeners(layer);
    }

    this.fire(new ContentsChangeEvent(ContentsChangeType.Add, [layer]));
  }
  /**
   * Removes the layer from the group.
   * @param layer - layer to remove.
   * @param recurse - if set to true, the layer will be removed from all child groups containing this layer.
   * @fires [[ContentsChangeEvent]]
   * @throws If the layer not in the group.
   */


  removeLayer(layer, recurse = false) {
    let index = this._layers.indexOf(layer);

    let removed = false;

    if (index !== -1) {
      this._layers.splice(index, 1);

      this._removeChildListeners(layer);

      if (layer instanceof LayerGroup) {
        this._removeForwardListeners(layer);
      }

      this.fire(new ContentsChangeEvent(ContentsChangeType.Remove, [layer]));
      removed = true;
    } else if (recurse) {
      for (let i = 0; i < this._layers.length; i++) {
        if (!(this._layers[i] instanceof LayerGroup)) continue;
        let group = this._layers[i];

        if (group.contains(layer)) {
          group.removeLayer(layer, true);
          removed = true;
        }
      }
    }

    if (!removed) error(new Error('The layer is not in the group'));
  }

  _setChildListeners(layer) {
    layer.on(VisibilityChangeEvent.type, () => this._fireContentChange(ContentsChangeType.VisibilityChange, [layer]));
  }

  _removeChildListeners(layer) {
    layer.off(VisibilityChangeEvent.type, () => this._fireContentChange(ContentsChangeType.VisibilityChange, [layer]));
  }

  _setForwardListeners(layerGroup) {
    layerGroup.on(ContentsChangeEvent.type, this._forwardEvent);
  }

  _removeForwardListeners(layerGroup) {
    layerGroup.off(ContentsChangeEvent.type, this._forwardEvent);
  }
  /**
   * Returns true if the group or any of the child groups (recursively) contains the given layer.
   * @param layer - layer to check
   */


  contains(layer) {
    for (let i = 0; i < this._layers.length; i++) {
      if (layer === this._layers[i]) return true;
      if (!(this._layers[i] instanceof LayerGroup)) continue;
      let group = this._layers[i];

      if (group.contains(layer)) {
        return true;
      }
    }

    return false;
  }
  /**
   * Returns index of a layer in the group. If not in the group, returns -1.
   * @param layer
   */


  indexOf(layer) {
    return this._layers.indexOf(layer);
  }
  /**
   * Inserts the layer to the given position. If the layer is already in the group, moves the layer so that new index of the layer equals the specified index.
   * If the index is negative, layer is added to the n-th position from the end of the list.
   * If the index is larger than number of the layers in the group, layer will be added to the end of the list.
   * @param layer - layer to insert.
   * @param index - integer position of the layer after insertion.
   * @fires [[ContentsChangeEvent]]
   * @throws If the given layer cannot be added to the group (e.g. if the result of reordering creates recursive nesting).
   */


  insertLayer(layer, index) {
    let currIndex = this._layers.indexOf(layer);

    let added = false;

    if (currIndex === -1) {
      this.prohibitEvent('layerAdd');
      this.addLayer(layer);
      this.allowEvent('layerAdd');
      currIndex = this._layers.length - 1;
      added = true;
    }

    let length = this._layers.length;
    index = index > length ? length : index < 0 && index < -length ? -length : index;
    if (index < 0) index = length + index;

    this._layers.splice(currIndex, 1);

    this._layers.splice(index, 0, layer);

    let changeType = added ? ContentsChangeType.Add : ContentsChangeType.Reorder;
    this.fire(new ContentsChangeEvent(changeType, [layer]));
  }
  /**
   * Moves the layer to the end of the list. If the layer is not in the group, the effect of this method is same as .add(layer).
   * @param layer - layer to add.
   */


  moveLayerToTop(layer) {
    this.insertLayer(layer, -1);
  }
  /**
   * Returns the flat list of layers in the group. Child LayerGroups are not included.
   * @param recurse - if set to true, the contents of child LayerGroups will be added to the list in the corresponding order.
   *                  E.g. if the group contents are layer1, group1, layer2, then the resulting list will be the result of concatenation of
   *                  [layer1], group1.getLayers(true), [layer2].
   * @param excludeInactive - if set to true, layers with isDisplayed=false and all their children will not be included.
   */


  getLayers(recurse = false, excludeInactive = false) {
    let layers = [];

    this._layers.forEach(layer => {
      if (excludeInactive && !layer.isDisplayed) return;

      if (recurse && layer instanceof LayerGroup) {
        layers = layers.concat(layer.getLayers(recurse, excludeInactive));
      } else {
        layers.push(layer);
      }
    });

    return layers;
  }
  /**
   * The list of the layers and child groups in the group. If assigned, two change event will be fired: one for layers
   * removal and one for layers adding.
   * @fires [[ContentsChangeEvent]]
   */


  get layers() {
    return this._layers;
  }

  set layers(layers) {
    let currLayers = this._layers;

    if (currLayers.length > 0) {
      this._layers.length = 0;
      this.fire(new ContentsChangeEvent(ContentsChangeType.Remove, currLayers));
    }

    if (layers.length > 0) {
      this.prohibitEvent(ContentsChangeEvent.type);

      for (let i = 0; i < layers.length; i++) {
        this.addLayer(layers[i]);
      }

      this.allowEvent(ContentsChangeEvent.type);
      this.fire(new ContentsChangeEvent(ContentsChangeType.Add, layers));
    }
  }
  /**
   * Whether the group is active and should be displayed on the map.
   * @fires [[VisibilityChangeEvent]]
   */


  get isDisplayed() {
    return this._isDisplayed;
  }

  set isDisplayed(bool) {
    if (this._isDisplayed === bool) return;
    this._isDisplayed = bool;
    this.fire(new VisibilityChangeEvent());
  }
  /**
   * Sets .isDisplayed property to true
   */


  show() {
    this.isDisplayed = true;
  }
  /**
   * Sets .isDisplayed property to false
   */


  hide() {
    this.isDisplayed = false;
  }

}

class BboxChangeEvent extends sGisEvent {
  constructor() {
    super(BboxChangeEvent.type);
  }

}
BboxChangeEvent.type = 'bboxChange';
class BboxChangeEndEvent extends sGisEvent {
  constructor() {
    super(BboxChangeEndEvent.type);
  }

}
BboxChangeEndEvent.type = 'bboxChangeEnd';
class AnimationStartEvent extends sGisEvent {
  constructor() {
    super(AnimationStartEvent.type);
  }

}
AnimationStartEvent.type = 'animationStart';
class AnimationEndEvent extends sGisEvent {
  constructor() {
    super(AnimationEndEvent.type);
  }

}
AnimationEndEvent.type = 'animationEnd';
/**
 * Map object with set of layers, specified position, resolution, coordinate system.
 * @alias sGis.Map
 */

class Map$1 extends LayerGroup {
  constructor({
    position = new Point$1([55.755831, 37.617673]).projectTo(webMercator).position,
    resolution = 611.4962262812505 / 2,
    crs = webMercator,
    centerPoint,
    tileScheme = TileScheme.default,
    animationTime = 300,
    changeEndDelay = 300,
    minResolution = -1,
    maxResolution = -1,
    layers = []
  } = {}) {
    super(layers);
    this._animationStopped = true;
    this._animationTarget = null;
    this._crs = crs;
    this._position = centerPoint ? centerPoint.projectTo(crs).position : position;
    this._resolution = resolution;
    this.tileScheme = tileScheme;
    this.animationTime = animationTime;
    this.changeEndDelay = changeEndDelay;
    this._minResolution = minResolution;
    this._maxResolution = maxResolution;

    this._listenForBboxChange();
  }

  _listenForBboxChange() {
    this.on(BboxChangeEvent.type, debounce(this._fireBboxChangeEnd.bind(this), this.changeEndDelay));
  }

  _fireBboxChangeEnd() {
    this.fire(new BboxChangeEndEvent());
  }
  /**
   * Moves the map position by the specified offset
   * @param dx - Offset along X axis in map coordinates, positive direction is right
   * @param dy - Offset along Y axis in map coordinates, positive direction is down
   */


  move(dx, dy) {
    this._position[0] += dx;
    this._position[1] += dy;
    this.fire(new BboxChangeEvent());
  }
  /**
   * Changes the scale of map by scalingK
   * @param scalingK - Coefficient of scaling (Ex. 5 -> 5 times zoom in)
   * @param basePoint - Base point of zooming
   * @param doNotAdjust - do not adjust resolution to the round ones
   */


  changeScale(scalingK, basePoint, doNotAdjust) {
    this.setResolution(this.resolution * scalingK, basePoint, doNotAdjust);
  }
  /**
   * Changes the scale of map by scalingK with animation
   * @param scalingK - Coefficient of scaling (Ex. 5 -> 5 times zoom in)
   * @param basePoint - Base point of zooming
   */


  animateChangeScale(scalingK, basePoint) {
    this.animateSetResolution(this.resolution * scalingK, basePoint);
  }
  /**
   * Changes resolution of the map by k zoom levels. Positive values represent zoom in.
   * @param k - number of levels to zoom
   * @param basePoint - zooming base point
   */


  zoom(k, basePoint) {
    let tileScheme = this.tileScheme;
    let currResolution = this._animationTarget ? this._animationTarget[1] : this.resolution;
    let resolution;

    if (tileScheme) {
      let level = tileScheme.getLevel(currResolution) + (k > 0 ? -1 : 1);
      resolution = tileScheme.levels[level] ? tileScheme.levels[level].resolution : currResolution;
    } else {
      resolution = currResolution * Math.pow(2, -k);
    }

    resolution = Math.min(Math.max(resolution, this.minResolution || 0), this.maxResolution || Number.MAX_VALUE);
    this.animateSetResolution(resolution, basePoint);
  }
  /**
   * Changes resolution of the map so that the new resolution corresponds to an even tile scheme level. Resolution is changed with animation.
   * @param direction - if false will adjust to smaller resolution, if true - to larger
   */


  adjustResolution(direction = false) {
    let resolution = this.resolution;
    let newResolution = this.getAdjustedResolution(resolution, direction);
    let ratio = newResolution / resolution;

    if (ratio > 1.1 || ratio < 0.9) {
      this.animateSetResolution(newResolution);
    } else if (ratio > 1.0001 || ratio < 0.9999) {
      this.setResolution(newResolution);
    }
  }
  /**
   * Returns closest resolution to the given one that corresponds to an even tile scheme level.
   * @param resolution - target resolution
   * @param direction - if false will adjust to smaller resolution, if true - to larger
   */


  getAdjustedResolution(resolution, direction = false) {
    if (!this.tileScheme) return resolution;
    resolution = Math.min(Math.max(resolution, this.minResolution || 0), this.maxResolution || Number.MAX_VALUE);
    return this.tileScheme.getAdjustedResolution(resolution, direction);
  }
  /**
   * Sets new resolution to the map with animation
   * @param resolution
   * @param basePoint - Base point of zooming
   */


  animateSetResolution(resolution, basePoint) {
    let adjustedResolution = this.getAdjustedResolution(resolution);

    let newPosition = this._getScaledPosition(adjustedResolution, basePoint && this._getBase(basePoint));

    this.animateTo(newPosition, adjustedResolution);
  }
  /**
   * Changes position and resolution of the map with animation
   * @param point - target center point of the map
   * @param resolution - target resolution;
   */


  animateTo(point, resolution) {
    if (!Array.isArray(point)) point = point.projectTo(this.crs).position;
    this.stopAnimation();
    this.fire(new AnimationStartEvent());
    let originalPosition = this.centerPoint;
    let originalResolution = this.resolution;
    let dx = point[0] - originalPosition.x;
    let dy = point[1] - originalPosition.y;
    let dr = resolution - originalResolution;
    let startTime = Date.now();
    this._animationStopped = false;
    this._animationTarget = [point, resolution];
    let self = this;
    this._animationTimer = setInterval(function () {
      let time = Date.now() - startTime;

      if (time >= self.animationTime || self._animationStopped) {
        self.setPosition(point, resolution);
        self.stopAnimation();
        self.fire(new AnimationEndEvent());
      } else {
        let x = self._easeFunction(time, originalPosition.x, dx, self.animationTime);

        let y = self._easeFunction(time, originalPosition.y, dy, self.animationTime);

        let r = self._easeFunction(time, originalResolution, dr, self.animationTime);

        self.setPosition(new Point$1([x, y], self.crs), r);
      }
    }, 1000 / 60);
  }

  _getBase(basePoint) {
    return basePoint.projectTo ? basePoint.projectTo(this.crs).position : basePoint;
  }

  _getScaledPosition(newResolution, basePoint = null) {
    let position = this.position;
    let base = basePoint ? basePoint : position;
    let resolution = this.resolution;
    let scalingK = newResolution / resolution;
    return [(position[0] - base[0]) * scalingK + base[0], (position[1] - base[1]) * scalingK + base[1]];
  }
  /**
   * Stops all animations of the map
   */


  stopAnimation() {
    this._animationStopped = true;
    this._animationTarget = null;
    clearInterval(this._animationTimer);
  }

  easing(t) {
    return t < 0.5 ? 2 * t * t : (4 - 2 * t) * t - 1;
  }

  _easeFunction(t, b, c, d) {
    return b + c * this.easing(t / d);
  }
  /**
   * Sets new position and resolution to the map
   * @param point - new center point of the map
   * @param resolution - new resolution of the map
   */


  setPosition(point, resolution) {
    if (!Array.isArray(point)) point = point.projectTo(this.crs).position;
    this.prohibitEvent(BboxChangeEvent.type);
    this.position = point;
    if (resolution) this.resolution = resolution;
    this.allowEvent(BboxChangeEvent.type);
    this.fire(new BboxChangeEvent());
  }
  /**
   * Sets new resolution to the map
   * @param resolution
   * @param basePoint - Base point of zooming
   * @param doNotAdjust - do not adjust resolution to the round ones
   */


  setResolution(resolution, basePoint = null, doNotAdjust = false) {
    this.setPosition(this._getScaledPosition(resolution, basePoint && this._getBase(basePoint)), doNotAdjust ? resolution : this.getAdjustedResolution(resolution));
  }
  /**
   * Return current scale level of the map
   */


  getScaleLevel() {
    return this.tileScheme.levels.length - 1 - this.tileScheme.getLevel(this.resolution);
  }
  /**
   * Geographical position of the center of the map given in map coordinate system
   */


  get position() {
    return this._position;
  }

  set position(position) {
    this._position = position;
    this.fire(new BboxChangeEvent());
  }
  /**
   * Center point of the map
   */


  get centerPoint() {
    return new Point$1(this.position, this.crs);
  }

  set centerPoint(point) {
    this.position = point.projectTo(this.crs).position;
  }
  /**
   * Coordinate system of the map. If the value is set and old crs cannot be projected to the new one, position of the map is set to [0, 0].
   * Otherwise position is projected to the new crs.
   */


  get crs() {
    return this._crs;
  }

  set crs(crs) {
    let projection = this._crs.projectionTo(crs);

    this._crs = crs;

    if (projection) {
      this.position = projection(this.position);
    } else {
      this.position = [0, 0];
    }
  }
  /**
   * Resolution of the map. Can be any positive number.
   */


  get resolution() {
    return this._resolution;
  }

  set resolution(resolution) {
    this._resolution = resolution;
    this.fire(new BboxChangeEvent());
  }
  /**
   * Minimum allowed resolution of the map. If not set, the minimum value from the map tile scheme will be used. Must be smaller then max resolution.
   * If current resolution is smaller that the newly assigned minResolution, the current resolution will be adjusted accordingly.
   */


  get minResolution() {
    return this._minResolution > 0 ? this._minResolution : this.tileScheme && this.tileScheme.minResolution;
  }

  set minResolution(resolution) {
    if (resolution > 0) {
      let maxResolution = this.maxResolution;
      if (resolution > maxResolution) error('maxResolution cannot be less then minResolution');
    }

    this._minResolution = resolution;
    if (this.resolution < this.minResolution) this.resolution = resolution;
  }
  /**
   * Maximum allowed resolution of the map. If not set, the maximum value from the map tile scheme will be used. Must be larger then min resolution.
   * If current resolution is larger that the newly assigned maxResolution, the current resolution will be adjusted accordingly.
   */


  get maxResolution() {
    return this._maxResolution > 0 ? this._maxResolution : this.tileScheme && this.tileScheme.maxResolution;
  }

  set maxResolution(resolution) {
    if (resolution > 0) {
      let minResolution = this.minResolution;
      if (resolution < minResolution) error('maxResolution cannot be less then minResolution');
    }

    this._maxResolution = resolution;
    if (this.resolution > this.maxResolution) this.resolution = resolution;
  }

}

var Action;

(function (Action) {
  Action[Action["addPoint"] = 0] = "addPoint";
  Action[Action["movePoint"] = 1] = "movePoint";
  Action[Action["removePoint"] = 2] = "removePoint";
})(Action || (Action = {}));
/**
 * Base class for polylines and polygons.
 * @alias sGis.feature.Poly
 */


class Poly extends Feature {
  constructor(rings, properties) {
    super(properties);

    if (rings && rings.length > 0) {
      if (!Array.isArray(rings[0][0])) rings = [rings];
      this.rings = copyArray(rings);
    } else {
      this._rings = [[]];
    }
  }

  get lastAction() {
    return this._lastAction;
  }

  set lastAction(action) {
    this._lastAction = action;
  }
  /**
   * Array of contours of coordinates. The contours must be not-enclosed for both polylines and polygons (first and last points of a contour must not be same)
   */


  get rings() {
    return this._rings;
  }

  set rings(rings) {
    this._rings = rings;

    this._update();
  }
  /**
   * Adds a point to the end of the specified contour (ring).
   * @param point - point to be added. If sGis.Point is given, the point will be automatically projected to the feature coordinate system.
   * @param [ringN] - number of the ring the point will be added to. If not specified, the point will be added to the last ring.
   */


  addPoint(point, ringN) {
    if (!ringN) ringN = this._rings.length - 1;
    this._lastAction = {
      type: Action.addPoint,
      ringIndex: ringN,
      coordinates: point.position && point.projectTo ? point.projectTo(this.crs).position : point
    };
    this.setPoint(ringN, this._rings[ringN].length, point, true);
  }
  /**
   * Removes a point from the feature.
   * @param ringN - index of the ring (contour) the point will be removed from.
   * @param index - index of the point in the ring.
   */


  removePoint(ringN, index) {
    this._lastAction = {
      type: Action.removePoint,
      ringIndex: ringN,
      pointIndex: index,
      previousCoordinates: this._rings[ringN][index]
    };

    this._rings[ringN].splice(index, 1);

    if (this._rings[ringN].length === 0) {
      this.removeRing(ringN);
    }

    this._update();
  }
  /**
   * Removes a ring (contour) from the feature.
   * @param ringN - index of the ring to be removed.
   */


  removeRing(ringN) {
    this._rings.splice(ringN, 1);

    this._update();
  }

  _update() {
    this._bbox = null;
    this.redraw();
  }
  /**
   * Sets new coordinates for a contour.
   * @param ringN - index of the contour to be set. If the index is larger then the number of rings of the feature, new ring will be appended to the ring list.
   * @param ring - coordinate set of the contour.
   */


  setRing(ringN, ring) {
    ringN = Math.min(ringN, this._rings.length);
    this._rings[ringN] = ring;

    this._update();
  }
  /**
   * Sets a new value for a point in the feature.
   * @param ringN - index of the contour of the point.
   * @param pointN - index of the point in the contour.
   * @param point - new coordinates
   * @param dontRecord - dont record edit action to feature
   */


  setPoint(ringN, pointN, point, dontRecord) {
    pointN = Math.min(pointN, this._rings[ringN].length);

    if (!dontRecord) {
      this._lastAction = {
        type: Action.movePoint,
        ringIndex: ringN,
        pointIndex: pointN,
        previousCoordinates: this._rings[ringN][pointN],
        coordinates: point.position && point.projectTo ? point.projectTo(this.crs).position : point
      };
    }

    this._rings[ringN][pointN] = point.position && point.projectTo ? point.projectTo(this.crs).position : point;

    this._update();
  }
  /**
   * Inserts a new point to the given position.
   * @param ringN - index of the contour the point will be inserted into.
   * @param pointN - index of the point to insert to.
   * @param point - point to be inserted
   */


  insertPoint(ringN, pointN, point) {
    this._lastAction = {
      type: Action.addPoint,
      ringIndex: ringN,
      pointIndex: pointN,
      coordinates: point.position && point.projectTo ? point.projectTo(this.crs).position : point
    };
    pointN = Math.min(pointN, this._rings[ringN].length);

    this._rings[ringN].splice(pointN, 0, [0, 0]);

    this.setPoint(ringN, pointN, point, true);
  }
  /**
   * Bounding box of the feature
   */


  get bbox() {
    if (this._bbox) return this._bbox;
    let xMin = Number.MAX_VALUE;
    let yMin = Number.MAX_VALUE;
    let xMax = -Number.MAX_VALUE;
    let yMax = -Number.MAX_VALUE;

    this._rings.forEach(ring => {
      ring.forEach(point => {
        xMin = Math.min(xMin, point[0]);
        yMin = Math.min(yMin, point[1]);
        xMax = Math.max(xMax, point[0]);
        yMax = Math.max(yMax, point[1]);
      });
    });

    this._bbox = new Bbox([xMin, yMin], [xMax, yMax], this.crs);
    return this._bbox;
  }

  get centroid() {
    let bbox = this.bbox;
    let x = (bbox.xMin + bbox.xMax) / 2;
    let y = (bbox.yMin + bbox.yMax) / 2;
    return [x, y];
  }
  /**
   * @deprecated
   */


  get coordinates() {
    return copyArray(this._rings);
  }

  set coordinates(rings) {
    this.rings = copyArray(rings);
  }

}

/**
 * Symbol of polygon with one color filling.
 * @alias sGis.symbol.polygon.Simple
 */

class PolygonSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Fill color of the polygon. Can be any valid css color string. */

    this.fillColor = 'transparent';
    /** Stroke color of the outline. Can be any valid css color string. */

    this.strokeColor = 'black';
    /** Stroke width of the outline. */

    this.strokeWidth = 1;
    /** @see [[PolyRender.lineDash]] */

    this.lineDash = [];
    /** Emulation CanvasRenderingContext2D.filter shadow. */

    this.shadow = null;
    /** @see [[PolyRender.lineCap]] */

    this.lineCap = "round";
    /** @see [[PolyRender.lineJoin]] */

    this.lineJoin = "round";
    /** @see [[PolyRender.miterLimit]] */

    this.miterLimit = 10;
    /** Drawn stroke outside. */

    this.isOutsideStroke = false;
    /** Drawn stroke inside. */

    this.isInsideStroke = false;
    Object.assign(this, options);
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof Poly)) return [];
    let coordinates = getRenderedPolygonCoordinates(feature, resolution, crs);
    return [new PolyRender(coordinates, {
      enclosed: true,
      fillStyle: FillStyle.Color,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      fillColor: this.fillColor,
      lineDash: this.lineDash,
      shadow: this.shadow,
      lineCap: this.lineCap,
      lineJoin: this.lineJoin,
      miterLimit: this.miterLimit,
      isOutsideStroke: this.isOutsideStroke,
      isInsideStroke: this.isInsideStroke
    })];
  }

}
registerSymbol(PolygonSymbol, 'polygon.Simple', ['fillColor', 'strokeColor', 'strokeWidth', 'lineDash', 'shadow', 'lineCap', 'lineJoin', 'miterLimit', 'isOutsideStroke', 'isInsideStroke']);

/**
 * Polygon with one or more contours (rings). Coordinates in the contours must not be enclosed (first and last points must not be same).
 * @alias sGis.feature.Polygon
 */

class Polygon extends Poly {
  constructor(rings, _a = {}) {
    var {
      symbol = new PolygonSymbol()
    } = _a,
        params = __rest(_a, ["symbol"]);

    super(rings, Object.assign({
      symbol
    }, params));
    this.isEnclosed = true;
  }
  /**
   * Returns a copy of the feature. Only generic properties are copied.
   */


  clone() {
    return new Polygon(this.rings, {
      crs: this.crs,
      symbol: this.originalSymbol
    });
  }

  projectTo(crs) {
    let projected = projectRings(this.rings, this.crs, crs);
    return new Polygon(projected, {
      crs: this.crs,
      symbol: this.symbol,
      persistOnMap: this.persistOnMap
    });
  }

}

/**
 * Base class for controls that create polygon feature. When the control is activated,
 * a new temporary layer is created and added to the map. The feature is drawn on that temp layer. After drawing is
 * finished, if the .activeLayer property is set, the feature is moved to the active layer.
 * @alias sGis.controls.PolyDrag
 * @fires [[DrawingBeginEvent]]
 * @fires [[DrawingFinishEvent]]
 */

class PolyDrag extends Control {
  /**
   * @param map - map the control will work with
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, {
    symbol = new PolygonSymbol(),
    activeLayer = null,
    isActive = false,
    createByClick = false
  } = {}) {
    super(map, {
      activeLayer,
      useTempLayer: true
    });
    this.createByClick = false;
    this.symbol = symbol;
    this.createByClick = createByClick;
    this._handleDragStart = this._handleDragStart.bind(this);
    this._handleDrag = this._handleDrag.bind(this);
    this._handleDragEnd = this._handleDragEnd.bind(this);
    this._handleClick = this._handleClick.bind(this);
    this._handleMousemove = this._handleMousemove.bind(this);
    this.isActive = isActive;
  }

  _activate() {
    if (this.createByClick) {
      this.map.on(sGisClickEvent.type, this._handleClick);
      this.map.on(sGisMouseMoveEvent.type, this._handleMousemove);
    } else {
      this.map.on(DragStartEvent.type, this._handleDragStart);
    }
  }

  _deactivate() {
    this._activeFeature = null;

    if (this.createByClick) {
      this.map.off(sGisClickEvent.type, this._handleClick);
      this.map.off(sGisMouseMoveEvent.type, this._handleMousemove);
    } else {
      this._removeDragListeners();

      this.map.off(DragStartEvent.type, this._handleDragStart);
    }
  }

  _handleDragStart(event) {
    this.startDrawing(event);
  }

  _handleDrag(event) {
    this.updateDrawing(event);
  }

  _handleDragEnd(event) {
    this.finishDrawing(event);
  }

  _handleClick(event) {
    if (!this._activeFeature) {
      this.startDrawing(event);
    } else {
      this.finishDrawing(event);
    }
  }

  _handleMousemove(event) {
    if (this._activeFeature) {
      this.updateDrawing(event);
    }
  }

  startDrawing(event) {
    this._activeFeature = new Polygon(this._getNewCoordinates(event.point), {
      crs: event.point.crs,
      symbol: this.symbol
    });

    this._tempLayer.add(this._activeFeature);

    if (!this.createByClick) {
      this.map.on(DragEvent.type, this._handleDrag);
      this.map.on(DragEndEvent.type, this._handleDragEnd);
    }

    this.fire(new DrawingBeginEvent());
  }

  updateDrawing(event) {
    this._activeFeature.rings = this._getUpdatedCoordinates(event.point);

    this._tempLayer.redraw();

    event.stopPropagation();
  }

  finishDrawing(event) {
    let feature = this._activeFeature;
    this._activeFeature = null;

    if (this._tempLayer && this._tempLayer.has(feature)) {
      this._tempLayer.remove(feature);
    }

    if (!this.createByClick) {
      this._removeDragListeners();
    }

    if (this.activeLayer) this.activeLayer.add(feature);
    this.fire(new DrawingFinishEvent(feature, event.browserEvent));
  }

  _removeDragListeners() {
    this.map.off(DragEvent.type, this._handleDrag);
    this.map.off(DragEndEvent.type, this._handleDragEnd);
  }
  /**
   * The feature being drawn.
   */


  get activeFeature() {
    return this._activeFeature;
  }

}

/**
 * Control for drawing circles by dragging from center to the radius.
 * @alias sGis.controls.Circle
 */

class Circle extends PolyDrag {
  constructor() {
    super(...arguments);
    /** The number of segments of the circle. The higher this number is the smoother the circle will be. */

    this.segmentNo = 36;
  }

  _getNewCoordinates(point) {
    this._centerPoint = point.position;
    return [[]];
  }

  _getUpdatedCoordinates(point) {
    if (!this._centerPoint) return [];
    let radius = Math.sqrt(Math.pow(this._centerPoint[0] - point.position[0], 2) + Math.pow(this._centerPoint[1] - point.position[1], 2));
    let angleStep = 2 * Math.PI / this.segmentNo;
    let coordinates = [];

    for (let i = 0; i < this.segmentNo; i++) {
      coordinates.push([this._centerPoint[0] + radius * Math.sin(angleStep * i), this._centerPoint[1] + radius * Math.cos(angleStep * i)]);
    }

    return [coordinates];
  }

}

/**
 * Utility class to save and restore sets of states. Used for undo/redo functions.
 */
class StateManager {
  /**
   * @param maxStates - max number of stored states
   */
  constructor(maxStates = 256) {
    this._maxStates = maxStates;
    this.clear();
  }
  /**
   * Clears all stored states.
   */


  clear() {
    this._states = [];
    this._activeState = -1;
  }
  /**
   * Saves the given state and makes it active.
   * @param state
   */


  setState(state) {
    let index = this._activeState + 1;
    this._states[index] = state;

    this._states.splice(index + 1, this._states.length);

    this._trimStates();

    this._activeState = this._states.length - 1;
  }
  /**
   * Returns current state.
   */


  getCurrentState() {
    return this._states[this._activeState] === undefined ? null : this._states[this._activeState];
  }
  /**
   * Returns previous state and makes it active. If there is no previous state, returns null.
   */


  undo() {
    if (this._activeState <= 0) return null;
    return this._states[--this._activeState];
  }
  /**
   * Returns next state and makes it active. If there is no next state, returns null.
   */


  redo() {
    if (this._activeState === this._states.length - 1) return null;
    return this._states[++this._activeState];
  }

  _trimStates() {
    while (this._states.length > this._maxStates) {
      this._states.shift();
    }
  }

}

/**
 * Control for editing point features. When activeFeature is set, the feature is becoming draggable.
 * @alias sGis.controls.PointEditor
 * @fires [[EditEvent]]
 */

class PointEditor extends Control {
  /**
   * @param map - map object the control will work with
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, {
    snappingProvider,
    snappingSymbol,
    isActive = false,
    activeLayer
  } = {}) {
    super(map, {
      useTempLayer: true,
      snappingProvider,
      snappingSymbol,
      activeLayer
    });
    this._activeFeature = null;
    this.ignoreEvents = false;
    this._debouncedSnappingHandle = debounce(this._snappingHandle.bind(this), 50);
    this._handleDragStart = this._handleDragStart.bind(this);
    this._handleDrag = this._handleDrag.bind(this);
    this._handleDragEnd = this._handleDragEnd.bind(this);
    this.isActive = isActive;
  }

  _activate() {
    if (!this._activeFeature) return;

    this._activeFeature.on(DragStartEvent.type, this._handleDragStart);

    this._activeFeature.on(DragEvent.type, this._handleDrag);

    this._activeFeature.on(DragEndEvent.type, this._handleDragEnd);
  }

  _deactivate() {
    if (!this._activeFeature) return;

    this._activeFeature.off(DragStartEvent.type, this._handleDragStart);

    this._activeFeature.off(DragEvent.type, this._handleDrag);

    this._activeFeature.off(DragEndEvent.type, this._handleDragEnd);
  }
  /**
   * Point to drag. If set to null, the control is deactivated.
   */


  get activeFeature() {
    return this._activeFeature;
  }

  set activeFeature(feature) {
    this.deactivate();
    this._activeFeature = feature;
    if (feature) this.activate();
  }

  _handleDragStart(event) {
    if (this.ignoreEvents || !this._activeFeature) return;
    let dragStartEvent = event;
    dragStartEvent.draggingObject = this._activeFeature;
    event.stopPropagation();
  }

  _handleDrag(event) {
    if (!this._activeFeature) return;
    let dragEvent = event;
    this._activeFeature.position = dragEvent.point.position;
    if (this.activeLayer) this.activeLayer.redraw();

    this._unsnap();

    this._debouncedSnappingHandle(dragEvent);
  }

  _snappingHandle(event) {
    if (!this._activeFeature) return;

    const snappingResult = this._snap(event.point.position, event.browserEvent.altKey);

    Promise.resolve(snappingResult).then(point => {
      this._activeFeature.position = point || event.point.position;
      if (this.activeLayer) this.activeLayer.redraw();
    });
  }

  _handleDragEnd() {
    this.fire(new EditEvent());
  }

}

/**
 * Snaps to the given base point.
 */

const emptySnapping = ({
  position
}) => {
  return position;
};
/**
 * Snaps to the closest point among given relevant points and contour vertexes.
 */

const vertexSnapping = ({
  position,
  data,
  snappingDistance
}) => {
  let minSqDist = snappingDistance * snappingDistance;
  let snappingPoint = null;
  data.points.forEach(point => {
    const currSqDist = squareDistance(position, point);

    if (currSqDist < minSqDist) {
      snappingPoint = point;
      minSqDist = currSqDist;
    }
  });
  return snappingPoint;
};
/**
 * Snaps to the closest point on any edge given in data.lines parameter.
 */

const lineSnapping = ({
  position,
  data,
  snappingDistance
}) => {
  let snappingPoint = null;
  let currDistanceSq = snappingDistance * snappingDistance;
  data.lines.forEach(contour => {
    for (let i = 1; i < contour.length; i++) {
      const projectedPoint = pointToLineProjection(position, [contour[i - 1], contour[i]]);
      let minX = Math.min(contour[i - 1][0], contour[i][0]);
      let maxX = Math.max(contour[i - 1][0], contour[i][0]);
      let minY = Math.min(contour[i - 1][1], contour[i][1]);
      let maxY = Math.max(contour[i - 1][1], contour[i][1]);
      if (projectedPoint[0] < minX || projectedPoint[0] > maxX || projectedPoint[1] < minY || projectedPoint[1] > maxY) continue;
      const distanceSq = squareDistance(projectedPoint, position);

      if (distanceSq < currDistanceSq) {
        currDistanceSq = distanceSq;
        snappingPoint = projectedPoint;
      }
    }
  });
  return snappingPoint;
};
/**
 * Snaps to a closest middle point on the lines given in data.lines parameter.
 */

const midPointSnapping = ({
  position,
  data,
  snappingDistance
}) => {
  let snappingPoint = null;
  let currDistanceSq = snappingDistance * snappingDistance;
  data.lines.forEach(contour => {
    for (let i = 1; i < contour.length; i++) {
      const midX = (contour[i - 1][0] + contour[i][0]) / 2;
      const midY = (contour[i - 1][1] + contour[i][1]) / 2;
      const distanceSq = squareDistance([midX, midY], position);

      if (distanceSq < currDistanceSq) {
        currDistanceSq = distanceSq;
        snappingPoint = [midX, midY];
      }
    }
  });
  return snappingPoint;
};
/**
 * In case a contour is being edited, this method takes two edges in the active contour adjusted to the active point
 * (that is point being edited), and then tries to find such a snapping point so that one of the edges (or both of them)
 * are either vertical or horizontal.
 */

const axisSnapping = ({
  position,
  data,
  snappingDistance,
  activeContour,
  activeIndex = -1,
  isEnclosed = false
}) => {
  if (!activeContour || activeIndex < 0 || activeContour.length < 2) return null;
  const lines = [];
  const lastIndex = activeContour.length - 1;

  if (activeIndex === 0 && isEnclosed) {
    lines.push([activeContour[0], activeContour[lastIndex]]);
  }

  if (activeIndex !== 0) {
    lines.push([activeContour[activeIndex], activeContour[activeIndex - 1]]);
  }

  if (activeIndex !== lastIndex) {
    lines.push([activeContour[activeIndex], activeContour[activeIndex + 1]]);
  }

  if (activeIndex === lastIndex && isEnclosed) {
    lines.push([activeContour[lastIndex], activeContour[0]]);
  }

  const basePoints = [];

  for (let i = 0; i < lines.length; i++) {
    for (let axis = 0; axis < 2; axis++) {
      let projection = [lines[i][axis][0], lines[i][(axis + 1) % 2][1]];

      if (Math.abs(projection[0] - position[0]) < snappingDistance && Math.abs(projection[1] - position[1]) < snappingDistance) {
        basePoints[(axis + 1) % 2] = lines[i][1][(axis + 1) % 2];
        break;
      }
    }
  }

  if (basePoints.length > 0) {
    return [basePoints[0] === undefined ? position[0] : basePoints[0], basePoints[1] === undefined ? position[1] : basePoints[1]];
  }

  return null;
};
/**
 * In case a contour is being edited, this method takes two edges in the active contour adjusted to the active point
 * (that is point being edited), and then tries to find such a snapping point so that one of the edges (or both of them)
 * is orthogonal to its neighbours or each other.
 */

const orthogonalSnapping = ({
  position,
  data,
  snappingDistance,
  activeContour,
  activeIndex = -1,
  isEnclosed = false
}) => {
  if (!activeContour || activeIndex < 0 || activeContour.length < 3) return null;
  const lines = [];
  const contourLength = activeContour.length;

  if (isEnclosed) {
    lines.push([activeContour[(activeIndex + 1) % contourLength], activeContour[(activeIndex + 2) % contourLength]]);
    lines.push([activeContour[(contourLength + activeIndex - 1) % contourLength], activeContour[(contourLength + activeIndex - 2) % contourLength]]);
  } else {
    if (activeIndex >= 2) lines.push([activeContour[activeIndex - 1], activeContour[activeIndex - 2]]);
    if (activeIndex <= contourLength - 3) lines.push([activeContour[activeIndex + 1], activeContour[activeIndex + 2]]);
  }

  let basePoint = position;

  for (let i = 0; i < lines.length; i++) {
    let projection = pointToLineProjection(basePoint, lines[i]);
    let dx = projection[0] - lines[i][0][0];
    let dy = projection[1] - lines[i][0][1];

    if (Math.abs(dx) < snappingDistance && Math.abs(dy) < snappingDistance) {
      basePoint = [basePoint[0] - dx, basePoint[1] - dy];
      let direction = i === 0 ? 1 : -1;
      let nextPoint = isEnclosed ? activeContour[(contourLength + activeIndex + direction) % contourLength] : activeContour[activeIndex + direction];
      let prevPoint = isEnclosed ? activeContour[(contourLength + activeIndex - direction) % contourLength] : activeContour[activeIndex - direction];

      if (nextPoint && prevPoint) {
        projection = pointToLineProjection(prevPoint, [activeContour[activeIndex], nextPoint]);

        if (Math.abs(projection[0] - basePoint[0]) < snappingDistance && Math.abs(projection[1] - basePoint[1]) < snappingDistance) {
          basePoint = projection;
        }
      }
    }
  }

  if (basePoint[0] === position[0] && basePoint[1] === position[1]) return null;
  return basePoint;
};

/**
 * Base functionality for snapping based on selected [[SnappingMethod]]s and snapping distance.
 */

class SnappingProviderBase {
  /**
   * @param map - working map of the control that uses snapping.
   * @param __namedParams - snapping parameters.
   */
  constructor(map, {
    snappingDistance = 7,
    snappingMethods = [vertexSnapping, midPointSnapping, lineSnapping, orthogonalSnapping, axisSnapping]
  } = {}) {
    this._map = map;
    this.snappingDistance = snappingDistance;
    this.snappingMethods = snappingMethods;
  }

  getSnappingPoint(position, activeContour, activeIndex, isEnclosed) {
    const data = this._getSnappingData(position);

    const snappingDistance = this.snappingDistance * this._map.resolution;

    for (let i = 0; i < this.snappingMethods.length; i++) {
      const snappingPoint = this.snappingMethods[i]({
        position,
        data,
        snappingDistance,
        activeContour,
        activeIndex,
        isEnclosed
      });
      if (snappingPoint) return snappingPoint;
    }

    return null;
  }

}

/**
 * Provides snapping to points, lines and polygons in a [[FeatureLayer]].
 */

class FeatureLayerSnappingProvider extends SnappingProviderBase {
  /**
   * @param map - working map of the control that uses snapping.
   * @param layer - the layer that contains the features to be snapped to.
   * @param params - snapping parameters.
   */
  constructor(map, layer, params = {}) {
    super(map, params);
    this._layer = layer;
  }

  _getSnappingData(point) {
    const currentPoint = new Point$1(point, this._map.crs);
    let bbox = Bbox.fromPoint(currentPoint, this.snappingDistance * this._map.resolution);

    let features = this._layer.getFeatures(bbox, this._map.resolution);

    let snappingData = {
      points: [],
      lines: []
    };
    features.forEach(feature => {
      if (feature instanceof PointFeature) {
        snappingData.points.push(feature.position);
      } else if (feature instanceof Poly) {
        FeatureLayerSnappingProvider.setPolyData(feature, snappingData);
      }
    });
    snappingData.points = snappingData.points.filter(dataPoint => !currentPoint.equals(new Point$1(dataPoint, this._map.crs)));
    snappingData.lines = snappingData.lines.filter(line => {
      const lineStart = new Point$1(line[0], this._map.crs);
      const lineEnd = new Point$1(line[1], this._map.crs);
      return !(currentPoint.equals(lineStart) || currentPoint.equals(lineEnd));
    });
    return snappingData;
  }
  /**
   * For the given snapping data object, it adds the snapping lines
   * @param poly - line or polygon to be snapped to
   * @param data - snapping data to modify
   */


  static setPolyData(poly, data) {
    const isPolygon = poly instanceof Polygon;
    poly.rings.forEach(ring => {
      ring.forEach(point => {
        data.points.push(point);
      });

      for (let i = 1; i < ring.length; i++) {
        data.lines.push([ring[i - 1], ring[i]]);
      }

      if (isPolygon) data.lines.push([ring[ring.length - 1], ring[0]]);
    });
  }

  clone() {
    return new FeatureLayerSnappingProvider(this._map, this._layer, {
      snappingDistance: this.snappingDistance,
      snappingMethods: this.snappingMethods
    });
  }

}

/**
 * This provider allows to find snapping points on a single polygon or polyline.
 */

class PolySnappingProvider extends SnappingProviderBase {
  /**
   * @param map - working map of the control that uses snapping.
   * @param options - snapping parameters.
   */
  constructor(map, _a = {}) {
    var {
      feature = null
    } = _a,
        params = __rest(_a, ["feature"]);

    super(map, params);
    this.feature = feature;
  }

  _getSnappingData(point) {
    let snappingData = {
      points: [],
      lines: []
    };
    if (this.feature) FeatureLayerSnappingProvider.setPolyData(this.feature, snappingData);
    return snappingData;
  }

  clone() {
    const result = new PolySnappingProvider(this._map, {
      snappingDistance: this.snappingDistance,
      snappingMethods: this.snappingMethods
    });
    result.feature = this.feature;
    return result;
  }

}

/**
 * Control for editing polyline and polygon features. When activeFeature is set, the feature becomes draggable.
 * If a vertex is dragged, the vertex position is changed. If a side is dragged, a new point is added to the side and
 * then being dragged. If inside area of the polygon is dragged, the whole polygon will change position.
 * @alias sGis.controls.PolyEditor
 * @fires [[EditEvent]]
 * @fires [[ChangeEvent]]
 */

class PolyEditor extends Control {
  /**
   * @param map - map object the control will work with
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, _a = {}) {
    var {
      isActive = false,
      onFeatureRemove = null,
      hoverSnappingProvider
    } = _a,
        controlParams = __rest(_a, ["isActive", "onFeatureRemove", "hoverSnappingProvider"]);

    super(map, Object.assign({
      useTempLayer: true
    }, controlParams));
    /** Distance from a vertex in pixels that will be considered as inside of the vertex. If the cursor is in this range from */

    this.vertexSize = 7;
    /** If user tries to remove the last point of the feature, the control will not remove it but will call this callback */

    this.onFeatureRemove = null;
    /** If set to false it will be not possible to change the shape of the feature. */

    this.vertexChangeAllowed = true;
    /** If set to false it will be not possible to move the feature as whole. */

    this.featureDragAllowed = true;
    this.ignoreEvents = false;
    this._activeRing = null;
    this._activeIndex = null;
    this._debouncedSnappingHandle = debounce(this._snappingHandle.bind(this), 50);
    this.onFeatureRemove = onFeatureRemove;
    this._handleMousemove = this._handleMousemove.bind(this);
    this._handleDragStart = this._handleDragStart.bind(this);
    this._handleDrag = this._handleDrag.bind(this);
    this._handleDragEnd = this._handleDragEnd.bind(this);
    this._handleDblClick = this._handleDblClick.bind(this);

    if (hoverSnappingProvider === undefined) {
      this.hoverSnappingProvider = new PolySnappingProvider(map, {
        snappingMethods: [vertexSnapping, lineSnapping]
      });
    }

    this.isActive = isActive;
  }

  _activate() {
    if (!this._activeFeature) return;
    this.map.on(sGisMouseMoveEvent.type, this._handleMousemove);

    this._activeFeature.on(DragStartEvent.type, this._handleDragStart);

    this._activeFeature.on(DragEvent.type, this._handleDrag);

    this._activeFeature.on(DragEndEvent.type, this._handleDragEnd);

    this._activeFeature.on(sGisDoubleClickEvent.type, this._handleDblClick);

    this.hoverSnappingProvider.feature = this._activeFeature;
  }

  _deactivate() {
    if (!this._activeFeature) return;
    this.map.off(sGisMouseMoveEvent.type, this._handleMousemove);

    this._activeFeature.off(DragStartEvent.type, this._handleDragStart);

    this._activeFeature.off(DragEvent.type, this._handleDrag);

    this._activeFeature.off(DragEndEvent.type, this._handleDragEnd);

    this._activeFeature.off(sGisDoubleClickEvent.type, this._handleDblClick);
  }
  /**
   * Feature to edit. If set to null, the control is deactivated.
   */


  get activeFeature() {
    return this._activeFeature;
  }

  set activeFeature(feature) {
    this.deactivate();
    this._activeFeature = feature;
    if (feature) this.activate();
  }

  _handleMousemove(event) {
    if (this.ignoreEvents) {
      this._unsnap();

      return;
    }

    this._snap(event.point.position, event.browserEvent.altKey, undefined, undefined, undefined, this.hoverSnappingProvider);
  }

  _getProjectedPoint(position, fromCrs) {
    return new Point$1(position, fromCrs).projectTo(this.map.crs).position;
  }

  _handleDragStart(event) {
    if (this.ignoreEvents || !this.vertexChangeAllowed && !this.featureDragAllowed) return;

    let [ringIndex, pointIndex] = this._getTargetPoint(event.point);

    if (ringIndex === null) {
      [ringIndex, pointIndex] = this._getTargetEdge(event.point);

      if (ringIndex !== null) {
        pointIndex++;

        this._activeFeature.insertPoint(ringIndex, pointIndex, event.point.position);
      }
    }

    this._activeRing = ringIndex;
    this._activeIndex = pointIndex;

    if (this._activeRing !== null || this.featureDragAllowed) {
      event.draggingObject = this._activeFeature;
      event.stopPropagation();
    }
  }

  _getTargetPoint(point) {
    if (!this.activeFeature) return [null, null];
    let closestDistanceSq = this.vertexSize * this.vertexSize * this.map.resolution * this.map.resolution;
    let closestRingIndex = null;
    let closestPointIndex = null;
    this.activeFeature.rings.forEach((ring, ringIndex) => {
      ring.forEach((coord, pointIndex) => {
        let projected = this._getProjectedPoint(coord, this.map.crs);

        let distanceSq = squareDistance(projected, point.position);

        if (distanceSq < closestDistanceSq) {
          closestDistanceSq = distanceSq;
          closestRingIndex = ringIndex;
          closestPointIndex = pointIndex;
        }
      });
    });
    return [closestRingIndex, closestPointIndex];
  }

  _getTargetEdge(point) {
    if (!this.activeFeature) return [null, null];
    let closestDistanceSq = this.vertexSize * this.vertexSize * this.map.resolution * this.map.resolution;
    let closestRingIndex = null;
    let closestEdgeIndex = null;
    this.activeFeature.rings.forEach((ring, ringIndex) => {
      ring.forEach((coord, pointIndex) => {
        let projectedA = this._getProjectedPoint(coord, this.map.crs);

        let projectedB = this._getProjectedPoint(pointIndex === ring.length - 1 ? ring[0] : ring[pointIndex + 1], this.map.crs);

        let distanceSq = pointToLineDistanceSquare(point.position, [projectedA, projectedB]);

        if (distanceSq < closestDistanceSq) {
          closestDistanceSq = distanceSq;
          closestRingIndex = ringIndex;
          closestEdgeIndex = pointIndex;
        }
      });
    });
    return [closestRingIndex, closestEdgeIndex];
  }

  _handleDrag(event) {
    if (this._activeRing === null) return this._handleFeatureDrag(event);

    this._activeFeature.setPoint(this._activeRing, this._activeIndex, event.point.position);

    this._activeFeature.redraw();

    if (this.activeLayer) this.activeLayer.redraw();
    this.fire(new ChangeEvent(this._activeRing, this._activeIndex));

    this._unsnap();

    this._debouncedSnappingHandle(event, this._activeRing, this._activeIndex);
  }

  _snappingHandle(event, activeRing, activeIndex) {
    if (!this._activeFeature) return;

    const snappingResult = this._snap(event.point.position, event.browserEvent.altKey, this._activeFeature.rings[activeRing], activeIndex, this._activeFeature instanceof Polygon);

    Promise.resolve(snappingResult).then(point => {
      this._activeFeature.setPoint(activeRing, activeIndex, point || event.point.position);

      this._activeFeature.redraw();

      if (this.activeLayer) this.activeLayer.redraw();
      this.fire(new ChangeEvent(activeRing, activeIndex));
    });
  }

  get isDraggingVertex() {
    return this._activeRing !== null;
  }

  _handleDragEnd() {
    this._activeRing = null;
    this._activeIndex = null;
    this.fire(new EditEvent());
  }

  _handleFeatureDrag(event) {
    move([this._activeFeature], [-event.offset[0], -event.offset[1]], this.map.crs);

    this._activeFeature.redraw();

    if (this.activeLayer) this.activeLayer.redraw();
    this.fire(new ChangeEvent());
  }

  _handleDblClick(event) {
    if (this.ignoreEvents) return;

    let [ringIndex, pointIndex] = this._getTargetPoint(event.point);

    if (ringIndex === null) return;
    let ring = this._activeFeature.rings[ringIndex];
    let evPoint = event.point.projectTo(this._activeFeature.crs).position;
    let d1 = distance(evPoint, ring[pointIndex]);
    let nextIndex = (pointIndex + 1) % ring.length;
    let d2 = distance(evPoint, ring[nextIndex]);
    if (d2 < d1) pointIndex = nextIndex;

    if (ring.length > 2) {
      this._activeFeature.removePoint(ringIndex, pointIndex);

      this.fire('edit', {
        ringIndex: ringIndex,
        pointIndex: pointIndex
      });
    } else if (this._activeFeature.rings.length > 1) {
      this._activeFeature.removeRing(ringIndex);

      this.fire('edit', {
        ringIndex: ringIndex,
        pointIndex: pointIndex
      });
    } else if (this.onFeatureRemove) {
      this.onFeatureRemove();
    }

    this._unsnap();

    if (this.activeLayer) this.activeLayer.redraw();
    event.stopPropagation();
  }

}

function distance(p1, p2) {
  return Math.sqrt((p1[0] - p2[0]) * (p1[0] - p2[0]) + (p1[1] - p2[1]) * (p1[1] - p2[1]));
}

/**
 * Symbol of point drawn as a square.
 * @alias sGis.symbol.point.Square
 */

class SquareSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Size of the square. */

    this.size = 10;
    /**
     * Clockwise rotation of the square in radians.
     */

    this.angle = 0;
    this._offset = [0, 0];
    /** Color of the inner part of the square. Can be any valid css color string. */

    this.fillColor = 'black';
    /** Color of the outline of the square. Can be any valid css color string. */

    this.strokeColor = 'transparent';
    /** Width of the outline. */

    this.strokeWidth = 1;
    Object.assign(this, options);
  }
  /** Offset of the point from the feature position. If set to [0, 0], center of the circle will be at the position of the feature. */


  get offset() {
    return this._offset;
  }

  set offset(value) {
    // TODO: remove deprecated part after 2018
    let deprecated = value;

    if (deprecated.x !== undefined && deprecated.y !== undefined) {
      warn('Using anchorPoint in {x, y} format is deprecated. Use [x, y] format instead.');
      this._offset = [deprecated.x, deprecated.y];
    } else {
      this._offset = value;
    }
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof PointFeature)) return [];
    const position = feature.projectTo(crs).position;
    const pxPosition = [position[0] / resolution, -position[1] / resolution];
    const halfSize = this.size / 2;
    const offset = this.offset;
    const angle = -this.angle || 0;
    const [x, y] = [halfSize + offset[0], halfSize + offset[1]];
    const [cx, cy] = pxPosition;
    const coordinates = [[rotate$1(cx, cy, cx - x, cy - y, angle), rotate$1(cx, cy, cx - x, cy + y, angle), rotate$1(cx, cy, cx + x, cy + y, angle), rotate$1(cx, cy, cx + x, cy - y, angle)]];
    return [new PolyRender(coordinates, {
      fillColor: this.fillColor,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      enclosed: true,
      fillStyle: FillStyle.Color
    })];
  }

}
registerSymbol(SquareSymbol, 'point.Square', ['size', 'offset', 'fillColor', 'strokeColor', 'strokeWidth', 'angle']);

/**
 * @event RotationStartEvent
 */

class RotationStartEvent extends sGisEvent {
  constructor() {
    super(RotationStartEvent.type);
  }

}
RotationStartEvent.type = 'rotationStart';
/**
 * @event RotationEndEvent
 */

class RotationEndEvent extends sGisEvent {
  constructor() {
    super(RotationEndEvent.type);
  }

}
RotationEndEvent.type = 'rotationEnd';
/**
 * @event ScalingStartEvent
 */

class ScalingStartEvent extends sGisEvent {
  constructor() {
    super(ScalingStartEvent.type);
  }

}
ScalingStartEvent.type = 'scalingStart';
/**
 * @event ScalingEndEvent
 */

class ScalingEndEvent extends sGisEvent {
  constructor() {
    super(ScalingEndEvent.type);
  }

}
ScalingEndEvent.type = 'scalingEnd';
/**
 * Control for modifying polylines or polygons as whole. When activeFeature is set, it shows points around the feature
 * dragging which one can scale or rotate the feature.
 * @alias sGis.controls.PolyTransform
 * @fires [[RotationStartEvent]]
 * @fires [[RotationEndEvent]]
 * @fires [[ScalingStartEvent]]
 * @fires [[ScalingEndEvent]]
 */

class PolyTransform extends Control {
  /**
   * @param map - map object the control will work with
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, {
    activeLayer = null,
    isActive = false
  } = {}) {
    super(map, {
      activeLayer,
      useTempLayer: true
    });
    /** Symbol of the rotation handle. */

    this.rotationHandleSymbol = new PointSymbol({
      offset: [0, -30]
    });
    /** Symbol of the scaling handles. */

    this.scaleHandleSymbol = new SquareSymbol({
      fillColor: 'transparent',
      strokeColor: 'black',
      strokeWidth: 2,
      size: 7
    });
    /** Distance in pixels between scaling handles and feature bbox. */

    this.scaleHandleOffset = 12;
    /** If set to false the rotation handle will not be displayed. */

    this.enableRotation = true;
    /** If set to false the scaling handle will not be displayed. */

    this.enableScaling = true;
    this.ignoreEvents = false;
    this._handleRotationStart = this._handleRotationStart.bind(this);
    this._handleRotation = this._handleRotation.bind(this);
    this._handleRotationEnd = this._handleRotationEnd.bind(this);
    this._handleScalingEnd = this._handleScalingEnd.bind(this);
    this.isActive = isActive;
  }
  /**
   * Feature to edit. If set to null, the control is deactivated.
   */


  get activeFeature() {
    return this._activeFeature;
  }

  set activeFeature(feature) {
    this.deactivate();
    this._activeFeature = feature;
    if (feature) this.activate();
  }
  /**
   * Updates position of the editor handles.
   */


  update() {
    if (this._activeFeature) this._updateHandles();
  }

  _activate() {
    if (!this._activeFeature) return;

    this._setHandles();
  }

  _deactivate() {
    return;
  }

  _setHandles() {
    if (this.enableRotation) this._setRotationHandle();
    if (this.enableScaling) this._setScaleHandles();
  }

  _setRotationHandle() {
    this._rotationHandle = new PointFeature([0, 0], {
      crs: this.map.crs,
      symbol: this.rotationHandleSymbol
    });

    this._updateRotationHandle();

    this._rotationHandle.on(DragStartEvent.type, this._handleRotationStart);

    this._rotationHandle.on(DragEvent.type, this._handleRotation);

    this._rotationHandle.on(DragEndEvent.type, this._handleRotationEnd);

    this._tempLayer.add(this._rotationHandle);
  }

  getScaleHandleSymbol(index) {
    return this.scaleHandleSymbol.clone();
  }

  _setScaleHandles() {
    this._scaleHandles = [];

    for (let i = 0; i < 9; i++) {
      if (i === 4) continue;
      let symbol = this.getScaleHandleSymbol(i);
      let xk = i % 3 - 1;
      let yk = 1 - Math.floor(i / 3);
      symbol.offset = [this.scaleHandleOffset * xk, this.scaleHandleOffset * yk];
      this._scaleHandles[i] = new PointFeature([0, 0], {
        symbol: symbol,
        crs: this.map.crs
      });

      this._scaleHandles[i].on(DragStartEvent.type, this._handleScalingStart.bind(this, i));

      this._scaleHandles[i].on(DragEvent.type, this._handleScaling.bind(this, i));

      this._scaleHandles[i].on(DragEndEvent.type, this._handleScalingEnd);
    }

    this._tempLayer.add(this._scaleHandles);

    this._updateScaleHandles();
  }

  _handleRotationStart(event) {
    if (this.ignoreEvents) return;
    this._rotationBase = this._activeFeature.bbox.projectTo(this.map.crs).center;
    event.draggingObject = this._rotationHandle;
    event.stopPropagation();
    this.fire(new RotationStartEvent());
  }

  _handleRotation(event) {
    let xPrev = event.point.x + event.offset[0];
    let yPrev = event.point.y + event.offset[1];
    let alpha1 = xPrev === this._rotationBase[0] ? Math.PI / 2 : Math.atan2(yPrev - this._rotationBase[1], xPrev - this._rotationBase[0]);
    let alpha2 = event.point.x === this._rotationBase[0] ? Math.PI / 2 : Math.atan2(event.point.y - this._rotationBase[1], event.point.x - this._rotationBase[0]);
    let angle = alpha2 - alpha1;
    rotate([this._activeFeature], angle, this._rotationBase, this.map.crs);
    if (this.activeLayer) this.activeLayer.redraw();

    this._updateHandles();
  }

  _handleRotationEnd() {
    this.fire(new RotationEndEvent());
  }

  _updateHandles() {
    if (this.enableRotation) this._updateRotationHandle();
    if (this.enableScaling) this._updateScaleHandles();

    this._tempLayer.redraw();
  }

  _updateRotationHandle() {
    let bbox = this._activeFeature.bbox.projectTo(this.map.crs);

    this._rotationHandle.position = [(bbox.xMin + bbox.xMax) / 2, bbox.yMax];
  }

  _updateScaleHandles() {
    let bbox = this._activeFeature.bbox.projectTo(this.map.crs);

    let xs = [bbox.xMin, (bbox.xMin + bbox.xMax) / 2, bbox.xMax];
    let ys = [bbox.yMin, (bbox.yMin + bbox.yMax) / 2, bbox.yMax];

    for (let i = 0; i < 9; i++) {
      if (i === 4) continue;
      this._scaleHandles[i].position = [xs[i % 3], ys[Math.floor(i / 3)]];
    }
  }

  _handleScalingStart(index, event) {
    if (this.ignoreEvents) return;
    event.draggingObject = this._scaleHandles[index];
    event.stopPropagation();
    this.fire(new ScalingStartEvent());
  }

  _handleScaling(index, event) {
    const MIN_SIZE = 10;
    let xIndex = index % 3;
    let yIndex = Math.floor(index / 3);
    let baseX = xIndex === 0 ? 2 : xIndex === 2 ? 0 : 1;
    let baseY = yIndex === 0 ? 2 : yIndex === 2 ? 0 : 1;
    let basePoint = this._scaleHandles[baseX + 3 * baseY].position;

    let bbox = this._activeFeature.bbox.projectTo(this.map.crs);

    let resolution = this.map.resolution;
    let tolerance = MIN_SIZE * resolution;
    let width = bbox.width;
    let xScale = baseX === 1 ? 1 : (width + (baseX - 1) * event.offset[0]) / width;
    if (width < tolerance && xScale < 1) xScale = 1;
    let height = bbox.height;
    let yScale = baseY === 1 ? 1 : (height + (baseY - 1) * event.offset[1]) / height;
    if (height < tolerance && yScale < 1) yScale = 1;
    scale([this._activeFeature], [xScale, yScale], basePoint, this.map.crs);
    if (this.activeLayer) this.activeLayer.redraw();

    this._updateHandles();
  }

  _handleScalingEnd() {
    this.fire(new ScalingEndEvent());
  }

}

const PIN_IMAGE = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEQAAABECAYAAAA4E5OyAAAOVUlEQVR4Xu1cZ3RU1Rb+9kwyLWEm' + 'IYkhoUgQIgFUXKEEgbwgRUBAeIBSFAuIir0BPkQfCIrYQUFdKAj6RBBBH0VANFI1NEGaCASlhEDapGeSmfPWvpmUSbtn7gz4frjXyo/k7rPPPt89' + 'Z9/dTgh/kwcCdKXwEEKYAHQF0ANAOwCxAFoCCAJgdutRBKAAwGkAxwEcAbANQAoRFV8JXS8rIEIIC4AhAMYA6AfAqHFRJQA2AvgcwDdEVKhRjuqw' + 'ywKIECISwGMAJgEIYS0EXMgtPYHMkl+Q7TiMgrIzKCg7izJXAZyC1wvoyYgAXRCCApohOKAFQgztEGa8EdbAa0DQVSwmB8ACAPOIKF11hV4y+BUQ' + 'IQRv/akAJgPgI4IcxxGcKVyPs4WbUeLM9FK9cnajPgzNLP3Q3DJAAclNfIRe5R8i4qPmF/IbIEKIvgAWAWjBml0o2obf8z5RdoQ/KczYEW0a3Y0m' + '5p4VYv8EMIGINvtjHp8BEUIEApgN4BkAlFt6Egez5yKjZK8/9KtXRrgxHteHTlaOk3IigdcBTCOiUl8m9gkQIYQNwJcA+rCN+D13CY7a31fsxZUg' + 'titxtgfRxnpPhY35DsAIIrJrnV8zIG7DyZb/BocrBymZU5FRvEerHj6NCzd1QpewOTDoFPt9gL9oRHRRi1BNgLh3xnYAHQrLzmPnpUeQX8ZH+a8j' + '/irdFPEuLAHRrMQh9ne07BSvAXE7WN8D6FZQdg7bL96PIqeml+F39M6l5iIpejHat+7OsncB6EVE5d90SdICyPsAHihxZmHrxfsUX+L/gVJPn0Z2' + 'VhaK7GZM6vUzYpp3YLU+IKIHvdHPK0CEEOxxfibgxLaLE5FVwsfVd9KT4rLAKbz3zoUQOHXqFOz2KjtamBGOmWNOwWIOZrFjiIg9XCmSBkQIEQ7g' + 'GICwQzlv40Tep1IT1GQK0FkQZU7CVaYEhATGKV6pjvjLDbhEqbLjckqP4mLxT0grSkaZq34vvS4wKubTZXTHv8dzGAT2Bq8lIimv0BtA2Okan+X4' + 'FVvT73N/+uUxYW8z1novWgYNRcWOUBvNO+Z0wRocz11cy8t1uVxITU312BnV5QkBdI9YhP7dWVd8REQT1Obj51KACCHYXz4k4KTk9Ltgd3AgKk8x' + 'wSPQ3vaoEqdoIY53DtvnIzWfXR6AwTh58iTy8vIaFFeUZcOssedhNJjZcetARBw9N0iygCwFcNeZgnXYm/WimszK53wU4hvPQFMLB7q+07nCTdid' + 'MR2/nzimCkbFbM3LHsb9w+fzr0uJ6G41LVQBEUJEATgj4NJ/f+EO5JWmqslUnusoAN3C5yHC1KVe/vSMP3E2ay8KSi4oPEHGJmjWOB6R4Uo4VCel' + 'HP0C647fCTbsMlSUE4yXx2YgMMDAA5oTUVpD42QAeRbA3PTindh1iSN6OYoPm4nmloG1mB2OEny0agqOZi2FLToHVBnVl7MKF2A/H4K4xuMwfvir' + 'MBhqp1C+3j4bezOnyynCrnTQHAzvwwE4JhPRa74CwuHqDXsyp+FsIXvq6nR10BDc2PiFWox7f/0ey7aPhDU6W10IgNzzobirx0rEX3dzLf5Xl/dE' + 'gXmHlBznpTjMnnCYeQ8QUUfNgAghmgBIcwkH1p3rVZnIaUhgoM6KvlGrYdBx3FdFW3Yux8bT42AKKpNaRAVTcX4AbolZit43jfIYl5OXhte+bQW9' + 'Qd0RdZYSnhuQieAgJdaJIqLyM1oHNXhkhBCjAfyHQ/ntFx+QWkhb20S0tU704D1+aj8W7UiAyaotMi/ODcSE7j8httWNHnLfWzMK6bRCSq+EsAUY' + '2ENxWkcT0XKtgLzDqcBjuR/imP1D1Yk5HO8fvUHJcFWnZxdeA3O0nDGub5Ki8zF47aGTHo8vZqVi/tY2IFJPNzTKH4xnx37N4zn1+LhWQL4FcEtK' + '5mScL+R4rmGKMHVG94iFHkzf//Q5ktPHqg2Vep4U+RluTuBNW0XTl8WArH+ojndmxWL2vexoYyMR9dcKyCkAMfy55UyYGrWzTUKsVfEMK+nFRV0h' + 'InarDZV6Tpc6Y8aEnz145385CpcC1Y+Nw27DnHGKMU8lolZaAeEMt40NaqmrYa+QJ+ga/gaizP/wmGvKUiuMtnypBasxFduDMXdcrgfb6h/mYn8u' + '57UbJqcjELNHKgbYTkSKda2L1Iwqu7z4+kwXqbRgUuSnCDG0rTaPwPTV+lq+hpry9T1nH+WlYexfVam965fV2PDHcHWRgjBzaLkzR0T1rlsKkDVn' + 'OqlPCKB3kxVoFFi1Gx3OXMxaW+/LkJJZk+n5QTkw6K2Vf045uAFrU2+VkjXztnLje8UA6RX5GWyGayuV42Tzi2sCAVI2mu+kvGX2Y6re47a9q7D5' + '7EhV2UQ6zBhS7gP5AohiQ9aeS2wwL1GhTUL4m2hiTvRQ7vnPw6CzyHmmaqtyFYZg1ugsD7bP18/A0dIZakPhKgvErOG+25ATAK7ZlDYEnExWo3a2' + 'h5WcR3V6Y2Uf2A3qn2w12fzc5rgZT4/kSkMVvbJ4MIoar1Md7ixqhNmjlKzaSSJqXd8ANRvCK+m1/eJEZJTsU52UI9vuEVx2raKjf2zB8gP9wNkt' + 'n4gIo2/YhLire3uIeWphJIKjL6mLzmuBmXdyUwF+IKLawZFbghogHwG4b3/WTPxR8I3qpER69I/+FkZdqAfv3FVdkB/gW80muKwTJg9P8ZB76LcU' + 'LD+SAF2NiLkuRS2FN2HqaK6c4GMiGq91hzwM4N1T+SuU8qQM1XVs7AXn8Pbm9nCSpw8hI4959MKKJ/oehi2oqceQ6Qt6g5r+ICWmjelR3HULRyJ4' + 'hIje0wpIAtc3sh2H8GP6PVITc5TbN2oNAnWNPPhT01OwdFc/r0FhMMZ124SYSM9E05Hf9mHJni4wBavHMazIyOvW47pWisfejYh+0gpIAGetBVzW' + '9ed6S3mrPFHL4GHoGDqt1pyZ+SexOHkocp2c2lSzKQSrvh3uTVqDsGCloF1JTqcTj78Vi8ZtJANGlxkzhuVx/Ze3aBgR1ZuDkMmYfQVg2O7M53Cu' + 'UL7joHPYK2hq4Q4JT2LfZM+Jpdh2/E0U6U6gpMSzFmM0mmB2tUbP2KfQqfW46o0ylYJmzLsDzqtXSu1YZrKUdMLU2xX7s5qI/tnQQBlAODG7hMFg' + 'UGSJu4ESIt5GhLFzvUNKXNnILj6GwtLyRiBLYCRCTW1rGeXqAl7/8DFkhb6LAIOsJkBis3fQJ/5RHnA3EXHCvF6SAYTLX+ku4bBsON8PpS75QI1B' + '6RT2cq2AT34pVZwOhwMvvjUG1Oorr8DQCTNeuC0bOjJwxSuSiBpcgCogrJIQQilD8JeGvzje0jWNxoBTA7IFqpryU/bswAfrxqJpR+87DKL0g/HQ' + 'ICUxtIyIxqnpLgsIR3e7ucy4OW2YhEGsPa1ZH4lrrePRImgQvy01vRRHbsfOrfhiyzTomu2CLUzNCNcWqdPpMSnxEK6yKfFVZyJSdYakAHHvkmQA' + '//DWuNZUk5PQTS19cJWpq1LbNQc0UQzn7t27kW3PRuqZX3DiwjakFexEVFw2yuvV2qgx9cATQ7by4GQi6iUjxRtAktjt5caYLWkjpPIjMgpwQUtP' + 'FiQnJ8NoBnR6mVHqPHpdAB5MPIBIWxwzJxHRj+qjJGu7FYKEEOsBDDiQ/QpS81fJyJfm2bdPPVaSFsatkOahmNCPPQZsIKLaFbN6hEnvEPex4S6U' + '/Q6XPWDLhREocfonrGfZ/gQkkGx4duApmAJC2QG7kYi4xUqKvALEDcocAFO4GXdvZu3qnNSsdTD5CxDODvaLfQ/d2yo1GG7qVU+4VtNHCyDcv86t' + 'Q61/znhGaWrxB/kLEG7Geaifkn/hXM713nY5ew2Ie5fwrYbtfHR+uDDaL013/gDEpIvA0wN/g1EfwkeFuxA9axYSb04TIG5Q/sUdzJw42nlpElzC' + 'u5ptTd18BSQwwIjRndajdaTydeWO5pcl1l+LxRdAOC3DWaNbubPnQDabFu3kCyA6nQ43t34NiXFPsgJrAdxGMvXNOtTVDIh7l3A9gMPIaxmQipYn' + 'LbBoBoQIHSLvxu1dP+ZpfwPQhUhjJkq2x6yhBQoh2gDYKeAKT8mYgrQiuQyWv45MTOgA3JP4X/Z2M9zJHzammsmnHVIxqxCCY/xkpyix7Mp4XFPP' + 'u5Yd0iykByYkflcRybI36nMR2S+AuI8P5+e+cYriwF0ZT3gNireANAvpifGJGzmC5qaTwUQk196ksnf8BogbFL5f96UWULwBpAYYfB1EvSQgeYj8' + 'Ckg1UFY6RbEhJWMyuFlPhmQAYS+0VVhf3HnTGt4ZDs4d+xMM1tPvgLhBYWdgjUuUWvdnv4QzBRwTNkxqgPCntX3UKAyPX8yt4Jws5k+rf9zkaqpd' + 'FkDcoHBD2EZARByxL1DasxuihgAxGAxIaPk0+sTN4nfIZbpbiGi/Gshanl82QNygxADgwmscB4NcAazPo60PELMpGLde9z6uj+aLGMrF5kFEJFl/' + '8B6SywqIGxTuz+Suv/5ZjoNgX6XYWbsWWxcgocEtMCZhFSKD4lkU97uN0nJLyhtYLjsgblA4D8Z3bJ/mi0e7+X5ejeJ5dUDYXsSEJ2FUlxUw6huz' + 'iDc45UBEcv3c3iBQg/eKAFLNgePepyUCzuCj9oU4nvtJZcK6AhCTyYJuMU8iKXYGe59cMriHiPybnmsAsCsKiHu3cAqcaxnXXypOwd6sF1DszFAy' + 'ZmHWqzEifhmaWvn/JeAggNuJiOOTK0ZXHBA3KHynjNsJHuXqHRvbzMxM9G0zr+Kq6Tz3EfH+zpmP0P0lgFQ7QtwttwQAX19jYmt7LxGptwT5uPD6' + 'hv+lgLh3C/8nCW7M4UrUeK0XkP2Fz18OiL8W4i85fwNSA8n/AV6gUZDNezugAAAAAElFTkSuQmCC';
const PIN_BACKGROUND = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEQAAABECAYAAAA4E5OyAAAACXBIWXMAABYlAAAW' + 'JQFJUiTwAAAKT2lDQ1BQaG90b3Nob3AgSUNDIHByb2ZpbGUAAHjanVNnVFPpFj333vRCS4iAlEtvUhUIIFJCi4AUkSYqIQkQSoghodkVUcERRUUE' + 'G8igiAOOjoCMFVEsDIoK2AfkIaKOg6OIisr74Xuja9a89+bN/rXXPues852zzwfACAyWSDNRNYAMqUIeEeCDx8TG4eQuQIEKJHAAEAizZCFz/SMB' + 'APh+PDwrIsAHvgABeNMLCADATZvAMByH/w/qQplcAYCEAcB0kThLCIAUAEB6jkKmAEBGAYCdmCZTAKAEAGDLY2LjAFAtAGAnf+bTAICd+Jl7AQBb' + 'lCEVAaCRACATZYhEAGg7AKzPVopFAFgwABRmS8Q5ANgtADBJV2ZIALC3AMDOEAuyAAgMADBRiIUpAAR7AGDIIyN4AISZABRG8lc88SuuEOcqAAB4' + 'mbI8uSQ5RYFbCC1xB1dXLh4ozkkXKxQ2YQJhmkAuwnmZGTKBNA/g88wAAKCRFRHgg/P9eM4Ors7ONo62Dl8t6r8G/yJiYuP+5c+rcEAAAOF0ftH+' + 'LC+zGoA7BoBt/qIl7gRoXgugdfeLZrIPQLUAoOnaV/Nw+H48PEWhkLnZ2eXk5NhKxEJbYcpXff5nwl/AV/1s+X48/Pf14L7iJIEyXYFHBPjgwsz0' + 'TKUcz5IJhGLc5o9H/LcL//wd0yLESWK5WCoU41EScY5EmozzMqUiiUKSKcUl0v9k4t8s+wM+3zUAsGo+AXuRLahdYwP2SycQWHTA4vcAAPK7b8HU' + 'KAgDgGiD4c93/+8//UegJQCAZkmScQAAXkQkLlTKsz/HCAAARKCBKrBBG/TBGCzABhzBBdzBC/xgNoRCJMTCQhBCCmSAHHJgKayCQiiGzbAdKmAv' + '1EAdNMBRaIaTcA4uwlW4Dj1wD/phCJ7BKLyBCQRByAgTYSHaiAFiilgjjggXmYX4IcFIBBKLJCDJiBRRIkuRNUgxUopUIFVIHfI9cgI5h1xGupE7' + 'yAAygvyGvEcxlIGyUT3UDLVDuag3GoRGogvQZHQxmo8WoJvQcrQaPYw2oefQq2gP2o8+Q8cwwOgYBzPEbDAuxsNCsTgsCZNjy7EirAyrxhqwVqwD' + 'u4n1Y8+xdwQSgUXACTYEd0IgYR5BSFhMWE7YSKggHCQ0EdoJNwkDhFHCJyKTqEu0JroR+cQYYjIxh1hILCPWEo8TLxB7iEPENyQSiUMyJ7mQAkmx' + 'pFTSEtJG0m5SI+ksqZs0SBojk8naZGuyBzmULCAryIXkneTD5DPkG+Qh8lsKnWJAcaT4U+IoUspqShnlEOU05QZlmDJBVaOaUt2ooVQRNY9aQq2h' + 'tlKvUYeoEzR1mjnNgxZJS6WtopXTGmgXaPdpr+h0uhHdlR5Ol9BX0svpR+iX6AP0dwwNhhWDx4hnKBmbGAcYZxl3GK+YTKYZ04sZx1QwNzHrmOeZ' + 'D5lvVVgqtip8FZHKCpVKlSaVGyovVKmqpqreqgtV81XLVI+pXlN9rkZVM1PjqQnUlqtVqp1Q61MbU2epO6iHqmeob1Q/pH5Z/YkGWcNMw09DpFGg' + 'sV/jvMYgC2MZs3gsIWsNq4Z1gTXEJrHN2Xx2KruY/R27iz2qqaE5QzNKM1ezUvOUZj8H45hx+Jx0TgnnKKeX836K3hTvKeIpG6Y0TLkxZVxrqpaX' + 'llirSKtRq0frvTau7aedpr1Fu1n7gQ5Bx0onXCdHZ4/OBZ3nU9lT3acKpxZNPTr1ri6qa6UbobtEd79up+6Ynr5egJ5Mb6feeb3n+hx9L/1U/W36' + 'p/VHDFgGswwkBtsMzhg8xTVxbzwdL8fb8VFDXcNAQ6VhlWGX4YSRudE8o9VGjUYPjGnGXOMk423GbcajJgYmISZLTepN7ppSTbmmKaY7TDtMx83M' + 'zaLN1pk1mz0x1zLnm+eb15vft2BaeFostqi2uGVJsuRaplnutrxuhVo5WaVYVVpds0atna0l1rutu6cRp7lOk06rntZnw7Dxtsm2qbcZsOXYBtuu' + 'tm22fWFnYhdnt8Wuw+6TvZN9un2N/T0HDYfZDqsdWh1+c7RyFDpWOt6azpzuP33F9JbpL2dYzxDP2DPjthPLKcRpnVOb00dnF2e5c4PziIuJS4LL' + 'Lpc+Lpsbxt3IveRKdPVxXeF60vWdm7Obwu2o26/uNu5p7ofcn8w0nymeWTNz0MPIQ+BR5dE/C5+VMGvfrH5PQ0+BZ7XnIy9jL5FXrdewt6V3qvdh' + '7xc+9j5yn+M+4zw33jLeWV/MN8C3yLfLT8Nvnl+F30N/I/9k/3r/0QCngCUBZwOJgUGBWwL7+Hp8Ib+OPzrbZfay2e1BjKC5QRVBj4KtguXBrSFo' + 'yOyQrSH355jOkc5pDoVQfujW0Adh5mGLw34MJ4WHhVeGP45wiFga0TGXNXfR3ENz30T6RJZE3ptnMU85ry1KNSo+qi5qPNo3ujS6P8YuZlnM1Vid' + 'WElsSxw5LiquNm5svt/87fOH4p3iC+N7F5gvyF1weaHOwvSFpxapLhIsOpZATIhOOJTwQRAqqBaMJfITdyWOCnnCHcJnIi/RNtGI2ENcKh5O8kgq' + 'TXqS7JG8NXkkxTOlLOW5hCepkLxMDUzdmzqeFpp2IG0yPTq9MYOSkZBxQqohTZO2Z+pn5mZ2y6xlhbL+xW6Lty8elQfJa7OQrAVZLQq2QqboVFoo' + '1yoHsmdlV2a/zYnKOZarnivN7cyzytuQN5zvn//tEsIS4ZK2pYZLVy0dWOa9rGo5sjxxedsK4xUFK4ZWBqw8uIq2Km3VT6vtV5eufr0mek1rgV7B' + 'yoLBtQFr6wtVCuWFfevc1+1dT1gvWd+1YfqGnRs+FYmKrhTbF5cVf9go3HjlG4dvyr+Z3JS0qavEuWTPZtJm6ebeLZ5bDpaql+aXDm4N2dq0Dd9W' + 'tO319kXbL5fNKNu7g7ZDuaO/PLi8ZafJzs07P1SkVPRU+lQ27tLdtWHX+G7R7ht7vPY07NXbW7z3/T7JvttVAVVN1WbVZftJ+7P3P66Jqun4lvtt' + 'Xa1ObXHtxwPSA/0HIw6217nU1R3SPVRSj9Yr60cOxx++/p3vdy0NNg1VjZzG4iNwRHnk6fcJ3/ceDTradox7rOEH0x92HWcdL2pCmvKaRptTmvtb' + 'Ylu6T8w+0dbq3nr8R9sfD5w0PFl5SvNUyWna6YLTk2fyz4ydlZ19fi753GDborZ752PO32oPb++6EHTh0kX/i+c7vDvOXPK4dPKy2+UTV7hXmq86' + 'X23qdOo8/pPTT8e7nLuarrlca7nuer21e2b36RueN87d9L158Rb/1tWeOT3dvfN6b/fF9/XfFt1+cif9zsu72Xcn7q28T7xf9EDtQdlD3YfVP1v+' + '3Njv3H9qwHeg89HcR/cGhYPP/pH1jw9DBY+Zj8uGDYbrnjg+OTniP3L96fynQ89kzyaeF/6i/suuFxYvfvjV69fO0ZjRoZfyl5O/bXyl/erA6xmv' + '28bCxh6+yXgzMV70VvvtwXfcdx3vo98PT+R8IH8o/2j5sfVT0Kf7kxmTk/8EA5jz/GMzLdsAAAAgY0hSTQAAeiUAAICDAAD5/wAAgOkAAHUwAADq' + 'YAAAOpgAABdvkl/FRgAAB7lJREFUeNrsnH9QVNcVx793Xdhll44GUbEzKNoWzQI6/go4ExHTGujYRg1MYtOpzsQ/mkAaK4nB/6haZ5pO80eTOGxm' + 'MmOaTuM/TabSzDBaBIV1tJUxbEQKnSWY8kcMYQwICyzD8u0fvkeXLOze3XcfvExyZt7M7tt7zj3nw333x7nvIkjiW/m/2GP9KIRQVhFJJ4BCAI8C' + '8ADIBZADwA0gTSs2BiAI4A6A/wDoBNAK4F9CiHGFvsT+ca5LQcUukgdI1pMcZ/IyTvK8ZsulAsicMZsBhOQKkqdJfkn18qVme4XlgZBMI3mC5BjN' + 'lzGSvyGZZkkgJHeT/JTzL5+S3G0ZICRTSP6e5BQXTqY0H1KMAhGxAo83ypBcDOCvAH5kkVGzEUCFEGIo2VEmaSBap3YBwEaLTSX8AB4XQvTPGxCt' + 'ZfgA5Ft0ftUB4NG5WkqsmG1JTrAarAYjHA7jxImT6OnpgeZbA0mH2jF59vJeWkwmJyf5i4OHmJLq4OqcNezr69N/8po6ypB8xmowQqEQ9z9ZzpRU' + 'x/T1SGERx8amp0I/MwUIyUySA1aDsW//kzNg6FdlVZVebIDkUjOAvG0lGMFgkHv37psVhn41NTfrxd9WCoSkZ4EnXlEwdu9+PCaMlFQH8ws2MBQK' + '6RM3jwwQ2VHmOABhhdFkdHQU+/btx+UrV+KW7e7uxjvv/Ama7zUy9uPOQ0iuBNAHYJHq4O7du4eBgQGMjo4CAFwuFzIzM5GRkTGnTmdnJ4p3luD+' + '/ftSdaxduxYdtz6G3W4PA8gWQnxmKB9C8pjqjvDNM2foycufs6l78vL55pkzenOPkgsXLzLV4Yz7yOhXfX29rnrMcB9Csl0VDL/fz4ING6UDKdiw' + 'kX6/f1Zbr7xSI23nqacP6GrthoCQzFIFo6WllRlLM6WD0K+HMpaypaU1yt7IyAhXrc6RsuFO/w6DwaCummWkU92loq/o7e1FeUUFhoeHE9YdGRlB' + 'eUUFent7Z9x3u92orj4qZWNiYgLXrl/Xv5bEKhsPSJEKIJWVVRgcHExaf3BwEJWVVVH3Dx08CIdDbrnia/XpH7cbAbLOKAyf7youNTUZhnqpqQk+' + '39UZ95YsWYIfPvaY3PL39m2pmOIByTUayLt/flfZMD2brR3FO6R0A4GAVEzxgGQYDaKlpVUZkNlsrV+/Xkr37t3PpGKKB2Sxig5Vlcxm67srV0p2' + 'zkGpmGwwUVRteMWyZ7PJhRAKhaTKmQpECCE9CsiIw+GISmuGQhNSuk6nUwmQIaNB5OXlKQPi8Xii7t25I/dIulwuqZjiARkwGkRZaakyID8uK4tO' + 'sfs/ltLNysqSiikekP8aDeLw4WeRkpJiGIbdbsfhw89G3f9HY6OU/upVq6RiigfE8BCRnZ2NyuefNwykqrIS2dnZUfmO9vZ2Kf3c3FypmOIBuami' + 'qZ86dRJbtmxJWn/z5s04depk1P3X33gjARub5GKKs9otUrXa7e//goVF2xNe7RYWbWd//xdR9rq7u+lyp0vb6enp0VWLjCz/7SSHlL2/MDbG6uqX' + '6ExzxQ3AmeZidfVLkdsJM/ZhSnbtkoaxbv3DuuqQFpOhBNEHqpPEgUCANTXHmZdfEOV8Xn4Ba2qOMxAIzKmfSHIoJdXBo0erddUPVGTMDpmZQQ+H' + 'wwyFQgyFQgyHw3HL/+7VVxN+7K5du66rH1QBJJ1k0AqbUi++eCRhGA978qZ3L0imG96GEEKMAHh/Ibce2trasKN4J+q83oR1n3vul/rH97VYjG92' + 'k9w6768ETU3R57vKp54+kFCGPfJatnwFh4eHdZNbZWK2Sy7S2kheAbDTrFZw48YNDA4N4ZOeT3Dzo5tobLyEvr4+QzZ/9cILSE9PB4DLQog2JRtV' + 'EVRLADSbBSTV4VRqb/myZejq+rcOpEQIcSWyhRhe/gshLuPBizJfC6mtrdVhNETCUNZCNLL5AD4C5B61hWohRUWFuNzcDJvNNglgkxCi46v9ppIE' + 'kWb4NSu3jNTUVHjr6vRM2mtfhWFGxuwkgIBVgZw+/Vs9kRQAcCLhLF+SbyEW4sFbiHYrPTJlpaU4f/5vEEJM4sFbiP+ca6qhNKeqVVRrpZaRk5OD' + 's2fP6n/E2rlgmNJCNMo2APUA9ix0C3G73fC1tuj52w8B7BVCTMWajCrPumsVPgOgeyFbht1ux3vv/UWH0Q3g57FgmLoNIYS4D+CnKpLRycpbXq+e' + 'fB4A8BPNp+RFxfEQktuMroiTWavU1XkjV7LblMSs8LxMGcmJ+QLi9b6lq06QLFXWCBSfqHoiWSgGYDyh9KlQfeZOgxIyA4jLnc5z585N54ySgTHv' + 'QLQKdyWanJZ51+zCxYuRyeISU/pNs46pktxEsl8FkO99/we8devW9I4GyU2mDSQmn9tdQ7LTCJAdxcW8+/nnerHbJNco8GthgGiVLybZkAyQI0d+' + 'HfnyboN2kgtfayCaA4tI/kEWyOIlD0V2ntR0Fyn0Z2GBRDhSTnI4FpCt2x5hV1eXfnuYZLkJflgDiObMOpL+2YC8/PIxjo9P/4sAP8l1JvlgHSCa' + 'Q06Sr0cC6ejoiPz6R+2wI74RQCIc20Mycmu/n+SeeajXmkA051aQ/JDk30kun6c6kzvq/k0U27cIZsr/BgDbzNoD8uJVDwAAAABJRU5ErkJggg==';
const PIN_FOREGROUND = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEQAAABECAYAAAA4E5OyAAAACXBIWXMAABYlAAAW' + 'JQFJUiTwAAAKT2lDQ1BQaG90b3Nob3AgSUNDIHByb2ZpbGUAAHjanVNnVFPpFj333vRCS4iAlEtvUhUIIFJCi4AUkSYqIQkQSoghodkVUcERRUUE' + 'G8igiAOOjoCMFVEsDIoK2AfkIaKOg6OIisr74Xuja9a89+bN/rXXPues852zzwfACAyWSDNRNYAMqUIeEeCDx8TG4eQuQIEKJHAAEAizZCFz/SMB' + 'APh+PDwrIsAHvgABeNMLCADATZvAMByH/w/qQplcAYCEAcB0kThLCIAUAEB6jkKmAEBGAYCdmCZTAKAEAGDLY2LjAFAtAGAnf+bTAICd+Jl7AQBb' + 'lCEVAaCRACATZYhEAGg7AKzPVopFAFgwABRmS8Q5ANgtADBJV2ZIALC3AMDOEAuyAAgMADBRiIUpAAR7AGDIIyN4AISZABRG8lc88SuuEOcqAAB4' + 'mbI8uSQ5RYFbCC1xB1dXLh4ozkkXKxQ2YQJhmkAuwnmZGTKBNA/g88wAAKCRFRHgg/P9eM4Ors7ONo62Dl8t6r8G/yJiYuP+5c+rcEAAAOF0ftH+' + 'LC+zGoA7BoBt/qIl7gRoXgugdfeLZrIPQLUAoOnaV/Nw+H48PEWhkLnZ2eXk5NhKxEJbYcpXff5nwl/AV/1s+X48/Pf14L7iJIEyXYFHBPjgwsz0' + 'TKUcz5IJhGLc5o9H/LcL//wd0yLESWK5WCoU41EScY5EmozzMqUiiUKSKcUl0v9k4t8s+wM+3zUAsGo+AXuRLahdYwP2SycQWHTA4vcAAPK7b8HU' + 'KAgDgGiD4c93/+8//UegJQCAZkmScQAAXkQkLlTKsz/HCAAARKCBKrBBG/TBGCzABhzBBdzBC/xgNoRCJMTCQhBCCmSAHHJgKayCQiiGzbAdKmAv' + '1EAdNMBRaIaTcA4uwlW4Dj1wD/phCJ7BKLyBCQRByAgTYSHaiAFiilgjjggXmYX4IcFIBBKLJCDJiBRRIkuRNUgxUopUIFVIHfI9cgI5h1xGupE7' + 'yAAygvyGvEcxlIGyUT3UDLVDuag3GoRGogvQZHQxmo8WoJvQcrQaPYw2oefQq2gP2o8+Q8cwwOgYBzPEbDAuxsNCsTgsCZNjy7EirAyrxhqwVqwD' + 'u4n1Y8+xdwQSgUXACTYEd0IgYR5BSFhMWE7YSKggHCQ0EdoJNwkDhFHCJyKTqEu0JroR+cQYYjIxh1hILCPWEo8TLxB7iEPENyQSiUMyJ7mQAkmx' + 'pFTSEtJG0m5SI+ksqZs0SBojk8naZGuyBzmULCAryIXkneTD5DPkG+Qh8lsKnWJAcaT4U+IoUspqShnlEOU05QZlmDJBVaOaUt2ooVQRNY9aQq2h' + 'tlKvUYeoEzR1mjnNgxZJS6WtopXTGmgXaPdpr+h0uhHdlR5Ol9BX0svpR+iX6AP0dwwNhhWDx4hnKBmbGAcYZxl3GK+YTKYZ04sZx1QwNzHrmOeZ' + 'D5lvVVgqtip8FZHKCpVKlSaVGyovVKmqpqreqgtV81XLVI+pXlN9rkZVM1PjqQnUlqtVqp1Q61MbU2epO6iHqmeob1Q/pH5Z/YkGWcNMw09DpFGg' + 'sV/jvMYgC2MZs3gsIWsNq4Z1gTXEJrHN2Xx2KruY/R27iz2qqaE5QzNKM1ezUvOUZj8H45hx+Jx0TgnnKKeX836K3hTvKeIpG6Y0TLkxZVxrqpaX' + 'llirSKtRq0frvTau7aedpr1Fu1n7gQ5Bx0onXCdHZ4/OBZ3nU9lT3acKpxZNPTr1ri6qa6UbobtEd79up+6Ynr5egJ5Mb6feeb3n+hx9L/1U/W36' + 'p/VHDFgGswwkBtsMzhg8xTVxbzwdL8fb8VFDXcNAQ6VhlWGX4YSRudE8o9VGjUYPjGnGXOMk423GbcajJgYmISZLTepN7ppSTbmmKaY7TDtMx83M' + 'zaLN1pk1mz0x1zLnm+eb15vft2BaeFostqi2uGVJsuRaplnutrxuhVo5WaVYVVpds0atna0l1rutu6cRp7lOk06rntZnw7Dxtsm2qbcZsOXYBtuu' + 'tm22fWFnYhdnt8Wuw+6TvZN9un2N/T0HDYfZDqsdWh1+c7RyFDpWOt6azpzuP33F9JbpL2dYzxDP2DPjthPLKcRpnVOb00dnF2e5c4PziIuJS4LL' + 'Lpc+Lpsbxt3IveRKdPVxXeF60vWdm7Obwu2o26/uNu5p7ofcn8w0nymeWTNz0MPIQ+BR5dE/C5+VMGvfrH5PQ0+BZ7XnIy9jL5FXrdewt6V3qvdh' + '7xc+9j5yn+M+4zw33jLeWV/MN8C3yLfLT8Nvnl+F30N/I/9k/3r/0QCngCUBZwOJgUGBWwL7+Hp8Ib+OPzrbZfay2e1BjKC5QRVBj4KtguXBrSFo' + 'yOyQrSH355jOkc5pDoVQfujW0Adh5mGLw34MJ4WHhVeGP45wiFga0TGXNXfR3ENz30T6RJZE3ptnMU85ry1KNSo+qi5qPNo3ujS6P8YuZlnM1Vid' + 'WElsSxw5LiquNm5svt/87fOH4p3iC+N7F5gvyF1weaHOwvSFpxapLhIsOpZATIhOOJTwQRAqqBaMJfITdyWOCnnCHcJnIi/RNtGI2ENcKh5O8kgq' + 'TXqS7JG8NXkkxTOlLOW5hCepkLxMDUzdmzqeFpp2IG0yPTq9MYOSkZBxQqohTZO2Z+pn5mZ2y6xlhbL+xW6Lty8elQfJa7OQrAVZLQq2QqboVFoo' + '1yoHsmdlV2a/zYnKOZarnivN7cyzytuQN5zvn//tEsIS4ZK2pYZLVy0dWOa9rGo5sjxxedsK4xUFK4ZWBqw8uIq2Km3VT6vtV5eufr0mek1rgV7B' + 'yoLBtQFr6wtVCuWFfevc1+1dT1gvWd+1YfqGnRs+FYmKrhTbF5cVf9go3HjlG4dvyr+Z3JS0qavEuWTPZtJm6ebeLZ5bDpaql+aXDm4N2dq0Dd9W' + 'tO319kXbL5fNKNu7g7ZDuaO/PLi8ZafJzs07P1SkVPRU+lQ27tLdtWHX+G7R7ht7vPY07NXbW7z3/T7JvttVAVVN1WbVZftJ+7P3P66Jqun4lvtt' + 'Xa1ObXHtxwPSA/0HIw6217nU1R3SPVRSj9Yr60cOxx++/p3vdy0NNg1VjZzG4iNwRHnk6fcJ3/ceDTradox7rOEH0x92HWcdL2pCmvKaRptTmvtb' + 'Ylu6T8w+0dbq3nr8R9sfD5w0PFl5SvNUyWna6YLTk2fyz4ydlZ19fi753GDborZ752PO32oPb++6EHTh0kX/i+c7vDvOXPK4dPKy2+UTV7hXmq86' + 'X23qdOo8/pPTT8e7nLuarrlca7nuer21e2b36RueN87d9L158Rb/1tWeOT3dvfN6b/fF9/XfFt1+cif9zsu72Xcn7q28T7xf9EDtQdlD3YfVP1v+' + '3Njv3H9qwHeg89HcR/cGhYPP/pH1jw9DBY+Zj8uGDYbrnjg+OTniP3L96fynQ89kzyaeF/6i/suuFxYvfvjV69fO0ZjRoZfyl5O/bXyl/erA6xmv' + '28bCxh6+yXgzMV70VvvtwXfcdx3vo98PT+R8IH8o/2j5sfVT0Kf7kxmTk/8EA5jz/GMzLdsAAAAgY0hSTQAAeiUAAICDAAD5/wAAgOkAAHUwAADq' + 'YAAAOpgAABdvkl/FRgAABjRJREFUeNrsnH1oV1UYxz+b8wVfVlnZQ6kh2qBCrLbSf8ICjah/VIRKDSukUlNEQchETSHFFwxDSRA1gjJQfPljgr04' + 'EcR3Y8EKUwQxeNKcpi42p1t/3Gf063I3z7m/c3/7WfvC/tjduec853Ofc85znnPvSlpbW+nSPyrtQtAFpEOVuRRS1bwbEpFSYBjwFPAkMAgYCPQB' + 'elqxJqABuACcB+qAU8BZVW0JYMMdy5S4zCH5ABGRJ4BXgLHA/SmruQzsA/aqat1dCUREngemmkeE1I/AF6p68K4AIiJDgflAZcbD/QSwUlXPFiUQ' + 'myPeAt4v4GTdAnwObHWZY1yABDFcRO4FNgAzCrxylVqbG8yGzl92ReRhYDNQ1YmrZRWw2WzpPCAi8giwCRhcBCHEYGCT2VR4ICLSH1gPDCiiuGoA' + 'sN5sKxwQEekGrLLAKoQa7SeEBgKrRKQszc1lKRudBYxIee9fQA1wGPgZuKCqzQa6u3XocWAU8ALQO0UbI4APgE99b/RedkVkuE2iJSmizS3ALlVt' + 'dPTEXsA44O0UUW4r8I6q/pRZHGJD5UugwtO47cBnqtqQcoj2Ma+c6HnraeBNVb2dVRzysieMZmCBqq5IC8MeSIOqrgAWWJ2uqjCbww8Zi0S/AYY4' + '1n0LmK2qRzt48n3N6Afs0h/AaVW90cE9zwHrPOa/c8Brqtri4iE+k+ooDxgAS5NgiEgJMBp4A3g6wUtbROQU8DVwQFVbYw/nqIh8DCxztGOI2X4o' + '9LL7qkfZPapanQBDgI3Aatv8lbZjU6WV2SgJj1VV9wJ7srC9xDGn2gPYn5PI6UjXgPGq+mcMxjDb7/gGTfXADFU9E6uvHNgFlDvU0QS8CNwM5SHD' + 'HWEAbEuA0d/GfZoIsj+wLh59quo1YJtjHT2tD8GGjGtuowXYkXB9fp4h/gCrI2k5v+1YxzMhgQxzTdqo6uWYd1QAYwKE5GOsrlwvqQdOOt7/WEgg' + 'jzqWO5ZwbVzAzds4xzZT96HUw2VddLad5TqURiZcOxOyD65A+jiW+z0h5hgYEMggq7PdNvPtgysQ13LxZa0vYVOKpVZnfEkN1ofQ+c/usd8bbOUJ' + 'pRbgRkKMFJS4aw7DRQ/FVoEWolO4ULoQD+UBCdkHVyBXHcsNzWMVSLuKDQ3ZB1cgridVzyZc2x0QSFJdVSH74ArE1e0rReS+2LCpI0oZ5qua+Lmu' + 'tVUVsg+uQH5xLNetneBpuW3S0qre6kgK1LqF7IMrEJ8T9yki0i/mJZeBmR5zUXzsz0zYEvQDpnjUUxcayHXHsvcAsxNyGL8SvQ1Q69GJWmCq3RvX' + 'LGvLRdeDArHl87BHR8aLyNiEen4DphHlRmvvAGIBMM3uiSeaxgITPOw54vrCjU8K8Qeil15ctURErqrqsQS4+4B9NilWAA/any8R5VSvtFepiFQC' + 'SzyH3feuBX2SzD2sI309DGkiyrofCLHmisho4BPck1VYZPuSqt4MegyhqjeBas8+9ATWiMhcO3RKC6KXiMwF1njCAKg224OG7m36iuhEzFeTgB0i' + 'MsE8zRVEDxGZQJQZm5Si3VazmeBDJsfI5Z5zSVzXgO+AI0Rnu9o24dnZjxCd7Y4kyrSV59HWt6r6YY7tmQAZbE8s1E75Vs7GqzfpD+CTdsYTVfW8' + 'DxDvTlkDOwPuT8rMC8oDwgDYmQsjqzmkTRuAKxSvrpiNFASInbusLWIga+NnQ1l7CHZUWVOEMGqSjlEzB2JaBlwsIhgXcT8EDw/E3HKhrRSdrVvA' + 'wrRDJZSHoKoniU7qO1urzRY6FYhB2W6xSWdpu9lAUQAxrSR6ZaLQ2m9tU1RALPxeCBwvIIzjwEchPi7KwkNQ1SZgToGgHAfm+OxkCw7EoDQWAEob' + 'jMbQFWfyKUcOlEMZVH8oKxiZAcmBMg//pFJHqgbmZQUjUyAGpRlYTPRKd77aAixuey8+K5WRsexwer2InAMWpWizGViWz/6kaDwkYTP4LlFm3VWX' + 'gPcKBaOgQAxKLTAZtxflTgCT7R7+k0AMSj0wHdhKcsK61f423coWVJl/2d2R7EX+pfz75f9FHX0wkGd7xechMdBHiT4COGg/r2cFI6iH/J/U9e8y' + 'uoB0AfHS3wMAkOtpr8ibyvkAAAAASUVORK5CYII=';

class StaticVectorImageRender extends StaticImageRender {
  constructor({
    src,
    position,
    width,
    height,
    onLoad,
    offset,
    angle = 0
  }) {
    super({
      src,
      width,
      height,
      onLoad,
      offset
    });
    this._cachedSin = null;
    this._cachedCos = null;
    this.position = position;
    this.angle = angle;
  }

  contains(position) {
    if (this.angle === 0) return this._boxContains(position[0], position[1]);
    let sin = this._sin;
    let cos = this._cos;
    let dx = position[0] - this.position[0];
    let dy = position[1] - this.position[1];
    let rotatedX = this.position[0] + dx * cos + dy * sin;
    let rotatedY = this.position[1] + dy * cos - dx * sin;
    return this._boxContains(rotatedX, rotatedY);
  }

  _boxContains(x, y) {
    let minX = this.position[0] + this.offset[0];
    let minY = this.position[1] + this.offset[1];
    return x > minX && x < minX + this.width && y > minY && y < minY + this.height;
  }

  get angle() {
    return this._angle;
  }

  set angle(value) {
    this._angle = value;
    this._cachedCos = null;
    this._cachedSin = null;
  }

  get _sin() {
    if (this._cachedSin === null) this._cachedSin = Math.sin(this.angle);
    return this._cachedSin;
  }

  get _cos() {
    if (this._cachedCos === null) this._cachedCos = Math.cos(this.angle);
    return this._cachedCos;
  }

}

/**
 * Symbol of point drawn as circle with outline.
 * @alias sGis.symbol.point.Image
 */

class StaticImageSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Width of the image. If not set, image will be automatically resized according to height. If both width and height are not set, original image size will be used. */

    this.width = 32;
    /** Height of the image. If not set, image will be automatically resized according to width. If both width and height are not set, original image size will be used. */

    this.height = 32;
    this._anchorPoint = [16, 32];
    /** Source of the image. Can be url or data:url string. */

    this.source = PIN_IMAGE;
    /**
     * Clockwise rotation of the image in radians.
     */

    this.angle = 0;
    Object.assign(this, options);
  }
  /**
   * Anchor point of the image. If set to [0, 0], image's left top corner will be at the feature position.<br>
   *     Anchor point does not scale with width and height parameters.
   */


  get anchorPoint() {
    return this._anchorPoint;
  }

  set anchorPoint(anchorPoint) {
    // TODO: remove deprecated part after 2018
    let deprecated = anchorPoint;

    if (deprecated.x !== undefined && deprecated.y !== undefined) {
      warn('Using anchorPoint in {x, y} format is deprecated. Use [x, y] format instead.');
      this._anchorPoint = [deprecated.x, deprecated.y];
    } else {
      this._anchorPoint = anchorPoint;
    }
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof PointFeature)) return [];
    let position = feature.projectTo(crs).position;
    let pxPosition = [position[0] / resolution, -position[1] / resolution];
    return [new StaticVectorImageRender({
      src: this.source,
      position: pxPosition,
      angle: this.angle,
      width: this.width,
      height: this.height,
      offset: [-this.anchorPoint[0], -this.anchorPoint[1]]
    })];
  }

  renderFunctionAsync(feature, resolution, crs) {
    return __awaiter(this, void 0, void 0, function* () {
      const renders = this.renderFunction(feature, resolution, crs);
      return Promise.all(renders.map(render => render.readyPromise)).then(() => renders);
    });
  }

}
registerSymbol(StaticImageSymbol, 'point.Image', ['width', 'height', 'anchorPoint', 'source', 'angle']);

const MAX_TRIES = 3;
/**
 * Symbol of point drawn as masked image.
 * @alias sGis.symbol.point.MaskedImage
 */

class MaskedImage extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor({
    width,
    height,
    angle,
    anchorPoint,
    imageSource,
    maskSource,
    maskColor,
    onUpdate
  } = {}) {
    super();
    /** Width of the image. If not set, image will be automatically resized according to height. If both width and height are not set, original image size will be used. */

    this.width = 32;
    /** Height of the image. If not set, image will be automatically resized according to width. If both width and height are not set, original image size will be used. */

    this.height = 32;
    this._anchorPoint = [16, 32];
    /**
     * Clockwise rotation of the image in radians.
     */

    this.angle = 0;
    this._imageSource = PIN_BACKGROUND;
    this._maskSource = PIN_FOREGROUND;
    this._maskColor = '#9bdb00';
    this.width = width;
    this.height = height;
    this.angle = angle;
    this.anchorPoint = anchorPoint;
    this._maskColor = maskColor;
    this.onUpdate = onUpdate;
    this.setImageSource(imageSource);
    this.setMaskSource(maskSource);

    this._updateMasked();
  }
  /**
   * Anchor point of the image. If set to [0, 0], image's left top corner will be at the feature position.<br>
   *     Anchor point does not scale with width and height parameters.
   */


  get anchorPoint() {
    return this._anchorPoint;
  }

  set anchorPoint(anchorPoint) {
    // TODO: remove deprecated part after 2018
    let deprecated = anchorPoint;

    if (deprecated && deprecated.x !== undefined && deprecated.y !== undefined) {
      warn('Using anchorPoint in {x, y} format is deprecated. Use [x, y] format instead.');
      this._anchorPoint = [deprecated.x, deprecated.y];
    } else {
      this._anchorPoint = anchorPoint;
    }
  }

  prepareSymbol() {
    return Promise.all([this.imageReadyPromise, this.maskReadyPromise]).then(() => {
      this._updateMasked();
    });
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof PointFeature) || !this._maskedSrc) return [];
    let position = feature.projectTo(crs).position;
    let pxPosition = [position[0] / resolution, -position[1] / resolution];
    return [new StaticVectorImageRender({
      src: this._maskedSrc,
      position: pxPosition,
      angle: this.angle,
      width: this.width,
      height: this.height,
      offset: [-this.anchorPoint[0], -this.anchorPoint[1]]
    })];
  }

  renderFunctionAsync(feature, resolution, crs) {
    return this.prepareSymbol().then(() => this.renderFunction(feature, resolution, crs));
  }
  /**
   * Source of the base image. Can be url or data:url string.
   */


  get imageSource() {
    return this._imageSource;
  }

  set imageSource(source) {
    this._imageSource = source;
    this._image = new Image();
    this._image.src = source;

    if (this._image.complete) {
      this._updateMasked();
    } else {
      this._image.onload = this._updateMasked.bind(this);
    }
  }
  /**
   * Source of the mask image. Can be url or data:url string.
   */


  get maskSource() {
    return this._maskSource;
  }

  set maskSource(source) {
    this._maskSource = source;
    this._mask = new Image();
    this._mask.src = source;

    if (this._mask.complete) {
      this._updateMasked();
    } else {
      this._mask.onload = this._updateMasked.bind(this);
    }
  }
  /**
   * Color of the mask. Can be any valid css color string.
   */


  get maskColor() {
    return this._maskColor;
  }

  set maskColor(color) {
    this._maskColor = color;

    this._updateMasked();
  }

  setImageSource(src) {
    this._imageSource = src;
    this._image = new Image();
    this.imageReadyPromise = loadImage(this._image, src, null);
    return this.imageReadyPromise;
  }

  setMaskSource(src) {
    this._maskSource = src;
    this._mask = new Image();
    this.maskReadyPromise = loadImage(this._mask, src, null);
    return this.maskReadyPromise;
  }

  _isLoaded() {
    return this._image.complete && this._mask.complete;
  }

  _updateMasked(triesNo = 0) {
    if (!this._mask || !this._image || !this._isLoaded()) return;
    let canvas = document.createElement('canvas');
    canvas.width = this._mask.width;
    canvas.height = this._mask.height;
    let ctx = canvas.getContext('2d');

    if (canvas.width === 0 || canvas.height === 0) {
      // IE sometimes forgets to change the size of canvas and fails to draw the image. Need to check for that.
      if (triesNo > MAX_TRIES) return;
      setTimeout(() => this._updateMasked(triesNo + 1), 0);
      return;
    }

    ctx.drawImage(this._mask, 0, 0);
    let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    this._recolorMask(imageData);

    ctx.putImageData(imageData, 0, 0);
    let resultCanvas = document.createElement('canvas');
    resultCanvas.width = this._image.width;
    resultCanvas.height = this._image.height;
    let resultCtx = resultCanvas.getContext('2d');

    if (resultCanvas.width === 0 || resultCanvas.height === 0) {
      // IE sometimes forgets to change the size of canvas and fails to draw the image. Need to check for that.
      if (triesNo > MAX_TRIES) return;
      setTimeout(() => this._updateMasked(triesNo + 1), 0);
      return;
    }

    resultCtx.drawImage(this._image, 0, 0);
    resultCtx.drawImage(canvas, 0, 0);
    this._maskedSrc = resultCanvas.toDataURL();
    if (this.onUpdate) this.onUpdate();
  }

  _recolorMask(imageData) {
    let maskColor = new Color(this.maskColor);
    let alphaNormalizer = 65025;
    let d = imageData.data;

    for (let i = 0; i < d.length; i += 4) {
      let r = d[i];
      let a = d[i + 3];
      let srcA = a * maskColor.a / alphaNormalizer;
      d[i + 3] = +Math.round(Math.min(1, srcA) * 255);
      d[i] = maskColor.r * r / 255;
      d[i + 1] = maskColor.g * r / 255;
      d[i + 2] = maskColor.b * r / 255;
    }
  }

}
registerSymbol(MaskedImage, 'point.MaskedImage', ['width', 'height', 'anchorPoint', 'imageSource', 'maskSource', 'maskColor', 'angle']);

/**
 * Symbol of a highlighted feature for editor.
 * @alias sGis.symbol.Editor
 */

class EditorSymbol extends Symbol {
  /**
   * @param {Object} [options] - key-value list of properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Base symbol of the feature. Used to render original feature with the highlight. */

    this.baseSymbol = new PointSymbol();
    /** Color of the halo (highlight). Can be any valid css color string. */

    this.color = 'rgba(97,239,255,0.5)';
    /** Size of the halo around the feature. */

    this.haloSize = 5;
    Object.assign(this, options);
  }

  renderFunction(feature, resolution, crs) {
    let baseRender = this.baseSymbol.renderFunction(feature, resolution, crs);
    let halo;

    for (let i = 0; i < baseRender.length; i++) {
      if (baseRender[i] instanceof Arc) {
        halo = new Arc(baseRender[i].center, {
          fillColor: this.color,
          radius: parseFloat(baseRender[i].radius) + this.haloSize,
          strokeColor: 'transparent'
        });
        break;
      } else if (baseRender[i] instanceof PolyRender) {
        halo = new PolyRender(baseRender[i].coordinates, {
          enclosed: baseRender[i].enclosed,
          fillStyle: baseRender[i].fillStyle === FillStyle.None ? FillStyle.None : FillStyle.Color,
          strokeColor: this.color,
          fillColor: this.color,
          strokeWidth: parseFloat(baseRender[i].strokeWidth) + 2 * this.haloSize
        });
        break;
      } else if (this.baseSymbol instanceof StaticImageSymbol || this.baseSymbol instanceof MaskedImage) {
        halo = new Arc([baseRender[i].position[0] - +this.baseSymbol.anchorPoint[0] + this.baseSymbol.width / 2, baseRender[i].position[1] - +this.baseSymbol.anchorPoint[1] + this.baseSymbol.height / 2], {
          fillColor: this.color,
          radius: this.baseSymbol.width / 2 + this.haloSize,
          strokeColor: 'transparent'
        });
        break;
      }
    }

    if (halo) baseRender.unshift(halo);
    return baseRender;
  }

}

class CombinedSnappingProvider {
  constructor(providers) {
    this.providers = providers;
  }

  getSnappingPoint(point, activeContour, activeIndex, isPolygon) {
    for (let i = 0; i < this.providers.length; i++) {
      const snappingPoint = this.providers[i].getSnappingPoint(point, activeContour, activeIndex, isPolygon);
      if (snappingPoint !== null) return snappingPoint;
    }
  }

  clone() {
    let children = this.providers.map(x => x.clone());
    return new CombinedSnappingProvider(children);
  }

}

class FeatureSelectEvent extends sGisEvent {
  constructor(feature) {
    super(FeatureSelectEvent.type);
    this.feature = feature;
  }

}
FeatureSelectEvent.type = 'featureSelect';
class FeatureDeselectEvent extends sGisEvent {
  constructor(feature) {
    super(FeatureDeselectEvent.type);
    this.feature = feature;
  }

}
FeatureDeselectEvent.type = 'featureDeselect';
class FeatureRemoveEvent extends sGisEvent {
  constructor(feature) {
    super(FeatureRemoveEvent.type);
    this.feature = feature;
  }

}
FeatureRemoveEvent.type = 'featureRemove';
const modes = ['vertex', 'rotate', 'scale', 'drag'];
/**
 * Control for editing points, polylines and polygons. It uses PointEditor, PolyEditor, PolyTransform and Snapping classes for editing corresponding features.
 * @alias sGis.controls.Editor
 */

class Editor extends Control {
  /**
   * @param map - map object the control will work with
   * @param options - key-value set of properties to be set to the instance
   */
  constructor(map, {
    snappingProvider,
    activeLayer,
    isActive = false
  } = {}) {
    super(map, {
      snappingProvider,
      activeLayer
    });
    this._ignoreEvents = false;
    this._scaling = true;
    this._rotation = true;
    this._dragging = true;
    this._vertexEditing = true;
    this._activeFeature = null;
    this._deselectAllowed = true;
    /**
     * If set to true the feature will be deleted in one of two cases:<br>
     *     1) User removes last point of polyline or polygon.
     *     2) User presses "del" button
     */

    this.allowDeletion = true;
    this._ns = '.' + getGuid();
    this._setListener = this._setListener.bind(this);
    this._removeListener = this._removeListener.bind(this);
    this._onEdit = this._onEdit.bind(this);

    this._setEditors();

    this._states = new StateManager();
    this._deselect = this._deselect.bind(this);
    this.setMode(modes);
    this._handleFeatureAdd = this._handleFeatureAdd.bind(this);
    this._handleFeatureRemove = this._handleFeatureRemove.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this.isActive = isActive;
  }

  _setEditors() {
    this._pointEditor = new PointEditor(this.map, {
      snappingProvider: this.snappingProvider,
      activeLayer: this.activeLayer
    });

    this._pointEditor.on(EditEvent.type, this._onEdit);

    this._polyEditor = new PolyEditor(this.map, {
      snappingProvider: this._getPolyEditorSnappingProvider(),
      onFeatureRemove: this._delete.bind(this),
      activeLayer: this.activeLayer
    });

    this._polyEditor.on(EditEvent.type, this._onEdit);

    this._polyEditor.on(ChangeEvent.type, this._updateTransformControl.bind(this));

    this._polyTransform = new PolyTransform(this.map);

    this._polyTransform.on('rotationEnd scalingEnd', this._onEdit);
  }

  setSnappingProvider(provider) {
    this.snappingProvider = provider;
    if (this._pointEditor) this._pointEditor.snappingProvider = provider;
    if (this._polyEditor) this._polyEditor.snappingProvider = this._getPolyEditorSnappingProvider();
  }

  _getPolyEditorSnappingProvider() {
    if (!this.snappingProvider) return undefined;
    const result = this.snappingProvider.clone();

    if (result instanceof SnappingProviderBase) {
      result.snappingMethods = result.snappingMethods.concat([emptySnapping]);
    } else if (result instanceof CombinedSnappingProvider) {
      result.providers.forEach(child => {
        if (child instanceof SnappingProviderBase) {
          child.snappingMethods = child.snappingMethods.concat([emptySnapping]);
        }
      });
    }

    return result;
  }

  _onEdit() {
    this.fire('edit');

    this._saveState();
  }

  _activate() {
    if (!this.activeLayer) return;
    this.activeLayer.features.forEach(this._setListener, this);
    this.activeLayer.on(FeaturesAddEvent.type, this._handleFeatureAdd);
    this.activeLayer.on(FeaturesRemoveEvent.type, this._handleFeatureRemove);
    this.activeLayer.redraw();
    this.map.on(sGisClickEvent.type, this._onMapClick.bind(this));
    listenDomEvent(document, 'keydown', this._handleKeyDown);
  }

  _handleFeatureAdd(event) {
    let featuresAddEvent = event;
    featuresAddEvent.features.forEach(f => this._setListener(f));
  }

  _handleFeatureRemove(event) {
    let featuresRemoveEvent = event;
    featuresRemoveEvent.features.forEach(f => this._removeListener(f));
  }

  _setListener(feature) {
    feature.on(sGisClickEvent.type + this._ns, this._handleFeatureClick.bind(this, feature));
  }

  _removeListener(feature) {
    feature.off(sGisClickEvent.type + this._ns);
  }

  _onMapClick() {
    if (!this.ignoreEvents) this._deselect();
  }

  _deactivate() {
    this._deselect();

    if (!this.activeLayer) return;
    this.activeLayer.features.forEach(this._removeListener, this);
    this.activeLayer.off('featureAdd', this._handleFeatureAdd);
    this.activeLayer.off('featureRemove', this._handleFeatureRemove);
    this.map.off('click', this._deselect);
    removeDomEventListener(document, 'keydown', this._handleKeyDown);
  }
  /**
   * Selects a given feature if it is in the active layer.
   * @param feature
   */


  select(feature) {
    this.activeFeature = feature;
  }
  /**
   * Clears selection if any.
   */


  deselect() {
    this.activeFeature = null;
  }
  /**
   * Currently selected for editing feature.
   */


  get activeFeature() {
    return this._activeFeature;
  }

  set activeFeature(feature) {
    if (feature) this.activate();

    this._select(feature);
  }

  _handleFeatureClick(feature, event) {
    if (!this.isActive) {
      this._removeListener(feature);

      return;
    }

    if (this.ignoreEvents) return;
    event.stopPropagation();

    this._select(feature);
  }

  _select(feature) {
    if (this._activeFeature === feature) return;

    this._deselect();

    this._activeFeature = feature;
    if (!feature) return;
    feature.setTempSymbol(new EditorSymbol({
      baseSymbol: feature.symbol
    }));

    if (feature instanceof PointFeature) {
      this._pointEditor.activeLayer = this.activeLayer;
      this._pointEditor.activeFeature = feature;
    } else if (feature instanceof Poly) {
      this._activatePolyControls(feature);
    }

    if (this.activeLayer) this.activeLayer.redraw();

    this._saveState();

    this.fire(new FeatureSelectEvent(feature));
  }

  _activatePolyControls(feature) {
    this._polyEditor.featureDragAllowed = this._dragging;
    this._polyEditor.vertexChangeAllowed = this._vertexEditing;
    this._polyEditor.activeLayer = this.activeLayer;
    this._polyEditor.activeFeature = feature;
    this._polyTransform.enableRotation = this._rotation;
    this._polyTransform.enableScaling = this._scaling;
    this._polyTransform.activeLayer = this.activeLayer;
    this._polyTransform.activeFeature = feature;
  }

  _deselect() {
    if (!this._activeFeature || !this._deselectAllowed) return;

    this._pointEditor.deactivate();

    this._polyEditor.deactivate();

    this._polyTransform.deactivate();

    let feature = this._activeFeature;

    this._activeFeature.clearTempSymbol();

    this._activeFeature = null;
    if (this.activeLayer) this.activeLayer.redraw();
    this.fire(new FeatureDeselectEvent(feature));
  }

  _updateTransformControl() {
    if (this._polyTransform.isActive) this._polyTransform.update();
  }
  /**
   * Sets the editing mode. Available modes are:<br>
   *     * vertex - editing vertexes of polygons and polylines.
   *     * rotate - rotation of polygons and polylines
   *     * drag - dragging of whole features
   *     * scale - scaling of polygons and polylines
   *     * all - all modes are active
   * @param mode - can be coma separated list or array of mode names
   */


  setMode(mode) {
    if (mode === 'all') mode = modes;
    if (!Array.isArray(mode)) mode = mode.split(',').map(x => x.trim());
    this._vertexEditing = mode.indexOf('vertex') >= 0;
    this._rotation = mode.indexOf('rotate') >= 0;
    this._dragging = mode.indexOf('drag') >= 0;
    this._scaling = mode.indexOf('scale') >= 0;

    if (this._activeFeature instanceof Poly) {
      this._polyEditor.deactivate();

      this._polyTransform.deactivate();

      this._activatePolyControls(this._activeFeature);
    }
  }
  /**
   * If deselecting was prohibited, this methods turns it on again.
   */


  allowDeselect() {
    this._deselectAllowed = true;
  }
  /**
   * Prevents feature to be deselected by any user or code interaction. It will not have effect if the control is deactivated though.
   */


  prohibitDeselect() {
    this._deselectAllowed = false;
  }

  _delete() {
    if (this._deselectAllowed && this.allowDeletion && this._activeFeature) {
      let feature = this._activeFeature;
      this.prohibitEvent(FeatureDeselectEvent.type);

      this._deselect();

      this.allowEvent(FeatureDeselectEvent.type);
      if (this.activeLayer) this.activeLayer.remove(feature);

      this._saveState();

      this.fire(new FeatureRemoveEvent(feature));
    }
  }

  _handleKeyDown(event) {
    switch (event.which) {
      case 27:
        this._deselect();

        return false;
      // esc

      case 46:
        this._delete();

        return false;
      // del

      case 90:
        if (event.ctrlKey) {
          this.undo();
          return false;
        }

        break;
      // ctrl+z

      case 89:
        if (event.ctrlKey) {
          this.redo();
          return false;
        }

        break;
      // ctrl+y
    }
  }

  _saveState() {
    let coordinates = this.activeFeature === null ? null : this.activeFeature instanceof Poly ? this.activeFeature.rings : this.activeFeature.position;

    this._states.setState({
      feature: this._activeFeature,
      coordinates: coordinates
    });
  }
  /**
   * Undo last change.
   */


  undo() {
    this._setState(this._states.undo());
  }
  /**
   * Redo a change that was undone.
   */


  redo() {
    this._setState(this._states.redo());
  }

  _setState(state) {
    if (!state || !this.activeLayer) return this._deselect();

    if (!state.coordinates && state.feature && this.activeLayer.features.indexOf(state.feature) >= 0) {
      this.activeFeature = null;
      this.activeLayer.remove(state.feature);
    } else if (state.coordinates && state.feature && this.activeLayer.features.indexOf(state.feature) < 0) {
      this._setCoordinates(state);

      this.activeLayer.add(state.feature);
      this.activeFeature = state.feature;
    } else if (state.coordinates) {
      this._setCoordinates(state);

      this.activeFeature = state.feature;
    }

    this._updateTransformControl();

    this.activeLayer.redraw();
  }

  _setCoordinates(state) {
    if (state.feature instanceof PointFeature) {
      state.feature.position = state.coordinates;
    } else if (state.feature instanceof Poly) {
      state.feature.coordinates = state.coordinates;
    }
  }

  get ignoreEvents() {
    return this._ignoreEvents;
  }

  set ignoreEvents(bool) {
    this._ignoreEvents = bool;
    this._pointEditor.ignoreEvents = bool;
    this._polyEditor.ignoreEvents = bool;
    this._polyTransform.ignoreEvents = bool;
  }

  get pointEditor() {
    return this._pointEditor;
  }

  get polyEditor() {
    return this._polyEditor;
  }

  get polyTransform() {
    return this._polyTransform;
  }

}

class MultiPointSymbol extends Symbol {
  constructor(baseSymbol) {
    super();
    this.baseSymbol = baseSymbol;
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof MultiPoint)) return [];
    let renders = [];
    feature.points.forEach(point => {
      let f = new PointFeature(point, {
        crs: feature.crs,
        symbol: this.baseSymbol
      });
      renders = renders.concat(f.render(resolution, crs));
    });
    return renders;
  }

}

/**
 * Represents a set of points on a map that behave as one feature: have same symbol, can be added, transformed or removed as one.
 * @alias sGis.feature.MultiPoint
 */

class MultiPoint extends Feature {
  constructor(points = [], _a = {}) {
    var {
      symbol = new MultiPointSymbol(new PointSymbol())
    } = _a,
        params = __rest(_a, ["symbol"]);

    super(Object.assign({
      symbol
    }, params));
    this._points = points;
  }
  /**
   * Set of points' coordinates.
   */


  get points() {
    return this._points;
  }

  set points(points) {
    this._points = points.slice();

    this._update();
  }

  projectTo(crs) {
    let projected = projectPoints(this.points, this.crs, crs);
    return new MultiPoint(projected, {
      symbol: this.symbol,
      crs: crs,
      persistOnMap: this.persistOnMap
    });
  }
  /**
   * Returns a copy of the feature. Only generic properties are copied.
   */


  clone() {
    return this.projectTo(this.crs);
  }
  /**
   * Adds a point to the end of the coordinates' list.
   * @param point - if sGis.IPoint instance is given, it will be automatically projected to the multipoint coordinate system.
   */


  addPoint(point) {
    if (point.position && point.crs) {
      this._points.push(point.projectTo(this.crs).position);
    } else {
      this._points.push([point[0], point[1]]);
    }

    this._update();
  }

  _update() {
    this._bbox = null;
    this.redraw();
  }

  get bbox() {
    if (this._bbox) return this._bbox;
    let xMin = Number.MAX_VALUE;
    let yMin = Number.MAX_VALUE;
    let xMax = Number.MIN_VALUE;
    let yMax = Number.MIN_VALUE;

    this._points.forEach(point => {
      xMin = Math.min(xMin, point[0]);
      yMin = Math.min(yMin, point[1]);
      xMax = Math.max(xMax, point[0]);
      yMax = Math.max(yMax, point[1]);
    });

    this._bbox = new Bbox([xMin, yMin], [xMax, yMax], this.crs);
    return this._bbox;
  }
  /**
   * @deprecated
   */


  get coordinates() {
    return this._points.slice();
  }

  set coordinates(points) {
    this.points = points;
  }

  get centroid() {
    return this.bbox.center;
  }

}

/**
 * Control for creating point features. When active, any click on the map will create a new point feature and add it
 * to the active layer. If active layer is not set, the point feature will be given through 'drawingFinish' event.
 * @alias sGis.controls.Point
 * @fires [[DrawingFinishEvent]]
 */

class PointControl extends Control {
  /**
   * @param map
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, {
    activeLayer,
    snappingProvider,
    snappingSymbol,
    isActive = false,
    symbol = new PointSymbol()
  } = {}) {
    super(map, {
      activeLayer,
      snappingProvider,
      snappingSymbol,
      useTempLayer: true
    });
    this._debouncedSnappingHandle = debounce(this._snappingHandle.bind(this), 50);
    this._handleClick = this._handleClick.bind(this);
    this._handleMouseMove = this._handleMouseMove.bind(this);
    this.symbol = symbol;
    this.isActive = isActive;
  }

  _activate() {
    this.map.on(sGisClickEvent.type, this._handleClick);
    this.map.on(sGisMouseMoveEvent.type, this._handleMouseMove);
  }

  _deactivate() {
    this.map.off(sGisClickEvent.type, this._handleClick);
    this.map.off(sGisMouseMoveEvent.type, this._handleMouseMove);
  }

  _handleClick(event) {
    event.stopPropagation();
    let clickEvent = event;
    let feature = new PointFeature(clickEvent.point.position, {
      crs: this.map.crs,
      symbol: this.symbol
    });
    if (this.activeLayer) this.activeLayer.add(feature);
    this.fire(new DrawingFinishEvent(feature, clickEvent.browserEvent));
  }

  _handleMouseMove(event) {
    let mouseMoveEvent = event;

    this._unsnap();

    this._debouncedSnappingHandle(mouseMoveEvent);
  }

  _snappingHandle(event) {
    this._snap(event.point.position, event.browserEvent.altKey);
  }

}

/**
 * Symbol of polyline drawn as simple line.
 * @alias sGis.symbol.polyline.Simple
 */

class PolylineSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Stroke color of the outline. Can be any valid css color string. */

    this.strokeColor = 'black';
    /** Stroke width of the outline. */

    this.strokeWidth = 1;
    /** @see [[PolyRender.lineDash]] */

    this.lineDash = [];
    /** Emulation CanvasRenderingContext2D.filter drop-shadow. */

    this.shadow = null;
    /** @see [[PolyRender.lineCap]] */

    this.lineCap = "round";
    /** @see [[PolyRender.lineJoin]] */

    this.lineJoin = "round";
    /** @see [[PolyRender.miterLimit]] */

    this.miterLimit = 10;
    Object.assign(this, options);
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof Poly)) return [];
    let coordinates = this.getRenderedCoordinates(feature, resolution, crs);
    return [new PolyRender(coordinates, {
      fillStyle: FillStyle.None,
      enclosed: false,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      lineDash: this.lineDash,
      shadow: this.shadow,
      lineCap: this.lineCap,
      lineJoin: this.lineJoin,
      miterLimit: this.miterLimit
    })];
  }
  /**
   * Projects coordinates of a poly feature to the requested crs and resolution.
   * @param feature
   * @param resolution
   * @param crs
   * @param tolerance
   */


  getRenderedCoordinates(feature, resolution, crs, tolerance) {
    let result;

    if (this.previousPoly === feature && this.previousCrs.equals(crs) && this.previousResolution === resolution && feature.lastAction && feature.lastAction.type !== Action.removePoint) {
      const lastAction = feature.lastAction;
      let newResult = this.previousSimplified;

      if (lastAction.type === Action.movePoint) {
        const newSimplifiedRing = simplifyCoordinates([feature.rings[lastAction.ringIndex]].map(ring => {
          return ring.map(point => {
            return [Math.round(point[0] / resolution), Math.round(point[1] / -resolution)];
          });
        }), tolerance);
        newResult[lastAction.ringIndex] = newSimplifiedRing[0];
      }

      if (lastAction.type === Action.addPoint) {
        const renderCoordinates = [Math.round(lastAction.coordinates[0] / resolution), Math.round(lastAction.coordinates[1] / -resolution)];
        newResult[lastAction.ringIndex].push(renderCoordinates);
      }

      result = newResult;
    } else {
      result = getRenderedPolygonCoordinates(feature, resolution, crs, tolerance);
    }

    this.previousSimplified = result;
    this.previousPoly = feature;
    this.previousCrs = crs;
    this.previousResolution = resolution;
    feature.lastAction = null;
    return result;
  }

}
registerSymbol(PolylineSymbol, 'polyline.Simple', ['strokeColor', 'strokeWidth', 'shadow', 'lineCap', 'lineDash', 'lineJoin', 'miterLimit']);

/**
 * A line or a set of geographical lines.
 * @alias sGis.feature.Polyline
 */

class Polyline extends Poly {
  constructor(rings, _a = {}) {
    var {
      symbol = new PolylineSymbol()
    } = _a,
        params = __rest(_a, ["symbol"]);

    super(rings, Object.assign({
      symbol
    }, params));
    this.isEnclosed = false;
  }
  /**
   * Returns a copy of the feature. Only generic properties are copied.
   */


  clone() {
    return new Polyline(this.rings, {
      crs: this.crs,
      symbol: this.originalSymbol
    });
  }

  projectTo(crs) {
    let projected = projectRings(this.rings, this.crs, crs);
    return new Polyline(projected, {
      crs: this.crs,
      symbol: this.symbol,
      persistOnMap: this.persistOnMap
    });
  }

}

/**
 * Control for drawing polyline features.
 * @alias sGis.controls.Polyline
 */

class PolylineControl extends PolyControl {
  /**
   * @param map - map the control will work with
   * @param properties - key-value set of properties to be set to the instance
   */
  constructor(map, _a = {}) {
    var {
      symbol = new PolylineSymbol()
    } = _a,
        controlOptions = __rest(_a, ["symbol"]);

    super(map, Object.assign({
      symbol
    }, controlOptions));
  }

  _getNewFeature(position) {
    return new Polyline([[position, position]], {
      crs: this.map.crs,
      symbol: this.symbol
    });
  }

}

/**
 * Control for drawing polygon features.
 * @alias sGis.control.Polygon
 */

class PolygonControl extends PolyControl {
  /**
   * @param map - map the control will work with
   * @param properties - key-value set of properties to be set to the instance
   */
  constructor(map, _a = {}) {
    var {
      symbol = new PolygonSymbol()
    } = _a,
        controlOptions = __rest(_a, ["symbol"]);

    super(map, Object.assign({
      symbol
    }, controlOptions));
  }

  _getNewFeature(position) {
    return new Polygon([[position, position]], {
      crs: this.map.crs,
      symbol: this.symbol
    });
  }

}

/**
 * Control for drawing rectangles by dragging from corner to corner.
 * @alias sGis.controls.Rectangle
 */

class Rectangle extends PolyDrag {
  _getNewCoordinates(point) {
    const position = point.position;
    return [[position, position, position, position]];
  }

  _getUpdatedCoordinates(point) {
    const baseCoord = this._activeFeature.rings[0][0];
    const pointCoord = point.position;
    return [[baseCoord, [baseCoord[0], pointCoord[1]], pointCoord, [pointCoord[0], baseCoord[1]]]];
  }

}

class StaticHtmlImageRender extends StaticImageRender {
  constructor({
    src,
    bbox,
    width,
    height,
    opacity,
    onLoad,
    onLoadStart,
    onLoadEnd,
    onDisplayed = undefined,
    onRemoved = undefined
  }) {
    super({
      src,
      width,
      height,
      opacity,
      onLoad,
      onLoadStart,
      onLoadEnd
    });
    this.bbox = bbox;
    this.onDisplayed = onDisplayed;
    this.onRemoved = onRemoved;
  }

  contains(position) {
    let resolution = this.bbox.width / this.width;
    return this.bbox.contains(new Point$1([position[0] * resolution, -position[1] * resolution], this.bbox.crs));
  }

}

/**
 * Represents a layer that is fully drawn by server and is displayed as an image overlay.
 * @alias sGis.DynamicLayer
 */

class DynamicLayer extends Layer {
  /**
   * @param properties - properties to be set to the corresponding fields
   * @param extensions - [JS ONLY]additional properties to be copied to the created instance
   */
  constructor(properties = {}, extensions) {
    super(properties, extensions);
    this._forceUpdate = false;
    this.delayedUpdate = true;
    const {
      onLoad,
      onLoadStart,
      onLoadEnd,
      onError
    } = properties;
    this.onLoad = onLoad;
    this.onLoadStart = onLoadStart;
    this.onLoadEnd = onLoadEnd;
    this.onError = onError;
  }

  getRenders(bbox, resolution) {
    if (!this.checkVisibility(resolution)) return [];

    if (this.crs) {
      if (bbox.crs.canProjectTo(this.crs)) {
        bbox = bbox.projectTo(this.crs);
      } else {
        return [];
      }
    }

    let needRedraw = this._forceUpdate || !this._currentRender || !bbox.equals(this._currentRender.bbox);

    if (needRedraw) {
      this._loadNextRender(bbox, resolution);
    }

    if (this._nextRender !== this._currentRender && this._nextRender && this._nextRender.isImageLoaded) {
      this._currentRender = this._nextRender;
    }

    this._forceUpdate = false;
    return this._currentRender && !this._currentRender.error ? [this._currentRender] : [];
  }

  _loadNextRender(bbox, resolution) {
    if (this._currentRender === this._nextRender) {
      let height = Math.round(bbox.height / resolution);
      let width = Math.round(bbox.width / resolution);
      let src = this.getUrl(bbox, resolution); // we only load images for changed bbox, so it's safe to disable cache here

      src += `&ts=${Date.now()}`;
      this._nextRender = new StaticHtmlImageRender({
        src,
        bbox,
        height,
        width,
        opacity: this.opacity,
        onLoadStart: this.onLoadStart,
        onLoadEnd: this.onLoadEnd,
        onLoad: () => {
          this._startNextLoad();

          this.redraw();
        },
        onError: err => {
          this.fire(new LayerErrorEvent(err));
        }
      });
      this._toLoad = null;
    } else {
      if (this._nextRender && !this._nextRender.isImageLoaded) {
        this._nextRender.deleteNode(); // create new new StaticHtmlImageRender


        this._nextRender = this._currentRender;

        this._loadNextRender(bbox, resolution);
      }

      this._toLoad = {
        bbox,
        resolution
      };
    }
  }

  _startNextLoad() {
    if (this._toLoad) {
      this._loadNextRender(this._toLoad.bbox, this._toLoad.resolution);

      this._toLoad = null;
    }
  }
  /**
   * Ensures update of the layer image
   */


  forceUpdate() {
    this._forceUpdate = true;
    this.fire(new PropertyChangeEvent('source'));
  }

  get opacity() {
    return this.getOpacity();
  }

  set opacity(opacity) {
    this.setOpacity(opacity);
  }

  setOpacity(value) {
    if (this._currentRender) this._currentRender.opacity = value;
    if (this._nextRender) this._nextRender.opacity = value;
    super.setOpacity(value);
  }
  /**
   * Coordinate system of the layer
   */


  get crs() {
    return this._crs;
  }

  set crs(crs) {
    this._crs = crs;
  }

}

const MIN_WHEEL_DELAY = 300;
/**
 * @alias sGis.painter.domPainter.EventDispatcher
 */

class EventDispatcher {
  constructor(baseNode, master) {
    this._hoverRender = null;
    this._master = master;

    this._setListeners(baseNode);

    this._baseNode = baseNode;
    this._onDocumentMousemove = this._onDocumentMousemove.bind(this);
    this._onDocumentMouseup = this._onDocumentMouseup.bind(this);
    this._wheelTimer = 0;
    this._touchHandler = {
      dragPrevPosition: []
    };
  }

  _dispatchEvent(event) {
    let map = this._master.map;
    let layerRenderers = this._master.layerRenderers;
    let position = [event.point.x / map.resolution, -event.point.y / map.resolution];

    if (event instanceof sGisMouseOutEvent) {
      if (this._hoverRender) this._hoverRender.triggerEvent(event);
      this._hoverRender = null;
      if (!event.isCanceled) map.fire(event);
      return;
    }

    let mouseOutEvent, mouseOverEvent;
    let targetRender = null;

    for (let i = layerRenderers.length - 1; i >= 0; i--) {
      targetRender = layerRenderers[i].getEventCatcher(event.eventFlag, position);
      if (targetRender) break;
    }

    if (!mouseOutEvent && event instanceof sGisMouseMoveEvent && this._hoverRender !== targetRender) {
      if (this._hoverRender) {
        mouseOutEvent = new sGisMouseOutEvent(event);

        this._hoverRender.triggerEvent(mouseOutEvent);
      }

      if (targetRender) {
        mouseOverEvent = new sGisMouseOverEvent(event);
        targetRender.triggerEvent(mouseOverEvent);
      }

      this._hoverRender = targetRender;
    }

    if (targetRender) targetRender.triggerEvent(event);
    if (!event.isCanceled) map.fire(event);
  }

  _listenFor(node, eventType, handler) {
    listenDomEvent(node, eventType, event => {
      let target = event && event.target;
      let cancelEvent = event.defaultPrevented;

      while (target && target !== node) {
        if (target.doNotBubbleToMap) {
          cancelEvent = true;
          break;
        }

        target = target.parentNode;
      }

      if (!cancelEvent) handler(event);
    });
  }

  _setListeners(baseNode) {
    this._listenFor(baseNode, 'mousedown', this._onmousedown.bind(this));

    this._listenFor(baseNode, 'mousedown', this._onmousedown.bind(this));

    this._listenFor(baseNode, 'wheel', this._onwheel.bind(this));

    this._listenFor(baseNode, 'click', this._onclick.bind(this));

    this._listenFor(baseNode, 'dblclick', this._ondblclick.bind(this));

    this._listenFor(baseNode, 'mousemove', this._onmousemove.bind(this));

    this._listenFor(baseNode, 'mouseleave', this._onmouseleave.bind(this));

    this._listenFor(baseNode, 'touchstart', this._ontouchstart.bind(this));

    this._listenFor(baseNode, 'touchmove', this._ontouchmove.bind(this));

    this._listenFor(baseNode, 'touchend', this._ontouchend.bind(this));
  }

  _onmousedown(event) {
    this._clickCatcher = true;

    if (event.which === 1) {
      this._dragPosition = getMouseOffset(event.currentTarget, event);
      listenDomEvent(document, 'mousemove', this._onDocumentMousemove);
      listenDomEvent(document, 'mouseup', this._onDocumentMouseup);

      document.ondragstart = function () {
        return false;
      };

      document.body.onselectstart = function () {
        return false;
      };
    }

    event.preventDefault();
    event.stopPropagation();
  }

  _onDocumentMousemove(event) {
    let map = this._master.map;
    let mousePosition = getMouseOffset(this._master.wrapper, event);
    let dxPx = this._dragPosition.x - mousePosition.x;
    let dyPx = this._dragPosition.y - mousePosition.y;
    let resolution = map.resolution;

    let point = this._master.getPointFromPxPosition(mousePosition.x, mousePosition.y);

    if (Math.abs(dxPx) > 2 || Math.abs(dyPx) > 2 || !this._clickCatcher) {
      this._lastDrag = {
        x: dxPx * resolution,
        y: -dyPx * resolution
      };

      if (this._clickCatcher) {
        this._clickCatcher = null;

        let originalPoint = this._master.getPointFromPxPosition(this._dragPosition.x, this._dragPosition.y);

        let dragStartEvent = new DragStartEvent(map, {
          point: originalPoint,
          browserEvent: event
        });

        this._dispatchEvent(dragStartEvent);

        this._draggingObject = dragStartEvent.draggingObject;
      }

      this._dragPosition = mousePosition;
      let dragEvent = new DragEvent({
        point,
        browserEvent: event,
        offset: [this._lastDrag.x, this._lastDrag.y],
        pxOffset: [dxPx, dyPx]
      });

      this._draggingObject.fire(dragEvent);
    }
  }

  _onDocumentMouseup(event) {
    this._clearDocumentListeners();

    if (this._draggingObject) {
      this._draggingObject.fire(new DragEndEvent(this._getMouseEventDescription(event)));
    }

    this._draggingObject = null;
    this._lastDrag = null;
  }

  remove() {
    this._clearDocumentListeners();
  }

  _clearDocumentListeners() {
    removeDomEventListener(document, 'mousemove', this._onDocumentMousemove);
    removeDomEventListener(document, 'mouseup', this._onDocumentMouseup);
    document.ondragstart = null;
    document.body.onselectstart = null;
  }

  _onwheel(event) {
    let time = Date.now();

    if (time - this._wheelTimer > MIN_WHEEL_DELAY) {
      this._wheelTimer = time;
      let map = this._master.map;
      let wheelDirection = getWheelDirection(event);
      let mouseOffset = getMouseOffset(event.currentTarget, event);
      map.zoom(wheelDirection, this._master.getPointFromPxPosition(mouseOffset.x, mouseOffset.y));
    }

    event.preventDefault();
  }

  _getMouseEventDescription(event) {
    let mouseOffset = getMouseOffset(event.currentTarget, event);

    let point = this._master.getPointFromPxPosition(mouseOffset.x, mouseOffset.y);

    return {
      point: point,
      browserEvent: event
    };
  }

  _onclick(event) {
    if (this._clickCatcher) {
      this._dispatchEvent(new sGisClickEvent(this._getMouseEventDescription(event)));
    }
  }

  _ondblclick(event) {
    this._clickCatcher = null;

    this._dispatchEvent(new sGisDoubleClickEvent(this._getMouseEventDescription(event)));
  }

  _onmousemove(event) {
    this._dispatchEvent(new sGisMouseMoveEvent(this._getMouseEventDescription(event)));
  }

  _onmouseleave(event) {
    if (event.target === this._baseNode) this._dispatchEvent(new sGisMouseOutEvent(this._getMouseEventDescription(event)));
  }

  _ontouchstart(event) {
    if (!this._touches) this._touches = [];

    for (let i = 0; i < event.changedTouches.length; i++) {
      let touch = event.changedTouches[i];
      if (!this._touches.some(t => t.id === touch.identifier)) this._touches.push({
        id: touch.identifier,
        position: [touch.pageX, touch.pageY]
      });
    }

    this._touchHandler.lastDrag = {
      x: 0,
      y: 0
    };
    if (event.touches.length > 1) event.preventDefault();
  }

  _ontouchmove(event) {
    this._clearTouches(event);

    let touches = Array.prototype.slice.apply(event.touches);
    let map = this._master.map;

    if (touches.length === 1 && this._touchHandler.lastDrag) {
      let touch = event.targetTouches[0];
      let [prevX, prevY] = this._touches[0].position;
      let dxPx = prevX - touch.pageX;
      let dyPx = prevY - touch.pageY;
      let resolution = map.resolution;
      let touchOffset = getMouseOffset(event.currentTarget, touch);

      let point = this._master.getPointFromPxPosition(touchOffset.x, touchOffset.y);

      let fakeMouseEvent = new MouseEvent('mousemove', event);

      if (this._touchHandler.lastDrag.x === 0 && this._touchHandler.lastDrag.y === 0) {
        let dragStartEvent = new DragStartEvent(map, {
          point,
          browserEvent: fakeMouseEvent
        });

        this._dispatchEvent(dragStartEvent);

        this._draggingObject = dragStartEvent.draggingObject;
      }

      this._touchHandler.lastDrag = {
        x: dxPx * resolution,
        y: 0 - dyPx * resolution
      };
      let dragEvent = new DragEvent({
        point,
        browserEvent: fakeMouseEvent,
        offset: [this._touchHandler.lastDrag.x, this._touchHandler.lastDrag.y],
        pxOffset: [dxPx, dyPx]
      });

      this._draggingObject.fire(dragEvent);

      this._touches[0].position = [touch.pageX, touch.pageY];
    } else if (touches.length > 1) {
      this._master.forbidUpdate();

      this._touchHandler.lastDrag = null;
      this._touchHandler.scaleChanged = true;
      let t1 = touches.find(t => t.identifier === this._touches[0].id);
      let t2 = touches.find(t => t.identifier === this._touches[1].id);
      let [x11, y11] = this._touches[0].position;
      let [x12, y12] = [t1.pageX, t1.pageY];
      let [x21, y21] = this._touches[1].position;
      let [x22, y22] = [t2.pageX, t2.pageY];
      let c1 = [(x11 + x21) / 2, (y11 + y21) / 2];
      let c2 = [(x12 + x22) / 2, (y12 + y22) / 2];
      let base = [(c1[0] + c2[0]) / 2, (c1[1] + c2[1]) / 2];
      let len1 = Math.sqrt(Math.pow(x11 - x21, 2) + Math.pow(y11 - y21, 2));
      let len2 = Math.sqrt(Math.pow(x12 - x22, 2) + Math.pow(y12 - y22, 2));
      let baseOffset = getMouseOffset(event.target, {
        pageX: base[0],
        pageY: base[1]
      });

      let basePoint = this._master.getPointFromPxPosition(baseOffset.x, baseOffset.y);

      let dc = [c1[0] - c2[0], c2[1] - c1[1]];
      let zoomK = len1 / len2;
      if (len1 !== len2 && len2 !== 0) map.changeScale(zoomK, basePoint, true);
      map.move(dc[0] * map.resolution, dc[1] * map.resolution);
      this._touches[0].position = [x12, y12];
      this._touches[1].position = [x22, y22];
      this._touchHandler.lastZoomDirection = zoomK < 1;
    }

    event.preventDefault();
  }

  _ontouchend(event) {
    this._clearTouches(event);

    for (let i = 0; i < event.changedTouches.length; i++) {
      let index = this._touches.findIndex(touch => touch.id === event.changedTouches[i].identifier);

      if (index >= 0) this._touches.splice(index, 1);
    }

    this._touchHandler.lastDrag = null;

    if (this._touchHandler.scaleChanged) {
      this._master.allowUpdate();

      this._master.map.adjustResolution(this._touchHandler.lastZoomDirection);

      this._touchHandler.scaleChanged = false;
    } else {
      if (this._draggingObject) {
        this._draggingObject.fire(new DragEndEvent({
          point: null,
          browserEvent: new MouseEvent('mouseup', event)
        }));

        this._draggingObject = null;
      }
    }
  }

  _clearTouches(event) {
    let touches = Array.prototype.slice.apply(event.touches);

    for (let i = this._touches.length - 1; i >= 0; i--) {
      if (!touches.some(touch => touch.identifier === this._touches[i].id)) this._touches.splice(i, 1);
    }
  }

}

const DEFAULT_CHUNK_SIZE = 50;
class PathRender extends VectorRender {
  constructor({
    path,
    renders,
    chunkSize = DEFAULT_CHUNK_SIZE
  }) {
    super();
    this.cachedPaths = {};
    this.renders = renders;
    this.chunkSize = chunkSize;

    if (path) {
      this.path = path;
    } else {
      this.initializePath();
    }
  }

  initializePath() {
    this.path = new Path2D();
    this.addRendersToPath();
  }

  addRendersToPath() {
    this.validateCache();

    for (let i = 0; i <= Math.round(this.renders.length / this.chunkSize); i++) {
      if (this.cachedPaths[i] && !this.cachedPaths[i].updateNeeded) {
        this.path.addPath(this.cachedPaths[i].path);
        this.cachedPaths[i] = Object.assign(Object.assign({}, this.cachedPaths[i]), {
          hadUpdate: false
        });
      } else {
        let path = new Path2D();

        for (let j = 0; j < this.chunkSize; j++) {
          const render = this.renders[i * this.chunkSize + j];

          if (!render) {
            break;
          }

          this.addObject(path, render);
        }

        this.cachedPaths[i] = {
          path,
          updateNeeded: false,
          hadUpdate: true
        };
        this.path.addPath(path);
      }
    }

    this.previousRenders = this.renders;
  }

  validateCache() {
    if (!this.previousRenders) {
      return;
    }

    const isAdd = this.previousRenders.length < this.renders.length;
    const isDelete = this.previousRenders.length > this.renders.length;

    if (isDelete) {
      this.cachedPaths = {};
      return;
    }

    for (let i = 0; i <= Math.round(this.renders.length / this.chunkSize); i++) {
      for (let j = 0; j < this.chunkSize; j++) {
        const render = this.renders[i * this.chunkSize + j];

        if (!render) {
          break;
        }

        if (render && render.updateNeeded) {
          this.updateNeeded = true;
          this.cachedPaths[i] = Object.assign(Object.assign({}, this.cachedPaths[i]), {
            updateNeeded: true
          });

          if (isAdd && this.cachedPaths[i + 1]) {
            for (let j = i + 1; j <= Math.round(this.renders.length / this.chunkSize); j++) {
              if (this.cachedPaths[j]) {
                this.cachedPaths[j] = Object.assign(Object.assign({}, this.cachedPaths[j]), {
                  updateNeeded: true
                });
              }
            }

            return;
          }

          break;
        }
      }
    }
  }

  updateRenders(renders) {
    this.path = new Path2D();
    this.renders = renders;
    this.addRendersToPath();
  }

  contains(position) {
    return this.renders.findIndex(render => render.contains(position)) !== -1;
  }

}

var HorizontalAlignment;

(function (HorizontalAlignment) {
  HorizontalAlignment[HorizontalAlignment["Left"] = 0] = "Left";
  HorizontalAlignment[HorizontalAlignment["Center"] = 1] = "Center";
  HorizontalAlignment[HorizontalAlignment["Right"] = 2] = "Right";
})(HorizontalAlignment || (HorizontalAlignment = {}));

var VerticalAlignment;

(function (VerticalAlignment) {
  VerticalAlignment[VerticalAlignment["Top"] = 0] = "Top";
  VerticalAlignment[VerticalAlignment["Middle"] = 1] = "Middle";
  VerticalAlignment[VerticalAlignment["Bottom"] = 2] = "Bottom";
})(VerticalAlignment || (VerticalAlignment = {}));
/**
 * Text label that is drawn as a vector element. This render prerenders itself to the offscreen canvas and then allows
 * layer renderer to draw it as a simple image.
 */


class VectorLabel extends StaticRender {
  constructor({
    position,
    text,
    horizontalAlignment = HorizontalAlignment.Right,
    verticalAlignment = VerticalAlignment.Middle,
    fontSize = 14,
    fontFamily = 'arial',
    fontStyle = null,
    fontWeight,
    strokeColor = 'white',
    strokeWidth = 2,
    fillColor = 'black'
  }) {
    super();
    this.offset = [0, 0];
    this._text = text;
    this.horizontalAlignment = horizontalAlignment;
    this.verticalAlignment = verticalAlignment;
    this.fontSize = fontSize;
    this.fontFamily = fontFamily;
    this.fontStyle = fontStyle;
    this.fontWeight = fontWeight;
    this.strokeColor = strokeColor;
    this.strokeWidth = strokeWidth;
    this.fillColor = fillColor;
    this.position = position;
  }

  contains(position) {
    let dx = position[0] - this.position[0];
    let dy = position[1] - this.position[1];
    return this._boxContains(dx, dy);
  }

  _boxContains(x, y) {
    let minX = this.position[0] + this.offset[0];
    let minY = this.position[1] + this.offset[1];
    return x > minX && x < minX + this.width && y > minY && y < minY + this.height;
  }

  get node() {
    if (this._canvas) return this._canvas;

    this._render();

    return this._canvas;
  }

  get width() {
    return this.node.width;
  }

  get height() {
    return this.node.height;
  }

  get font() {
    let font = '';
    if (this.fontStyle) font += this.fontStyle + ' ';
    if (this.fontWeight) font += this.fontWeight + ' ';
    font += `${this.fontSize}px ${this.fontFamily}`;
    return font;
  }

  _render() {
    this._canvas = document.createElement('canvas');
    this._canvas.height = this._canvas.width = 0;

    let ctx = this._canvas.getContext('2d');

    ctx.font = this.font;
    let measure = ctx.measureText(this._text);
    this._canvas.width = Math.ceil(measure.width);
    this._canvas.height = Math.ceil(this.fontSize);
    let dy = 0;

    if (this.verticalAlignment === VerticalAlignment.Bottom) {
      ctx.textBaseline = 'bottom';
      dy = this._canvas.height;
    } else if (this.verticalAlignment === VerticalAlignment.Middle) {
      ctx.textBaseline = 'middle';
      dy = this._canvas.height / 2;
    } else {
      ctx.textBaseline = 'top';
    }

    ctx.font = this.font;

    if (this.strokeWidth > 0) {
      ctx.strokeStyle = this.strokeColor;
      ctx.lineWidth = this.strokeWidth;
      ctx.strokeText(this._text, 0, dy);
    }

    ctx.fillStyle = this.fillColor;
    ctx.fillText(this._text, 0, dy);

    this._setOffset();
  }

  _setOffset() {
    let dx = 0;
    let dy = 0;

    if (this.horizontalAlignment === HorizontalAlignment.Left) {
      dx = -this._canvas.width;
    } else if (this.horizontalAlignment === HorizontalAlignment.Center) {
      dx = -this._canvas.width / 2;
    }

    if (this.verticalAlignment === VerticalAlignment.Top) {
      dy = -this._canvas.height;
    } else if (this.verticalAlignment === VerticalAlignment.Middle) {
      dy = -this._canvas.height / 2;
    }

    this.offset = [Math.round(dx), Math.round(dy)];
  }

}

/**
 * @alias sGis.painter.domPainter.Canvas
 * @ignore
 */

class Canvas {
  constructor(canvasNode) {
    if (canvasNode) {
      this._canvasNode = canvasNode;
      this._ctx = this._canvasNode.getContext('2d');
    } else {
      this._createNode();
    }
  }

  _createNode() {
    this._canvasNode = document.createElement('canvas');
    this._canvasNode.style.pointerEvents = 'none';
    this._ctx = this._canvasNode.getContext('2d');
  }

  reset(bbox, resolution, width, height) {
    this._ctx.clearRect(0, 0, this._canvasNode.width, this._canvasNode.height);

    this._canvasNode.width = width;
    this._canvasNode.height = height;
    this._isEmpty = true;

    this._ctx.translate(Math.round(-bbox.xMin / resolution), Math.round(bbox.yMax / resolution));

    this.bbox = bbox;
  }

  get width() {
    return this._canvasNode.width;
  }

  get height() {
    return this._canvasNode.height;
  }

  draw(render, map) {
    if (render instanceof VectorRender) {
      render.drawOnCanvas(this._ctx, map);
    } else if (render instanceof StaticVectorImageRender || render instanceof VectorLabel) {
      this._drawImage(render);
    } else {
      error('Unknown vector geometry type.');
    }

    this._isEmpty = false;
  }

  setIndex(index) {
    if (index !== this.zIndex) {
      this._canvasNode.style.zIndex = index;
      this.zIndex = index;
    }
  }

  _drawImage(render) {
    let [x, y] = render.position;
    x = Math.round(x);
    y = Math.round(y);

    this._ctx.translate(x, y);

    if (!(render instanceof VectorLabel)) {
      this._ctx.rotate(render.angle);
    }

    let opacity = !(render instanceof VectorLabel) ? render.opacity : 1;
    if (opacity !== 1) this._ctx.globalAlpha = opacity;

    this._ctx.drawImage(render.node, render.offset[0], render.offset[1], render.width, render.height);

    if (opacity !== 1) this._ctx.globalAlpha = 1;

    if (!(render instanceof VectorLabel)) {
      this._ctx.rotate(-render.angle);
    }

    this._ctx.translate(-x, -y);
  }

  get isEmpty() {
    return this._isEmpty;
  }

  get node() {
    return this._canvasNode;
  }

}

/**
 * @alias sGis.painter.domPainter.CanvasGroupRenderer
 * @ignore
 */

class CanvasGroupRenderer {
  constructor(canvasContainer, domPainter) {
    this.canvasGroups = [];
    this.canvasContainer = canvasContainer;
    this.domPainter = domPainter;
  }

  setIndex(index) {
    if (this.zIndex !== index) {
      for (let canvasGroup of this.canvasGroups) {
        if (canvasGroup.canvas) {
          canvasGroup.canvas.setIndex(index + 1);
        }
      }

      this.zIndex = index + 1;
    }
  }

  removeCanvas(canvas) {
    if (this.canvasContainer) this.canvasContainer.removeNode(canvas.node);
  }

  resetCanvas(canvas, bbox) {
    this.removeCanvas(canvas);
    canvas.reset(bbox, this.domPainter.map.resolution, this.domPainter.width, this.domPainter.height);
  }

  drawRender(canvas, render) {
    canvas.draw(render, this.domPainter.map);
  }

  addCanvasToDom(canvas, bbox) {
    this.canvasContainer.addNode(canvas.node, canvas.width, canvas.height, bbox);
  }

  clear() {
    for (let i = 0; i < this.canvasGroups.length; i++) {
      const {
        canvas
      } = this.canvasGroups[i];

      if (canvas && canvas.node.parentNode) {
        this.canvasContainer.removeNode(canvas.node);
      }

      this.canvasGroups[i] = {
        canvas: null,
        render: null,
        updateNeeded: false
      };
    }
  }

  moveToContainer(canvasContainer) {
    for (let canvasGroup of this.canvasGroups) {
      const {
        canvas
      } = canvasGroup;

      if (canvas && canvas.node.parentNode) {
        this.canvasContainer.removeNode(canvas.node);
        canvasContainer.addNode(canvas.node, canvas.width, canvas.height, canvas.bbox);
      }
    }

    this.canvasContainer = canvasContainer;
  }

  render(renders) {
    var _a;

    if (this.canvasGroups.length === 0) {
      this.canvasGroups = new Array(renders.length);
    }

    const canvasGroupsToRender = [];

    for (let i = 0; i < renders.length; i++) {
      const canvas = ((_a = this.canvasGroups[i]) === null || _a === void 0 ? void 0 : _a.canvas) || new Canvas();
      canvas.setIndex(renders[i] instanceof PathRender ? this.zIndex + 1 : this.zIndex);
      this.canvasGroups[i] = {
        canvas: canvas,
        render: renders[i],
        updateNeeded: renders[i].updateNeeded
      };
      canvasGroupsToRender.push({
        canvas: canvas,
        render: renders[i],
        updateNeeded: renders[i].updateNeeded
      });
    }

    const bbox = this.domPainter.bbox;
    const activeCanvasGroups = this.canvasGroups.filter(({
      canvas
    }) => !!canvas);

    for (let i = 0; i < activeCanvasGroups.length - renders.length; i++) {
      const canvas = activeCanvasGroups[activeCanvasGroups.length - 1 - i].canvas;
      this.resetCanvas(canvas, bbox);
    }

    const canvasGroupsToUpdate = canvasGroupsToRender.filter(({
      updateNeeded
    }) => updateNeeded);

    for (let canvasGroup of canvasGroupsToUpdate) {
      this.resetCanvas(canvasGroup.canvas, bbox);
      this.drawRender(canvasGroup.canvas, canvasGroup.render);

      if (!canvasGroup.canvas.isEmpty) {
        this.addCanvasToDom(canvasGroup.canvas, bbox);
      }
    }
  }

}

/**
 * @alias sGis.painter.domPainter.LayerRenderer
 * @ignore
 */

class LayerRenderer {
  /**
   * @constructor
   * @param master
   * @param layer
   * @param index
   * @param useCanvas
   */
  constructor(master, layer, index, useCanvas = true) {
    this.delayedUpdateTime = 500;
    this.listensFor = [MouseEventFlags.MouseClick, MouseEventFlags.DoubleClick, MouseEventFlags.MouseDown, MouseEventFlags.MouseMove, MouseEventFlags.MouseOut, MouseEventFlags.MouseOver, MouseEventFlags.MouseUp, MouseEventFlags.DragStart];
    this._renders = [];
    this._master = master;
    this._layer = layer;

    if (layer instanceof FeatureLayer) {
      this._useCanvasGroupRenderer = true;
      this._canvasGroupRenderer = new CanvasGroupRenderer(this._currentContainer, master);
    } else {
      this._useCanvas = useCanvas;
    }

    this._canvas = new Canvas();

    this._resetEventCatcherMaps();

    this._setListeners();

    this.setIndex(index);

    this._forceUpdate();
  }

  get layer() {
    return this._layer;
  }

  _setListeners() {
    this._layer.on('propertyChange', () => {
      this._forceUpdate();
    });
  }

  _resetEventCatcherMaps() {
    this._eventCatchers = {};
    this.listensFor.forEach(eventName => {
      this._eventCatchers[eventName] = new Map();
    });
  }

  _forceUpdate() {
    this.updateNeeded = true;
  }

  setIndex(index) {
    if (index === this._index) return;
    let zIndex = index * 2 + 1;

    for (let render of this._renders) {
      if (render instanceof StaticImageRender || render instanceof DynamicRender) {
        render.node.style.zIndex = zIndex.toString();
      }
    }

    this._canvas.setIndex(index * 2);

    if (this._useCanvasGroupRenderer) {
      this._canvasGroupRenderer.setIndex(index * 2);
    }

    this._index = index;
    this._zIndex = zIndex;
  }

  clear() {
    for (let render of this._renders) {
      this._removeRender(render);
    }

    this._renders = [];

    this._resetEventCatcherMaps();

    if (this._canvasContainer) this._canvasContainer.removeNode(this._canvas.node);
    if (this._updateTimer) clearTimeout(this._updateTimer);
    if (this._canvasGroupRenderer) this._canvasGroupRenderer.clear();
  }

  update() {
    if (this._layer.delayedUpdate) {
      if (this._updateTimer) clearTimeout(this._updateTimer);

      if (this.updateNeeded) {
        this._rerender();
      } else {
        this._updateTimer = setTimeout(() => {
          this._rerender();
        }, this.delayedUpdateTime);
      }
    } else {
      this._rerender();
    }

    this.updateNeeded = false;
  }

  updateDynamic() {
    this._renders.forEach(render => {
      if (render instanceof DynamicRender) render.update(this._master.bbox, this._master.map.resolution);
    });
  }

  _removeOutdatedRenders(newRenders) {
    for (let i = this._renders.length - 1; i >= 0; i--) {
      if (newRenders.indexOf(this._renders[i]) < 0) {
        this._removeRender(this._renders[i]);
      }
    }
  }

  _removeCanvas() {
    if (this._canvasContainer) this._canvasContainer.removeNode(this._canvas.node);
  }

  _addCanvasToDom(bbox) {
    this._canvasContainer = this.currentContainer;

    this._canvasContainer.addNode(this._canvas.node, this._canvas.width, this._canvas.height, bbox);
  }

  _resetCanvas(bbox) {
    this._removeCanvas();

    this._canvas.reset(bbox, this._master.map.resolution, this._master.width, this._master.height);
  }

  _renderWithCanvasGroup(renders, bbox) {
    let complexRenders = [];

    for (let render of renders) {
      if (render instanceof PolyRender || render instanceof PathRender) {
        complexRenders.push(render);

        this._setRenderListeners(render);
      } else if (render instanceof StaticVectorImageRender || render instanceof VectorRender || this._renders.indexOf(render) < 0) {
        this._drawRender(render);
      }
    }

    this._canvasGroupRenderer.render(complexRenders);

    if (!this._canvas.isEmpty) {
      this._addCanvasToDom(bbox);
    }
  }

  _renderWithCanvas(renders, bbox) {
    this._draw(renders);

    if (!this._canvas.isEmpty) {
      this._addCanvasToDom(bbox);
    }
  }

  _rerender() {
    let bbox = this._master.bbox;

    let renders = this._layer.getRenders(bbox, this._master.map.resolution);

    if (this._layer.updateProhibited) return;
    if (this.currentContainer !== this._master.currContainer) this.moveToLastContainer();

    this._resetCanvas(bbox);

    this._removeOutdatedRenders(renders);

    if (this._useCanvasGroupRenderer) {
      this._renderWithCanvasGroup(renders, bbox);
    } else {
      const vectorImages = renders.filter(r => r instanceof StaticVectorImageRender);

      if (vectorImages.length > 0 && renders.length !== vectorImages.length) {
        this._drawVectorSync(renders, vectorImages, bbox);
      } else {
        this._renderWithCanvas(renders, bbox);
      }
    }

    this._renders = renders;
  }

  _drawVectorSync(renders, images, bbox) {
    Promise.all(images.map(({
      readyPromise
    }) => readyPromise)).then(() => {
      this._renderWithCanvas(renders, bbox);

      this._renders = renders;
    });
  }

  _draw(renders) {
    for (let render of renders) {
      if (render instanceof StaticVectorImageRender || render instanceof VectorRender || this._renders.indexOf(render) < 0) {
        this._drawRender(render);
      }
    }
  }

  _drawRender(render) {
    if (render instanceof StaticRender) {
      this._drawStaticRender(render);
    } else if (render instanceof DynamicRender) {
      this._drawDynamicRender(render);
    }

    this._setRenderListeners(render);
  }

  _setRenderListeners(render) {
    this.listensFor.forEach(eventFlag => {
      if (render.listensFor & eventFlag) this._eventCatchers[eventFlag].set(render, render);
    });
  }

  _removeRenderListeners(render) {
    this.listensFor.forEach(eventFlag => {
      if (render.listensFor & eventFlag) this._eventCatchers[eventFlag].delete(render);
    });
  }

  _drawStaticRender(render) {
    if (render instanceof StaticHtmlImageRender) {
      this._drawImageRender(render);
    } else if (render instanceof VectorRender) {
      this._drawVectorRender(render);
    } else if (render instanceof StaticVectorImageRender) {
      this._drawAfterLoad(render);
    }
  }

  _drawImageRender(render) {
    render.node.style.zIndex = this._zIndex.toString();

    this._currentContainer.addNode(render.node, render.width, render.height, render.bbox);

    if (render.onDisplayed) render.onDisplayed();
  }

  _drawAfterLoad(render) {
    let image = render.node;

    if (image instanceof HTMLImageElement) {
      if (image.complete) return this._drawVectorRender(render);
      listenDomEvent(image, 'load', () => {
        if (this._renders.indexOf(render) >= 0) {
          this._drawVectorRender(render);

          if (!this._canvas.node.parentNode) this._addCanvasToDom(this._master.bbox);
        }
      });
    } else {
      this._drawVectorRender(render);
    }
  }

  _drawVectorRender(render) {
    this._canvas.draw(render, this._master.map);
  }

  _drawDynamicRender(render) {
    render.update(this._master.bbox, this._master.map.resolution);

    this._master.dynamicContainer.appendChild(render.node);

    this._renders.push(render);

    if (render.onRender) render.onRender();
  }

  get currentContainer() {
    return this._currentContainer;
  }

  set currentContainer(container) {
    if (this._currentContainer !== container) {
      this._currentContainer = container;

      this._master.resolveLayerOverlay();

      if (this._useCanvasGroupRenderer) {
        this._canvasGroupRenderer.canvasContainer = container;
      }
    }
  }

  _removeRender(render) {
    if ((render instanceof StaticImageRender || render instanceof DynamicRender) && render.node.parentNode) {
      render.node.parentNode.removeChild(render.node);
      if (render instanceof StaticHtmlImageRender && render.onRemoved) render.onRemoved();
    }

    this._removeRenderListeners(render);
  }

  moveToLastContainer() {
    this._moveRendersToLastContainer();

    if (this._useCanvasGroupRenderer) {
      this._canvasGroupRenderer.moveToContainer(this._master.currContainer);
    } else if (this._canvas.node.parentNode) {
      this._canvasContainer.removeNode(this._canvas.node);

      this._master.currContainer.addNode(this._canvas.node, this._canvas.width, this._canvas.height, this._canvas.bbox);

      this._canvasContainer = this._master.currContainer;
    }

    this.currentContainer = this._master.currContainer;
  }

  _moveRendersToLastContainer() {
    let container = this._master.currContainer;

    for (let render of this._renders) {
      if (render instanceof StaticHtmlImageRender) {
        container.addNode(render.node, render.width, render.height, render.bbox);
      }
    }
  }

  getEventCatcher(eventFlag, position) {
    if (!this._eventCatchers[eventFlag]) return null;
    let keys = Array.from(this._eventCatchers[eventFlag].keys());

    for (let i = keys.length - 1; i >= 0; i--) {
      let render = keys[i];

      if (render.contains(position)) {
        return render;
      }
    }

    return null;
  }

}

let containerStyle = 'width: 100%; height: 100%; transform-origin: left top; position: absolute;';
/**
 * @alias sGis.painter.domPainter.Container
 */

class Container {
  constructor(wrapper, bbox, resolution, onEmpty) {
    this._onEmpty = onEmpty;
    this._bbox = bbox;
    this._resolution = resolution;

    this._setContainer(wrapper);
  }

  _setContainer(wrapper) {
    this._container = document.createElement('div');
    this._container.style.cssText = containerStyle;
    wrapper.appendChild(this._container);
  }

  remove() {
    this._container.innerHTML = '';
    if (this._container.parentNode) this._container.parentNode.removeChild(this._container);
  }

  get isEmpty() {
    return this._container.childElementCount === 0;
  }

  get scale() {
    return this._scale;
  }

  get crs() {
    return this._bbox.crs;
  }

  updateTransform(parentBbox, parentResolution) {
    if (parentBbox.crs !== this._bbox.crs) parentBbox = parentBbox.projectTo(this._bbox.crs);
    this._scale = this._resolution / parentResolution;
    setNodeTransform(this._container, this._scale, (this._bbox.xMin - parentBbox.xMin) / parentResolution, (parentBbox.yMax - this._bbox.yMax) / parentResolution);
  }

  addNode(node, width, height, bbox) {
    if (bbox.crs !== this._bbox.crs) {
      if (!bbox.crs.canProjectTo(this._bbox.crs)) return;
      bbox = bbox.projectTo(this._bbox.crs);
    }

    Container._setNodeStyle(node);

    setNodeTransform(node, bbox.width / width / this._resolution, (bbox.xMin - this._bbox.xMin) / this._resolution, (this._bbox.yMax - bbox.yMax) / this._resolution);

    this._container.appendChild(node);
  }

  addFixedSizeNode(node, position, offset = [0, 0]) {
    Container._setNodeStyle(node);

    setNodeTransform(node, 1, position[0] + offset[0] - this._bbox.xMin / this._resolution, position[1] + offset[1] + this._bbox.yMax / this._resolution);

    this._container.appendChild(node);
  }

  get resolution() {
    return this._resolution;
  }

  static _setNodeStyle(node) {
    node.style.position = 'absolute';
    node.style.transformOrigin = 'left top';
  }

  removeNode(node) {
    if (node.parentNode === this._container) {
      this._container.removeChild(node);

      if (this._container.childElementCount === 0) {
        this._onEmpty();
      }
    }
  }

}

function setNodeTransform(node, scale, tx, ty) {
  tx = browserAdj(normalize(tx));
  ty = browserAdj(normalize(ty));
  scale = normalize(scale);
  node.style.transform = 'translate3d(' + tx + 'px,' + ty + 'px,0px) scale(' + scale.toPrecision(6) + ',' + scale.toPrecision(6) + ')';
}

function normalize(n) {
  return Math.abs(n - Math.round(n)) < 0.001 ? Math.round(n) : n;
}

function browserAdj(n) {
  if (!isIE) {
    return Math.round(n);
  }

  return n;
}

let innerWrapperStyle = 'position: relative; overflow: hidden; width: 100%; height: 100%;';
let layerWrapperStyle = 'position: absolute; width: 100%; height: 100%; z-index: 0;';
class MapResize extends sGisEvent {
  constructor(width, height) {
    super(MapResize.type);
    this.width = width;
    this.height = height;
  }

}
MapResize.type = 'mapResize';
class DomPainter extends EventHandler {
  /**
   * @param map - the map to be drawn.
   * @param options - key-value list of properties to be assigned to the instance.
   */
  constructor(map, {
    wrapper = null
  } = {}) {
    super();
    this._fireMapResize = debounce((width, height) => this.fire(new MapResize(width, height)), 300);
    this._map = map;
    this.wrapper = wrapper;
    this._layerRenderers = new Map();
    this._containers = [];
    this._position = [Infinity, Infinity];
    this._resolution = Infinity;
    this._needUpdate = true;
    this._updateAllowed = true;

    this._updateLayerList();

    this._setEventListeners();

    this._repaintBound = this._repaint.bind(this);

    this._repaint();
  }
  /**
   * DOM element, inside of which the map will be drawn. If null is given, the map will not be drawn. If string is given, an element with given id will be searched.
   */


  get wrapper() {
    return this._wrapper;
  }

  set wrapper(wrapper) {
    if (this._wrapper) this._clearDOM();

    if (wrapper) {
      this._initDOM(wrapper);

      this._eventDispatcher = new EventDispatcher(this._innerWrapper, this);
      this._needUpdate = true;
      this._redrawNeeded = true;
    }

    this.fire('wrapperChange');
  }

  get layerRenderers() {
    return Array.from(this._layerRenderers.values());
  }
  /**
   * Sets position and resolution of the map to show the full bounding box in the center of the map
   * @param bbox
   * @param animate - if set to true, the position will be changed gradually with animation.
   * @returns the actual bbox of the map after the change.
   */


  show(bbox, animate = true) {
    let projected = bbox.projectTo(this.map.crs);
    let xResolution = projected.width / this.width;
    let yResolution = projected.height / this.height;
    let method = animate ? 'animateTo' : 'setPosition';
    let center = projected.center;
    this.map[method](center, this.map.getAdjustedResolution(Math.max(xResolution, yResolution)));
    return new Bbox([center[0] - this.width * xResolution, center[1] - this.height * yResolution], [center[0] + this.width * xResolution, center[1] + this.height * yResolution], this.map.crs);
  }

  _updateLayerList() {
    let mapLayers = this._map.getLayers(true, true);

    for (let layer of this._layerRenderers.keys()) {
      if (mapLayers.indexOf(layer) < 0) this._removeLayer(layer);
    }

    mapLayers.forEach((layer, index) => {
      let renderer = this._layerRenderers.get(layer);

      if (renderer) {
        renderer.setIndex(index);
      } else {
        this._addLayer(layer, index);
      }
    });
  }

  _addLayer(layer, index) {
    this._layerRenderers.set(layer, new LayerRenderer(this, layer, index));
  }

  _removeLayer(layer) {
    this._layerRenderers.get(layer).clear();

    this._layerRenderers.delete(layer);
  }

  _setEventListeners() {
    this._map.on(ContentsChangeEvent.type, this._updateLayerList.bind(this));

    this._map.on(DragEvent.type, this._onMapDrag.bind(this));

    this._map.on(sGisDoubleClickEvent.type, this._onMapDblClick.bind(this));

    this._map.on(AnimationStartEvent.type, this.forbidUpdate.bind(this));

    this._map.on(AnimationEndEvent.type, this.allowUpdate.bind(this));
  }
  /**
   * Prevents the map to be redrawn.
   */


  forbidUpdate() {
    this._updateAllowed = false;
  }
  /**
   * Allows redrawing of the map again after .forbidUpdate() has been called.
   */


  allowUpdate() {
    this._updateAllowed = true;
  }

  _repaint() {
    this._updateSize();

    if (this.isDisplayed) {
      if (this._needUpdate && this._updateAllowed) {
        this._setNewContainer();

        this._needUpdate = false;
      }

      this._updateBbox();

      let layers = this._map.getLayers(true, true);

      let redrawNeeded = this._redrawNeeded;

      if (this._updateAllowed) {
        for (let i = layers.length - 1; i >= 0; i--) {
          let renderer = this._layerRenderers.get(layers[i]);

          if (redrawNeeded || renderer.updateNeeded) {
            try {
              renderer.update();
            } catch (e) {
              warn(e);
              renderer.updateNeeded = false;
            }
          }
        }

        this._redrawNeeded = false;
      }

      if (redrawNeeded) {
        layers.forEach(layer => {
          let renderer = this._layerRenderers.get(layer);

          renderer.updateDynamic();
        });
      }
    }

    window.requestAnimationFrame(this._repaintBound);
  }

  _setNewContainer() {
    this._containers.push(new Container(this._staticRendersContainer, this.bbox, this._map.resolution, this._removeEmptyContainers.bind(this)));
  }

  _removeEmptyContainers() {
    // Check all containers except the last one, for we never remove it
    for (let i = this._containers.length - 2; i >= 0; i--) {
      if (this._containers[i].isEmpty) {
        this._removeContainer(i);
      }
    }
  }

  _removeContainer(i) {
    this._containers[i].remove();

    this._containers.splice(i, 1);
  }

  _updateSize() {
    const newWidth = this._wrapper ? this._wrapper.clientWidth || this._wrapper.offsetWidth : 0;
    const newHeight = this._wrapper ? this._wrapper.clientHeight || this._wrapper.offsetHeight : 0;

    if (this._width !== newWidth || this._height !== newHeight) {
      this._fireMapResize(newWidth, newHeight);
    }

    this._width = newWidth;
    this._height = newHeight;
  }
  /**
   * Returns true is the map is currently displayed in the DOM>
   */


  get isDisplayed() {
    return this._width > 0 && this._height > 0;
  }

  _updateBbox() {
    let mapPosition = this._map.position;

    if (this._position[0] !== mapPosition[0] || this._position[1] !== mapPosition[1] || !softEquals(this._map.resolution, this._resolution) || this._bboxWidth !== this._width || this._bboxHeight !== this._height) {
      this._position = [mapPosition[0], mapPosition[1]];
      this._resolution = this._map.resolution;
      let dx = this._width * this._resolution / 2;
      let dy = this._height * this._resolution / 2;
      this._bbox = new Bbox([mapPosition[0] - dx, mapPosition[1] - dy], [mapPosition[0] + dx, mapPosition[1] + dy], this._map.crs);

      this._containers.forEach(container => {
        if (container.crs.canProjectTo(this._map.crs)) {
          container.updateTransform(this._bbox, this._resolution);
        } else {
          this._removeContainer(this._containers.indexOf(container));

          if (this._containers.length === 0) this._setNewContainer();
        }
      });

      if (this._containers.length > 0 && this._containers[this._containers.length - 1].scale !== 1) this._needUpdate = true;
      this._bboxWidth = this._width;
      this._bboxHeight = this._height;
      this._redrawNeeded = true;
    }
  }
  /**
   * Current bbox of the map drawn by this painter.
   */


  get bbox() {
    if (!this._bbox) this._updateBbox();
    return this._bbox;
  }
  /**
   * The map this painter draws.
   */


  get map() {
    return this._map;
  }

  get currContainer() {
    return this._containers[this._containers.length - 1];
  }

  get dynamicContainer() {
    return this._dynamicRendersContainer;
  }
  /**
   * Width of the map on the screen in pixels.
   */


  get width() {
    return this._width;
  }
  /**
   * Height of the map on the screen in pixels.
   */


  get height() {
    return this._height;
  }

  _initDOM(node) {
    let wrapper = node instanceof HTMLElement ? node : document.getElementById(node);
    if (!wrapper) error('The element with ID "' + node + '" is not found.');
    this._innerWrapper = document.createElement('div');
    this._innerWrapper.style.cssText = innerWrapperStyle;
    this._staticRendersContainer = document.createElement('div');
    this._staticRendersContainer.style.cssText = layerWrapperStyle;
    this._dynamicRendersContainer = document.createElement('div');
    this._dynamicRendersContainer.style.cssText = layerWrapperStyle;

    this._innerWrapper.appendChild(this._staticRendersContainer);

    this._innerWrapper.appendChild(this._dynamicRendersContainer);

    wrapper.appendChild(this._innerWrapper);
    this._wrapper = wrapper;
  }

  _clearDOM() {
    if (this._innerWrapper.parentNode) this._innerWrapper.parentNode.removeChild(this._innerWrapper);
    this._innerWrapper = null;
    this._staticRendersContainer = null;
    this._dynamicRendersContainer = null;
    this._wrapper = null;

    this._eventDispatcher.remove();

    this._eventDispatcher = null;

    this._clearContainers();
  }

  _clearContainers() {
    this._containers.forEach((container, i) => {
      this._removeContainer(i);
    });

    this._map.getLayers(true, true).reverse().forEach(layer => {
      let renderer = this._layerRenderers.get(layer);

      renderer.clear();
    });
  }

  get innerWrapper() {
    return this._innerWrapper;
  }

  resolveLayerOverlay() {
    let prevContainerIndex = 0;

    this._map.getLayers(true, true).forEach(layer => {
      let renderer = this._layerRenderers.get(layer);

      if (!renderer) return;

      let containerIndex = this._containers.indexOf(renderer.currentContainer);

      if (containerIndex < prevContainerIndex) {
        renderer.moveToLastContainer();
        prevContainerIndex = this._containers.length - 1;
      } else {
        prevContainerIndex = containerIndex;
      }
    });

    this._removeEmptyContainers();
  }
  /**
   * Returns the point in map coordinates, that is located at the given offset from the left top corner of the map.
   * @param x
   * @param y
   */


  getPointFromPxPosition(x, y) {
    let resolution = this._map.resolution;
    let bbox = this.bbox;
    return new Point$1([bbox.xMin + x * resolution, bbox.yMax - y * resolution], bbox.crs);
  }
  /**
   * For the given point, returns the px offset on the screen from the left top corner of the map.
   * @param position - point in the map coordinates
   */


  getPxPosition(position) {
    return [(position[0] - this.bbox.xMin) / this._map.resolution, (this.bbox.yMax - position[1]) / this._map.resolution];
  }

  _onMapDrag(event) {
    setTimeout(() => {
      if (event.isCanceled) return;

      this._map.move(event.offset[0], event.offset[1]);
    }, 0);
  }

  _onMapDblClick(event) {
    setTimeout(() => {
      if (event.isCanceled) return;

      this._map.animateSetResolution(this._map.resolution / 2, event.point);
    }, 0);
  }

  get staticRendersContainer() {
    return this._staticRendersContainer;
  }

  setStaticBackgroundColor(backgroundColor) {
    if (this._staticRendersContainer && this._staticRendersContainer.style.backgroundColor !== backgroundColor) {
      this._staticRendersContainer.style.backgroundColor = backgroundColor;
    }
  }

  resetStaticBackgroundColor() {
    if (this._staticRendersContainer && this._staticRendersContainer.style.backgroundColor) {
      this._staticRendersContainer.style.backgroundColor = "";
    }
  }

}

var NS = 'http://www.w3.org/2000/svg';
/**
 * @alias sGis.painter.domPainter.SvgRender
 */

class SvgRender {
  constructor(render, adjK = 1) {
    this._baseRender = render;
    this._adjK = adjK;
  }

  getNode(callback) {
    if (!this._node) this._setNode();
    callback(null, this._node);
  }

  get baseRender() {
    return this._baseRender;
  }

  get position() {
    return this._position;
  }

  _setNode() {
    if (this._baseRender instanceof Arc) {
      if (this._baseRender.startAngle == 0 || this._baseRender.endAngle == 2 * Math.PI) {
        this._setArcNode();
      } else {
        this._setSegmentNode();
      }
    } else if (this._baseRender instanceof PolyRender) {
      this._setPolyNode();
    }
  }

  _setPolyNode() {
    var path = this._getSvgPath();

    if (this._baseRender.enclosed) {
      path.d += ' Z';
      path.d = path.d.replace(/ M/g, ' Z M');
    }

    this._node = this._getPathNode({
      stroke: this._baseRender.strokeColor,
      'stroke-dasharray': this._baseRender.lineDash && this._baseRender.lineDash.length > 0 ? this._baseRender.lineDash.join(',') : undefined,
      'stroke-width': this._baseRender.strokeWidth,
      fill: this._baseRender.fillStyle === FillStyle.Color ? this._baseRender.fillColor : 'none',
      fillImage: this._baseRender.fillStyle === FillStyle.Image ? this._baseRender.fillImage : undefined,
      width: path.width,
      height: path.height,
      x: path.x,
      y: path.y,
      viewBox: [path.x, path.y, path.width, path.height].join(' '),
      d: path.d
    });
    this._position = [path.x, path.y];
  }

  _getPathNode(properties) {
    if (properties.fillImage) {
      var defs = document.createElementNS(NS, 'defs');
      var pattern = document.createElementNS(NS, 'pattern');
      var id = getGuid();
      pattern.setAttribute('id', id);
      pattern.setAttribute('patternUnits', 'userSpaceOnUse');
      pattern.setAttribute('x', properties.x);
      pattern.setAttribute('y', properties.y);
      pattern.setAttribute('width', properties.fillImage.width);
      pattern.setAttribute('height', properties.fillImage.height);
      var image = document.createElementNS(NS, 'image');
      image.setAttributeNS("http://www.w3.org/1999/xlink", 'xlink:href', properties.fillImage.src);
      image.setAttribute('width', properties.fillImage.width);
      image.setAttribute('height', properties.fillImage.height);
      pattern.appendChild(image);
      defs.appendChild(pattern);
    }

    var path = document.createElementNS(NS, 'path');
    var svgAttributes = setAttributes(path, properties);

    var svg = this._getSvgBase(svgAttributes);

    if (properties.fillImage) {
      svg.setAttribute('xmlns', NS);
      svg.setAttribute('xmlns:xlink', "http://www.w3.org/1999/xlink");
      path.setAttribute('fill', 'url(#' + id + ')');
      svg.appendChild(defs);
    }

    svg.appendChild(path);
    return svg;
  }

  _setSegmentNode() {
    var path = this._getSegment();

    this._node = this._getPathNode({
      stroke: this._baseRender.strokeColor,
      'stroke-width': this._baseRender.strokeWidth,
      fill: this._baseRender.fillColor,
      width: path.width,
      height: path.height,
      x: path.x,
      y: path.y,
      viewBox: [path.x, path.y, path.width, path.height].join(' '),
      d: path.d
    });
    this._position = [path.x, path.y];
  }

  _getSegment() {
    var r = this._baseRender.radius;
    var r2 = r * 2 + this._baseRender.strokeWidth;
    var x = this._baseRender.center[0];
    var y = this._baseRender.center[1];
    var x1 = x + r * Math.cos(this._baseRender.startAngle);
    var y1 = y + r * Math.sin(this._baseRender.startAngle);
    var x2 = x + r * Math.cos(this._baseRender.endAngle);
    var y2 = y + r * Math.sin(this._baseRender.endAngle);
    var largeFlag = Math.abs(this._baseRender.endAngle - this._baseRender.startAngle) % (Math.PI * 2) > Math.PI ? 1 : 0;
    var path = `M ${x},${y} L ${x1},${y1} A ${r},${r} 0 ${largeFlag} 1 ${x2},${y2}`;
    var x0 = x - r - this._baseRender.strokeWidth / 2;
    var y0 = y - r - this._baseRender.strokeWidth / 2;
    return {
      x: x0,
      y: y0,
      width: r2,
      height: r2,
      d: path
    };
  }

  _setArcNode() {
    var r2 = this._baseRender.radius * 2 + this._baseRender.strokeWidth;
    var x = this._baseRender.center[0] * this._adjK - this._baseRender.radius - this._baseRender.strokeWidth / 2;
    var y = this._baseRender.center[1] * this._adjK - this._baseRender.radius - this._baseRender.strokeWidth / 2;
    this._node = this._getCircle({
      r: this._baseRender.radius,
      cx: this._baseRender.center[0] * this._adjK,
      cy: this._baseRender.center[1] * this._adjK,
      stroke: this._baseRender.strokeColor,
      'stroke-width': this._baseRender.strokeWidth,
      fill: this._baseRender.fillColor,
      width: r2,
      height: r2,
      viewBox: [x, y, r2, r2].join(' ')
    });
    this._position = [x, y];
  }

  _getCircle(properties) {
    var circle = document.createElementNS(NS, 'circle');
    var svgAttributes = setAttributes(circle, properties);

    var svg = this._getSvgBase(svgAttributes);

    svg.appendChild(circle);
    return svg;
  }

  _getSvgBase(properties) {
    var svg = document.createElementNS(NS, 'svg');
    setAttributes(svg, properties);
    svg.setAttribute('style', 'pointerEvents: none;');
    return svg;
  }

  _getSvgPath() {
    var d = '';
    var coordinates = this._baseRender.coordinates;
    var x = coordinates[0][0][0] * this._adjK;
    var y = coordinates[0][0][1] * this._adjK;
    var xMax = x;
    var yMax = y;

    for (var ring = 0; ring < coordinates.length; ring++) {
      d += 'M' + this._adj(coordinates[ring][0]).join(' ') + ' ';

      for (var i = 1; i < coordinates[ring].length; i++) {
        d += 'L' + this._adj(coordinates[ring][i]).join(' ') + ' ';
        x = Math.min(x, coordinates[ring][i][0] * this._adjK);
        y = Math.min(y, coordinates[ring][i][1] * this._adjK);
        xMax = Math.max(xMax, coordinates[ring][i][0] * this._adjK);
        yMax = Math.max(yMax, coordinates[ring][i][1] * this._adjK);
      }
    }

    var width = xMax - x + this._baseRender.strokeWidth;
    var height = yMax - y + this._baseRender.strokeWidth;
    x -= this._baseRender.strokeWidth / 2;
    y -= this._baseRender.strokeWidth / 2;
    d = d.trim();
    return {
      width: width,
      height: height,
      x: x,
      y: y,
      d: d
    };
  }

  _adj(position) {
    return [position[0] * this._adjK, position[1] * this._adjK];
  }

}
var svgAttributes = ['width', 'height', 'viewBox'];

function setAttributes(element, attributes) {
  var isSvg = element instanceof SVGSVGElement;
  var notSet = {};

  for (var i in attributes) {
    if (attributes.hasOwnProperty(i) && i !== 'fillImage' && attributes[i] !== undefined) {
      if (!isSvg && svgAttributes.indexOf(i) !== -1) {
        notSet[i] = attributes[i];
        continue;
      }

      if (i === 'stroke' || i === 'fill') {
        var color = new Color(attributes[i]);

        if (color.a < 255 || color.format === 'rgba') {
          element.setAttribute(i, color.toString('rgb'));
          if (color.a < 255) element.setAttribute(i + '-opacity', color.a / 255);
          continue;
        }
      }

      element.setAttribute(i, attributes[i]);
    }
  }

  return notSet;
}

/**
 * Point geometry rendered to the screen coordinates for drawing.
 * @alias sGis.render.Point
 */

class Point extends VectorRender {
  /**
   * @param coordinates - the rendered (px) coordinates of the point in [x, y] format.
   * @param __namedParameters - properties to be set to the corresponding fields.
   */
  constructor(coordinates, {
    color = 'black',
    ignoreEvents = false
  } = {}) {
    super();
    /** The color of the point. Can be any valid css color string. */

    this.color = 'black';
    /** Specifies whether this render can catch mouse events. If true, this render will be transparent for any pointer events. */

    this.ignoreEvents = false;
    this._coord = coordinates;
    this.color = color;
    this.ignoreEvents = ignoreEvents;
  }

  get isVector() {
    return true;
  }

  contains(position, tolerance = 2) {
    let dx = position[0] - this._coord[0];
    let dy = position[1] - this._coord[1];
    let distance2 = dx * dx + dy * dy;
    return distance2 < tolerance * tolerance;
  }
  /**
   *  The rendered (px) coordinates of the point in [x, y] format.
   */


  get coordinates() {
    return this._coord;
  }

  setupRender(ctx, map) {
    ctx.strokeStyle = this.color;
    ctx.fillStyle = this.color;
  }

  renderObject(ctx, map) {
    ctx.fillRect(this.coordinates[0], this.coordinates[1], 1, 1);
  }

}

const ALPHA_NORMALIZER = 65025;
/**
 * Symbol of polygon with brush filling.
 * @alias sGis.symbol.polygon.BrushFill
 */

class BrushFill extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    this._fillBackground = 'transparent';
    this._fillForeground = 'black';
    this._fillBrush = [[255, 255, 0, 0, 0, 0, 0, 0, 255, 255], [255, 255, 255, 0, 0, 0, 0, 0, 0, 255], [255, 255, 255, 255, 0, 0, 0, 0, 0, 0], [0, 255, 255, 255, 255, 0, 0, 0, 0, 0], [0, 0, 255, 255, 255, 255, 0, 0, 0, 0], [0, 0, 0, 255, 255, 255, 255, 0, 0, 0], [0, 0, 0, 0, 255, 255, 255, 255, 0, 0], [0, 0, 0, 0, 0, 255, 255, 255, 255, 0], [0, 0, 0, 0, 0, 0, 255, 255, 255, 255], [255, 0, 0, 0, 0, 0, 0, 255, 255, 255]];
    /** Stroke color of the outline. Can be any valid css color string. */

    this.strokeColor = 'black';
    /** Stroke width of the outline. */

    this.strokeWidth = 1;
    /** @see [[PolyRender.lineDash]] */

    this.lineDash = [];
    /** Emulation CanvasRenderingContext2D.filter drop-shadow. */

    this.shadow = null;
    /** @see [[PolyRender.lineCap]] */

    this.lineCap = "round";
    /** @see [[PolyRender.lineJoin]] */

    this.lineJoin = "round";
    /** @see [[PolyRender.miterLimit]] */

    this.miterLimit = 10;
    /** Drawn stroke outside. */

    this.isOutsideStroke = false;
    /** Drawn stroke inside. */

    this.isInsideStroke = false;
    this._initialized = false;
    if (options) Object.assign(this, options);
    this._initialized = true;

    this._updateBrush();
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof Poly)) return [];
    let coordinates = getRenderedPolygonCoordinates(feature, resolution, crs);
    return [new PolyRender(coordinates, {
      enclosed: true,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      fillStyle: FillStyle.Image,
      fillImage: this._brush,
      lineDash: this.lineDash,
      shadow: this.shadow,
      lineCap: this.lineCap,
      lineJoin: this.lineJoin,
      miterLimit: this.miterLimit,
      isOutsideStroke: this.isOutsideStroke,
      isInsideStroke: this.isInsideStroke
    })];
  }
  /**
   * Brush pattern for filling.
   */


  get fillBrush() {
    return this._fillBrush;
  }

  set fillBrush(brush) {
    this._fillBrush = brush;

    this._updateBrush();
  }
  /**
   * Brush background color. Can be any valid css color string.
   */


  get fillBackground() {
    return this._fillBackground;
  }

  set fillBackground(color) {
    this._fillBackground = color;

    this._updateBrush();
  }
  /**
   * Brush foreground color. Can be any valid css color string.
   */


  get fillForeground() {
    return this._fillForeground;
  }

  set fillForeground(color) {
    this._fillForeground = color;

    this._updateBrush();
  }

  _updateBrush() {
    if (!this._initialized) return;
    let canvas = document.createElement('canvas');
    let ctx = canvas.getContext('2d');
    let brush = this.fillBrush;
    let foreground = new Color(this.fillForeground);
    let background = new Color(this.fillBackground);
    canvas.height = brush.length;
    canvas.width = brush[0].length;

    for (let i = 0, l = brush.length; i < l; i++) {
      for (let j = 0, m = brush[i].length; j < m; j++) {
        let srcA = brush[i][j] * foreground.a / ALPHA_NORMALIZER,
            dstA = background.a / 255 * (1 - srcA),
            a = +Math.min(1, srcA + dstA).toFixed(2),
            r = Math.round(Math.min(255, background.r * dstA + foreground.r * srcA)),
            g = Math.round(Math.min(255, background.g * dstA + foreground.g * srcA)),
            b = Math.round(Math.min(255, background.b * dstA + foreground.b * srcA));
        ctx.fillStyle = 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
        ctx.fillRect(j, i, 1, 1);
      }
    }

    this._brush = new Image();
    this._brush.src = canvas.toDataURL();
  }

}
registerSymbol(BrushFill, 'polygon.BrushFill', ['fillBrush', 'fillBackground', 'fillForeground', 'strokeColor', 'strokeWidth', 'lineDash', 'shadow', 'lineCap', 'lineJoin', 'miterLimit', 'isOutsideStroke', 'isInsideStroke']);

/**
 * Symbol of polygon with image filling.
 * @alias sGis.symbol.polygon.ImageFill
 */

class ImageFill extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor({
    src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
    strokeWidth = 1,
    strokeColor = 'block',
    lineDash = [],
    shadow = null
  } = {}) {
    super();
    this._image = new Image();
    /** @see [[PolyRender.lineDash]] */

    this.lineDash = [];
    /** @see [[PolyRender.lineCap]] */

    this.lineCap = "round";
    /** @see [[PolyRender.lineJoin]] */

    this.lineJoin = "round";
    /** @see [[PolyRender.miterLimit]] */

    this.miterLimit = 10;
    /** Emulation CanvasRenderingContext2D.filter drop-shadow. */

    this.shadow = null;
    /** Drawn stroke outside. */

    this.isOutsideStroke = false;
    /** Drawn stroke inside. */

    this.isInsideStroke = false;
    this.strokeWidth = strokeWidth;
    this.strokeColor = strokeColor;
    this.lineDash = lineDash;
    this._src = src;
    this.shadow = shadow;

    this._updateImage();
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof Poly)) return [];

    if (!this._image.complete) {
      this._image.onload = feature.redraw.bind(feature);
      return [];
    }

    let coordinates = getRenderedPolygonCoordinates(feature, resolution, crs);
    return [new PolyRender(coordinates, {
      enclosed: true,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      fillStyle: FillStyle.Image,
      fillImage: this._image,
      lineDash: this.lineDash,
      shadow: this.shadow,
      lineCap: this.lineCap,
      lineJoin: this.lineJoin,
      miterLimit: this.miterLimit,
      isOutsideStroke: this.isOutsideStroke,
      isInsideStroke: this.isInsideStroke
    })];
  }
  /**
   * Source for the filling image. Can be url or data:url string.
   */


  get src() {
    return this._src;
  }

  set src(src) {
    this._src = src;

    this._updateImage();
  }

  _updateImage() {
    this._image = new Image();
    this._image.src = this._src;
  }

}
registerSymbol(ImageFill, 'polygon.ImageFill', ['src', 'strokeColor', 'strokeWidth', 'lineDash', 'shadow', 'lineCap', 'lineJoin', 'miterLimit', 'isOutsideStroke', 'isInsideStroke']);

class TileRender extends StaticHtmlImageRender {
  constructor() {
    super(...arguments);
    this.isComplete = true;
  }

}
/**
 * A layer that is drawn as a set of tile images received from server. The layer calculates tile indexes (x, y, z)
 * according to the tile scheme and requested resolution, then inserts them into url mask and creates ImageFeatures
 * for each tile. The features are cached by the layer to prevent unnecessary recalculations and garbage collection.
 * @alias sGis.TileLayer
 */


class TileLayer extends Layer {
  /**
   * @param urlMask - url of the source for tiles in format http(s)://...../..{x}..{y}..{z} - where x an y are indexes of tiles for the scale level z
   * @param __namedParameters - properties to be set to the corresponding fields.
   * @param extensions - [JS ONLY]additional properties to be copied to the created instance
   */
  constructor(urlMask, _a = {}, extensions) {
    var {
      tileScheme = TileScheme.default,
      crs = webMercator,
      cycleX = true,
      cycleY = false,
      cacheSize = 256,
      transitionTime = 500
    } = _a,
        LayerParams = __rest(_a, ["tileScheme", "crs", "cycleX", "cycleY", "cacheSize", "transitionTime"]);

    super(LayerParams, extensions);
    this._tileCache = {};
    this._cachedIndexes = [];
    this._previousTiles = [];
    this._urlMask = urlMask;
    this.tileScheme = tileScheme;
    this.crs = crs;
    this.cycleX = cycleX;
    this.cycleY = cycleY;
    this._cacheSize = cacheSize;
    this._transitionTime = transitionTime;
    this._tileFadeIn = this._tileFadeIn.bind(this);
  }
  /**
   * Returns url of a tile.
   * @param xIndex - index of tile along x axis.
   * @param yIndex - index of tile along y axis.
   * @param level - scale level of the tile.
   */


  getTileUrl(xIndex, yIndex, level) {
    if (typeof this._urlMask === "string") {
      return this._urlMask.replace('{x}', xIndex.toString()).replace('{y}', yIndex.toString()).replace('{z}', level.toString());
    } else {
      return this._urlMask(xIndex, yIndex, level);
    }
  }

  getRenders(bbox, resolution) {
    let indexes = this._getTileIndexes(bbox, resolution);

    let renders = [];

    this._cancelUnloadedTile(indexes);

    let isSetComplete = true;

    for (let index of indexes) {
      let tile = this._getRender(index);

      if (tile.isImageLoaded && !tile.error) renders.push(tile);
      if (!tile.error && (!tile.isComplete || !tile.isImageLoaded)) isSetComplete = false;
    }

    if (isSetComplete) {
      this._previousTiles = [];
    } else {
      this._previousTiles.forEach(tile => {
        if (renders.indexOf(tile) < 0) renders.push(tile);
      });
    }

    this._previousTiles = renders;
    return renders;
  }

  _getRender(index) {
    let tileId = TileLayer._getTileId(index.z, index.x, index.y);

    if (this._tileCache[tileId]) return this._tileCache[tileId];
    let adjX = this.cycleX ? this._getAdjustedIndex(index.x, index.level) : index.x;
    let adjY = this.cycleY ? this._getAdjustedIndex(index.y, index.level) : index.y;

    let bbox = this._getTileBbox(index);

    let tile = new TileRender({
      src: this.getTileUrl(adjX, adjY, index.z),
      width: this.tileWidth,
      height: this.tileHeight,
      bbox,
      onLoad: () => this.redraw()
    });

    this._cacheTile(tileId, tile);

    if (this.transitionTime <= 0) return tile;
    tile.node.style.opacity = '0';
    tile.node.style.transition = `opacity ${this.transitionTime / 1000}s`;
    tile.isComplete = false;

    tile.onDisplayed = () => {
      setTimeout(() => {
        tile.node.style.opacity = this.opacity.toString();
        setTimeout(() => {
          tile.isComplete = true;
          this.redraw();
        }, this.transitionTime);
      }, 0);
    };

    tile.onRemoved = () => {
      tile.node.style.opacity = '0';
      tile.isComplete = false;
    };

    return tile;
  }

  _getTileBbox(index) {
    let resolution = this.tileScheme.levels[index.level].resolution;
    let width = this.tileWidth * resolution;
    let x = this.tileScheme.origin[0] + index.x * width;
    let height = this.tileHeight * resolution;
    if (!this.tileScheme.reversedY) height *= -1;
    let yOffset = index.y * height;
    let y = this.tileScheme.origin[1] + yOffset;
    return new Bbox([x, y], [x + width, y + height], this.crs);
  }

  _cacheTile(id, tile) {
    if (this._tileCache[id]) return;
    this._tileCache[id] = tile;

    this._cachedIndexes.push(id);

    if (this._cachedIndexes.length > this._cacheSize) {
      delete this._tileCache[this._cachedIndexes.shift()];
    }
  }

  _cancelUnloadedTile(indexes) {
    Object.keys(this._tileCache).forEach(tileId => {
      const tile = this._tileCache[tileId];
      const indexDoesNotExistInCurrentBBox = !indexes.some(index => TileLayer._getTileId(index.z, index.x, index.y) === tileId);

      if (indexDoesNotExistInCurrentBBox && !tile.isImageLoaded) {
        tile.deleteNode();
        delete this._tileCache[tileId];
      }
    });
  }

  _getTileIndexes(bbox, resolution) {
    if (!this.crs.canProjectTo(bbox.crs)) return [];
    if (!this.checkVisibility(resolution)) return [];
    let level = this.tileScheme.getLevel(resolution);
    if (level < 0) return [];

    let trimmedBbox = this._getTrimmedBbox(bbox.projectTo(this.crs));

    if (trimmedBbox.width === 0 || trimmedBbox.height === 0) return [];
    let layerResolution = this.tileScheme.levels[level].resolution;
    if (layerResolution * 2 < resolution) return [];
    let xStartIndex = Math.floor((trimmedBbox.xMin - this.tileScheme.origin[0]) / this.tileWidth / layerResolution);
    let xEndIndex = Math.ceil((trimmedBbox.xMax - this.tileScheme.origin[0]) / this.tileWidth / layerResolution);
    let yStartIndex, yEndIndex;

    if (this.tileScheme.reversedY) {
      yStartIndex = Math.floor((trimmedBbox.yMin - this.tileScheme.origin[1]) / this.tileHeight / layerResolution);
      yEndIndex = Math.ceil((trimmedBbox.yMax - this.tileScheme.origin[1]) / this.tileHeight / layerResolution);
    } else {
      yStartIndex = Math.floor((this.tileScheme.origin[1] - trimmedBbox.yMax) / this.tileHeight / layerResolution);
      yEndIndex = Math.ceil((this.tileScheme.origin[1] - trimmedBbox.yMin) / this.tileHeight / layerResolution);
    }

    let indexes = [];

    for (let xIndex = xStartIndex; xIndex < xEndIndex; xIndex++) {
      for (let yIndex = yStartIndex; yIndex < yEndIndex; yIndex++) {
        indexes.push({
          x: xIndex,
          y: yIndex,
          z: this.tileScheme.levels[level].zIndex,
          level: level
        });
      }
    }

    return indexes;
  }

  static _getTileId(level, xIndex, yIndex) {
    return [level, xIndex, yIndex].join(',');
  }

  _getAdjustedIndex(index, level) {
    let desc = this.tileScheme.levels[level];
    if (!desc.indexCount || desc.indexCount <= 0 || index >= 0 && index < desc.indexCount) return index;

    while (index < 0) index += desc.indexCount;

    return index % desc.indexCount;
  }
  /**
   * Width of the tiles in px. Same as tile scheme's tileWidth.
   */


  get tileWidth() {
    return this.tileScheme.tileWidth;
  }
  /**
   * Height of the tiles in px. Same as tile scheme's tileHeight.
   */


  get tileHeight() {
    return this.tileScheme.tileHeight;
  }

  get opacity() {
    return this._opacity;
  }

  set opacity(opacity) {
    this._opacity = opacity;

    for (let tileId of Object.keys(this._tileCache)) {
      if (this._tileCache[tileId].isImageLoaded) this._tileCache[tileId].node.style.opacity = opacity.toString();
    }

    this.redraw();
  }

  clearCache() {
    this._tileCache = {};
    this._cachedIndexes = [];
    this._previousTiles = [];
  }
  /**
   * Opacity transition time in milliseconds with which tiles are added to the map.
   */


  get transitionTime() {
    return this._transitionTime;
  }

  set transitionTime(time) {
    this._transitionTime = time;
  }

  _getTrimmedBbox(bbox) {
    if (!this.tileScheme.limits) return bbox;
    let limits = this.tileScheme.limits;
    let xMin = Math.max(bbox.xMin, limits[0]);
    let yMin = Math.max(bbox.yMin, limits[1]);
    let xMax = Math.min(bbox.xMax, limits[2]);
    let yMax = Math.min(bbox.yMax, limits[3]);
    if (xMax < xMin) xMax = xMin;
    if (yMax < yMin) yMax = yMin;
    return new Bbox([xMin, yMin], [xMax, yMax], bbox.crs);
  }

  _tileFadeIn(image) {
    if (this._transitionTime <= 0) return;
    image.style.transition = 'opacity ' + this.transitionTime / 1000 + 's linear';
    image.style.opacity = this.opacity.toString();
  }

}

/**
 * @example symbols/Label_Symbols
 */

class StaticLabelSymbol extends Symbol {
  constructor({
    fontSize,
    fontFamily,
    fontStyle,
    horizontalAlignment,
    verticalAlignment,
    offset = [5, 0],
    strokeColor,
    strokeWidth,
    fillColor
  } = {}) {
    super();
    this.fontSize = fontSize;
    this.fontFamily = fontFamily;
    this.fontStyle = fontStyle;
    this.horizontalAlignment = horizontalAlignment;
    this.verticalAlignment = verticalAlignment;
    this.offset = offset;
    this.strokeColor = strokeColor;
    this.strokeWidth = strokeWidth;
    this.fillColor = fillColor;
  }

  renderFunction(feature, resolution, crs) {
    if (!feature.crs.canProjectTo(crs)) return [];
    let position = feature.projectTo(crs).position;
    let pxPosition = [position[0] / resolution + (this.offset[0] || 0), -position[1] / resolution + (this.offset[1] || 0)];
    return [new VectorLabel({
      position: pxPosition,
      text: feature.content,
      fontSize: this.fontSize,
      fontFamily: this.fontFamily,
      fontStyle: this.fontStyle,
      verticalAlignment: this.verticalAlignment,
      horizontalAlignment: this.horizontalAlignment,
      fillColor: this.fillColor,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth
    })];
  }

}

const DEFAULT_LABEL_SYMBOL = /*#__PURE__*/new StaticLabelSymbol();
/**
 * Text label on the map.
 * @example symbols/Label_Symbols
 */

class LabelFeature extends PointFeature {
  constructor(position, _a) {
    var {
      content = '',
      symbol = DEFAULT_LABEL_SYMBOL
    } = _a,
        params = __rest(_a, ["content", "symbol"]);

    super(position, Object.assign({
      symbol
    }, params));
    this._content = content;
  }
  /**
   * The text of the label.
   */


  get content() {
    return this._content;
  }

  set content(value) {
    this._content = value;
    this.redraw();
  }

  clone() {
    return new LabelFeature(this.position, {
      crs: this.crs,
      symbol: this.symbol,
      content: this.content,
      persistOnMap: this.persistOnMap
    });
  }

  projectTo(crs) {
    let projected = Point$1.prototype.projectTo.call(this, crs).position;
    return new LabelFeature(projected, {
      crs,
      symbol: this.symbol,
      content: this.content,
      persistOnMap: this.persistOnMap
    });
  }

}

setStyleNode(`
    .sGis-balloon {
        border: 3px solid rgba(0, 0, 0, 0.3);
        border-radius: 7px;
        transform: translate(-50%, -100%);
    }
    
    .sGis-balloon:before {
        content: " ";
        position: absolute;
        background: white;
        border: 3px solid rgba(0, 0, 0, 0.3);
        width: 10px;
        height: 10px;
        margin-left: 0;
        transform: translate(-50%, -50%) rotate(45deg);
        left: 50%;
        top: 100%;
    }
    
    .sGis-balloon > div {
        border-radius: 5px;
        background: white;
        position: relative;
        min-height: 40px;
        min-width: 40px;
        overflow: hidden;
        padding: 5px;
    }
`);
class BalloonSymbol extends DynamicPointSymbol {
  constructor({
    onRender,
    offset = [0, -7]
  } = {}) {
    super({
      offset,
      onRender
    });
  }

  _getFeatureNode(feature) {
    let balloonFeature = feature;
    let node = document.createElement('div');
    node.className = 'sGis-balloon';
    let container = document.createElement('div');
    if (balloonFeature.content) container.appendChild(balloonFeature.content);
    node.appendChild(container);
    let mapNode = node;
    mapNode.doNotBubbleToMap = true;
    return node;
  }

}

/**
 * This feature lets you put a custom html blocks on the map in the form of balloons over the map. The position of the balloon
 * is set as geographic coordinates of a point.
 * @alias sGis.feature.Balloon
 * @example controls/Balloon_Control
 */

class Balloon extends PointFeature {
  constructor(position, _a) {
    var {
      content,
      symbol = new BalloonSymbol(),
      persistOnMap = true
    } = _a,
        params = __rest(_a, ["content", "symbol", "persistOnMap"]);

    super(position, Object.assign({
      symbol,
      persistOnMap
    }, params));

    if (content instanceof HTMLElement) {
      this._content = content;
    } else {
      this._content = this._getNode(content);
    }
  }

  _getNode(htmlString) {
    let div = document.createElement('div');
    div.innerHTML = htmlString;

    if (div.children.length === 1) {
      return div.firstChild;
    } else {
      return div;
    }
  }

  get content() {
    return this._content;
  }

}

const DEFAULT_CLASS_NAME = 'sGis-dynamicLabel';
const DEFAULT_STYLE = `
    transform: translate(0, -50%);
    font: 14px arial;
    color: black;
    text-shadow: -1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white; 
`;
setCssClasses({
  [DEFAULT_CLASS_NAME]: DEFAULT_STYLE
});
/**
 * @example symbols/Label_Symbols
 */

class DynamicLabelSymbol extends DynamicPointSymbol {
  constructor({
    cssClassName = DEFAULT_CLASS_NAME,
    offset
  } = {}) {
    super({
      offset
    });
    this.cssClassName = cssClassName;
  }

  _getFeatureNode(feature) {
    let labelFeature = feature;
    let node = document.createElement('span');
    node.innerText = labelFeature.content;
    if (this.cssClassName) node.className = this.cssClassName;
    return node;
  }

  _updateFeatureNode(feature) {
    let node = this.getNode(feature);
    node.innerText = feature.content;
  }

}

const OFFSET = 10;
/**
 * @example controls/Balloon_Control
 */

class BalloonControl extends Control {
  constructor(map, {
    painter
  } = {}) {
    super(map, {
      useTempLayer: true
    });
    this.painter = painter;
    this._ns = '.' + getGuid;
    this._onMapClick = this._onMapClick.bind(this);
    this._symbol = new BalloonSymbol({
      onRender: this._onRender.bind(this)
    });
  }

  attach(feature, html) {
    let balloon = new Balloon([0, 0], {
      crs: feature.crs,
      content: html,
      symbol: this._symbol
    });
    feature.on(sGisClickEvent.type + this._ns, this._showBalloon.bind(this, feature, balloon));
  }

  detach(feature) {
    feature.off(this._ns);
  }

  _showBalloon(feature, balloon, event) {
    event.stopPropagation();

    if (feature instanceof Poly) {
      balloon.position = feature.centroid;
    } else if (feature instanceof PointFeature) {
      balloon.position = feature.position;
    }

    if (this._activeBalloon && this._tempLayer) {
      this._tempLayer.remove(this._activeBalloon);
    }

    if (!this._isActive) this.activate();
    if (this._tempLayer) this._tempLayer.add(balloon);
    this._activeBalloon = balloon;
  }

  _onRender() {
    if (!this.painter || !this._activeBalloon) return;

    let node = this._symbol.getNode(this._activeBalloon);

    if (!node) return;
    let size = node.getBoundingClientRect();

    let position = this._activeBalloon.projectTo(this.map.crs).position;

    let resolution = this.map.resolution;
    let halfWidth = size.width * resolution / 2;
    let height = size.height * resolution;
    let balloonBbox = new Bbox([position[0] - halfWidth, position[1]], [position[0] + halfWidth, position[1] + height], this._activeBalloon.crs).offset([OFFSET * resolution, OFFSET * resolution]);
    let mapBbox = this.painter.bbox;
    let newBbox = mapBbox.clone();

    if (newBbox.yMin > balloonBbox.yMin) {
      newBbox.yMin = balloonBbox.yMin;
      newBbox.yMax = newBbox.yMin + mapBbox.height;
    } else if (newBbox.yMax < balloonBbox.yMax) {
      newBbox.yMax = balloonBbox.yMax;
      newBbox.yMin = newBbox.yMax - mapBbox.height;
    }

    if (newBbox.xMin > balloonBbox.xMin && newBbox.xMax < balloonBbox.xMax) {
      newBbox.xMin = balloonBbox.center[0] - mapBbox.width / 2;
      newBbox.xMax = balloonBbox.center[1] + mapBbox.width / 2;
    } else if (newBbox.xMin > balloonBbox.xMin) {
      newBbox.xMin = balloonBbox.xMin;
      newBbox.xMax = newBbox.xMin + mapBbox.width;
    } else if (newBbox.xMax < balloonBbox.xMax) {
      newBbox.xMax = balloonBbox.xMax;
      newBbox.xMin = newBbox.xMax - mapBbox.width;
    }

    this.painter.show(newBbox);
  }

  _activate() {
    this.map.on('click', this._onMapClick);
  }

  _deactivate() {
    this.map.off('click', this._onMapClick);
    this._activeBalloon = undefined;
  }

  _onMapClick() {
    this.deactivate();
  }

}

/**
 * A line or a set of geographical lines.
 * @alias sGis.feature.CombinedPolyline
 */

class CombinedPolyline extends Polyline {
  constructor(rings, _a = {}) {
    var {
      symbol = new CombinedPolylineSymbol()
    } = _a,
        params = __rest(_a, ["symbol"]);

    super(rings, Object.assign({
      symbol
    }, params));
    this.isEnclosed = false;
    this.values = [];
  }
  /**
   * Returns a copy of the feature. Only generic properties are copied.
   */


  clone() {
    return new CombinedPolyline(this.rings, {
      crs: this.crs,
      symbol: this.originalSymbol
    });
  }

  projectTo(crs) {
    let projected = projectRings(this.rings, this.crs, crs);
    return new CombinedPolyline(projected, {
      crs: this.crs,
      symbol: this.symbol,
      persistOnMap: this.persistOnMap
    });
  }

}

/**
 * Symbol of polyline drawn as simple line.
 * @alias sGis.symbol.polyline.Combined
 */

class CombinedPolylineSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Values of the symbol. */

    this.values = [];
    Object.assign(this, options);
  }

  renderFunction(feature, resolution, crs) {
    const result = [];
    if (!(feature instanceof CombinedPolyline)) return result;
    const coordinates = this.getRenderedCoordinates(feature, resolution, crs);
    this.values.forEach(polyline => {
      result.push(new PolyRender(coordinates, {
        fillStyle: FillStyle.Color,
        enclosed: false,
        strokeColor: getParameterValue(polyline.stroke.color),
        strokeWidth: getParameterValue(polyline.stroke.width),
        lineDash: getParameterValue(polyline.stroke.intervals),
        shadow: null,
        lineCap: polyline.lineCap,
        lineJoin: polyline.lineJoin,
        miterLimit: polyline.miterLimit
      }));
    });
    return result;
  }
  /**
   * Projects coordinates of a poly feature to the requested crs and resolution.
   * @param feature
   * @param resolution
   * @param crs
   * @param tolerance
   */


  getRenderedCoordinates(feature, resolution, crs, tolerance) {
    let result;

    if (this.previousPoly === feature && this.previousCrs.equals(crs) && this.previousResolution === resolution && feature.lastAction && feature.lastAction.type !== Action.removePoint) {
      const lastAction = feature.lastAction;
      let newResult = this.previousSimplified;

      if (lastAction.type === Action.movePoint) {
        const newSimplifiedRing = simplifyCoordinates([feature.rings[lastAction.ringIndex]].map(ring => {
          return ring.map(point => {
            return [Math.round(point[0] / resolution), Math.round(point[1] / -resolution)];
          });
        }), tolerance);
        newResult[lastAction.ringIndex] = newSimplifiedRing[0];
      }

      if (lastAction.type === Action.addPoint) {
        const renderCoordinates = [Math.round(lastAction.coordinates[0] / resolution), Math.round(lastAction.coordinates[1] / -resolution)];
        newResult[lastAction.ringIndex].push(renderCoordinates);
      }

      result = newResult;
    } else {
      result = getRenderedPolygonCoordinates(feature, resolution, crs, tolerance);
    }

    this.previousSimplified = result;
    this.previousPoly = feature;
    this.previousCrs = crs;
    this.previousResolution = resolution;
    feature.lastAction = null;
    return result;
  }

}
registerSymbol(CombinedPolylineSymbol, 'polyline.Combined', ['values']);

/**
 * Convenience method for simple map initialization
 * @param position
 * @param centerPoint
 * @param resolution
 * @param crs
 * @param layers
 * @param wrapper
 * @param pluginDefinitions
 * @example init_Setting_position_and_resolution
 */

const init = function ({
  position = undefined,
  centerPoint = undefined,
  resolution = undefined,
  crs = undefined,
  layers = undefined,
  wrapper = undefined,
  pluginDefinitions = []
}) {
  let map = new Map$1({
    crs,
    position,
    centerPoint,
    resolution,
    layers
  });
  let painter = new DomPainter(map, {
    wrapper
  });
  let p = pluginDefinitions.map(pluginDefinition => {
    let name = pluginDefinition.name;

    if (!plugins[name]) {
      throw new Error(`Plugin ${name} is not available.`);
    }

    return new plugins[name](map, painter, pluginDefinition.properties);
  });
  return {
    map,
    painter,
    p
  };
};
const plugins = {
  registerPlugin(name, Constructor) {
    if (plugins[name]) {
      warn(`Plugin with name ${name} already registered. Skipping.`);
    } else {
      plugins[name] = Constructor;
    }
  }

};

const math = mathModule;
const geotools = geotoolsModule;
const event = eventModule;
const version = "0.5.0-alpha.57";
const releaseDate = "17.10.2023";
let utilsModulesExt = {};
Object.assign(utilsModulesExt, utilsModule, {
  Color: Color
});
const controls = {
  Circle: Circle,
  Editor: Editor,
  MultiPoint: MultiPoint,
  Point: PointControl,
  PointEditor: PointEditor,
  Poly: PolyControl,
  PolyDrag: PolyDrag,
  PolyEditor: PolyEditor,
  Polyline: PolylineControl,
  Polygon: PolygonControl,
  PolyTransform: PolyTransform,
  Rectangle: Rectangle,
  snapping: {
    CombinedSnappingProvider: CombinedSnappingProvider
  },
  BalloonControl: BalloonControl
};
const Crs = Crs$1;
const CRS = CrsModule;
const feature = {
  MultiPoint: MultiPoint,
  Point: PointFeature,
  Poly: Poly,
  Polygon: Polygon,
  Polyline: Polyline,
  CombinedPolyline: CombinedPolyline,
  Label: LabelFeature,
  Balloon: Balloon
};
const painter = {
  DomPainter: DomPainter,
  domPainter: {
    Container: Container,
    EventDispatcher: EventDispatcher,
    SvgRender: SvgRender,
    LayerRenderer: LayerRenderer,
    Canvas: Canvas
  }
};
const render = {
  Arc: Arc,
  HtmlElement: HTMLElement,
  Point: Point,
  Polygon: PolyRender
};
const symbol = {
  point: {
    Image: StaticImageSymbol,
    MaskedImage: MaskedImage,
    Point: PointSymbol,
    Square: SquareSymbol
  },
  polygon: {
    BrushFill: BrushFill,
    ImageFill: ImageFill,
    Simple: PolygonSymbol
  },
  polyline: {
    Simple: PolylineSymbol,
    Combined: CombinedPolylineSymbol
  },
  Editor: EditorSymbol,
  label: {
    DynamicLabelSymbol: DynamicLabelSymbol,
    StaticLabelSymbol: StaticLabelSymbol
  },
  Balloon: BalloonSymbol
};
const utils = utilsModulesExt;
const serializer = {
  symbolSerializer: symbolSerializer
};

export { Bbox, CRS, Control, Crs, DynamicLayer, EventHandler, Feature, FeatureLayer, Layer, LayerGroup, Map$1 as Map, Point$1 as Point, Symbol, TileLayer, TileScheme, controls, event, feature, geotools, init, math, painter, releaseDate, render, serializer, symbol, utils, version };
//# sourceMappingURL=sGis.esm.js.map
